import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
// A sheet under 600px, this same Dialog above it. Used for 補資料; the DISC
// playbook further down stays a plain Dialog, being four lines nobody edits.
import BottomSheetOrDialog from '../../../../components/BottomSheetOrDialog/BottomSheetOrDialog';
import { useWindowSize } from '../../../../utils/hooks';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Switch from '@mui/material/Switch';
import {
  Baby,
  BarChart3,
  Building2,
  FolderOpen,
  GraduationCap,
  BookOpenCheck,
  Cake,
  Calculator,
  ChartPie,
  Check,
  CirclePlus,
  Compass,
  LifeBuoy,
  Smartphone,
  Wallet,
  Wrench,
  Radar as RadarIcon,
  IdCard,
  WandSparkles,
  NotebookPen,
  Cctv,
  MessageCircle,
  AlarmClock,
  UserPlus,
  Puzzle,
  Bot,
  BellRing,
  Briefcase,
  UserRound,
  ClipboardList,
  Minus,
  MoreHorizontal,
  CalendarClock,
  Heart,
  Lightbulb,
  Pencil,
  Share2,
  Star,
  House,
  Tags,
  Settings,
  Plus,
  ChevronRight,
  ChevronDown,
  CircleCheck,
  Info,
  Lock,
  User,
  Users,
  ScrollText,
  QrCode,
  Milestone,
  X
} from 'lucide-react';
import CoverageRadar from './CoverageRadar';
import RecruitRadar from './RecruitRadar';
import RecruitHeaderMarkers from './RecruitHeaderMarkers';
import RecruitIdeaDialog from './RecruitIdeaDialog';
import RecruitQuizDialog from './RecruitQuizDialog';
import CoverQuizDialog from '../../../CoverQuiz/CoverQuizDialog';
import QuizChooserDialog from './QuizChooserDialog';
import { useRecruitIdeaPath, writeRecruitIdeaPath } from './RecruitIdeaPath';
import RecruitStageDot from './RecruitStageDot';
import { RECRUIT_STAGES, useRecruitStage, writeRecruitStage } from '../recruitStage';
import type { RecruitStage } from '../recruitStage';
import { useCanEditClientNotes, useSeedClientNotes } from '../clientNotesStore';
import { servicePrefsSummary, useServicePrefs } from '../../../CoverQuiz/coverServicePrefs';
import { quizLangOf } from '../../../CoverQuiz/quizLang';
import type { RecruitIdeaPath } from './RecruitIdeaPath';
import DiscTips from '../../../../components/ClientEntry/SmartIdeaDialog/DiscTips';
import FactToggle from './FactToggle';
import type { FactToggleOption } from './FactToggle';
import { DatePicker as ClickableDatePicker } from '../../../../components/ClickableField/ClickableField';
import { adviserFactsheetOf } from '../../../../utils/client-factsheet';
import { nextBirthday } from '../../../../utils/client-queue';
import { getCoverageSegments } from '../../../../utils/client-insights';
import { QUEUE_SIGNALS, SIGNAL_COLOR, SIGNAL_MESSAGE_ID, SIGNAL_TINT } from '../../../../constants/client-queue';
import type { QueueSignal } from '../../../../constants/client-queue';
import { discFull, discLabel, discTag, isDiscLetter } from '../../../../constants/disc';
import {
  avatarGenderClass,
  cpiOf,
  elapsedOf,
  familiesOf,
  tagsOf,
  tierBadgeOf,
  tierValueOf,
  CLIENT_TIER_CHOICES,
  existingClientOf,
  formatBusinessVolume,
  connectionStatusOf,
  discColorOf,
  personalityMetaOf,
  trackingDetailOf,
  reminderOf,
  reminderMarkOf,
  lastMeetingDurationOf,
  clientNameOf,
  OPPORTUNITY_LEVELS
} from '../clientPresentation';
import { clientCapabilitiesOf, clientRestrictionReasonOf, isClientRestricted } from '../clientEntitlements';
import { readsAsRecruitView, rememberRecruitView } from '../clientViewState';
import { cpiGapOf } from '../clientCpiGap';
import { crossSellGapOf, holdsAnyTrackedCategory, policyFiguresVisible } from '../clientFilters';
import type { CpiField } from '../clientCpiGap';
import type { ClientTierChoice, ExistingClientChoice, IntlLike } from '../clientPresentation';
import type { ClientCapability } from '../clientEntitlements';
import type { ClientDrawerAction } from '../clientActions';
import type { AnalysisScores } from '../../../../utils/client-insights';
import type { ClientTag, QueueClient } from '../../../../types/client';

/**
 * `BottomSheetOrDialog` is JavaScript, and react-intl 2's `injectIntl` typings
 * drop the wrapped component's own props — TS sees a component that takes
 * nothing but children, so every prop below is an error. This names the ones
 * this file actually passes rather than casting the whole component to `any`,
 * which would also silence a genuine typo.
 *
 * Loose object types for the four pass-through bags: they are forwarded whole to
 * MUI and react-spring-bottom-sheet, which have their own (and very different)
 * prop sets, and re-declaring either here would be a second source of truth that
 * cannot be kept honest.
 */
type FactsSurfaceProps = {
  open: boolean;
  onClose: () => void;
  className?: string;
  header?: React.ReactNode;
  headerCTAs?: React.ReactNode[];
  actions?: React.ReactNode[];
  content?: React.ReactNode;
  BottomSheetProps?: Record<string, unknown>;
  BottomSheetParams?: Record<string, unknown>;
  DialogProps?: Record<string, unknown>;
  DialogTitleProps?: Record<string, unknown>;
  DialogContentProps?: Record<string, unknown>;
};
const FactsSurface = BottomSheetOrDialog as unknown as React.ComponentType<FactsSurfaceProps>;

// 客製化展業工作台. Icons are lucide-react (barrel import; the deep `dist/esm`
// paths are `.mjs` and break jest's CJS transform, so ClientV2 leaves use the
// barrel). Labels reuse existing message ids wherever they exist (Established
// Pattern #3).
//
// The layout is three promoted cards over two labelled groups of tiles, with
// the PRO three behind a ⋯ overflow — see
// docs/specs/2026-08-13-clientv2-drawer-regroup-design.md. What it replaced was
// a flat 8-tile grid plus a 5-pill action row: two visual languages for the
// same job ("open something"), no headings, and 18 controls on screen before
// the adviser scrolled.

type PromotedCard = {
  icon: typeof ChartPie;
  labelKey: string;
  /** 'recruit-idea' is opened by the drawer itself rather than through onAction. */
  action: ClientDrawerAction | 'recruit-idea';
  /** V1's permanent attention badge — an affordance hint, not a state signal. */
  alwaysDot?: boolean;
};

/**
 * The three features the business promotes. They are the only controls drawn as
 * cards rather than tiles: a larger target and a title at readable size. Each
 * carried a second line explaining itself as well (一頁保單總覽 / 摘要·問答 /
 * 記錄·提醒); the three names already say that much, and three glosses side by
 * side read as a manual to get through before anything can be tapped.
 *
 * 救命紙 takes over from the old 保單PDF tile outright. Both open
 * `/export_options`, so keeping the tile as well would have left two
 * differently-named buttons pointing at one page; the two other cards on that
 * page (保單Review, 加到電子錢包) are not lost, they are one tap further in, and
 * 救命紙 now leads that page rather than sitting second on it.
 */
const PROMOTED: PromotedCard[] = [
  // V1 gives the export entry a permanent red pulsing badge
  // (ClientExpansionPortfolio.js:111-121), and V1 badges 會面筆記
  // unconditionally too (ClientEntry.js:1179). Both are kept as they were.
  { icon: LifeBuoy, labelKey: 'Emergency Reference', action: 'export', alwaysDot: true },
  { icon: Bot, labelKey: 'clientv2-ai', action: 'ai' },
  { icon: NotebookPen, labelKey: 'meetingNotes', action: 'meeting-note', alwaysDot: true }
];

type GroupItem = {
  icon: typeof ChartPie;
  labelKey: string;
  action: ClientDrawerAction;
  pro?: boolean;
  /** 進度跟進 alone reports a state (跟進中) rather than only opening something. */
  kind?: 'tracking';
};

/**
 * Everything else, in two named groups. Every member is the same tile shape —
 * the pill/tile split is gone, because both halves only ever meant "open this".
 */
const GROUPS: { titleKey: string; icon: typeof ChartPie; items: GroupItem[] }[] = [
  {
    titleKey: 'clientv2-group-policy',
    icon: Briefcase,
    items: [
      { icon: ChartPie, labelKey: 'Policy_Portfolio', action: 'portfolio' },
      { icon: CirclePlus, labelKey: 'clientpage-add', action: 'add-policy' },
      // 智能摘要 sits third in 保單與分析, where 需求分析 was: it reads the policies
      // and analysis beside it. 需求分析 swapped down to 客戶與跟進.
      { icon: Lightbulb, labelKey: 'clientv2-smart-idea', action: 'smart-idea' },
      // 保障小測驗 — put in front of the client, like 招募's 職涯測驗 tile. Asks
      // which part to send first (QuizChooserDialog).
      { icon: ScrollText, labelKey: 'clientv2-cover-quiz', action: 'cover-quiz' }
    ]
  },
  {
    titleKey: 'clientv2-group-client',
    icon: UserRound,
    items: [
      // Same reason as the analysis tile below: `Factsheet` is the shared id and
      // still reads 「Detail Info」 on the six V1 screens that print it.
      { icon: IdCard, labelKey: 'clientv2-tile-details', action: 'factsheet' },
      // 需求分析 — swapped here with 智能摘要 (2026-10-08).
      // `clientv2-tile-analysis`, not the app-wide `need-analysis`. The tiles on
      // this grid are read as a set, at a glance, in two lines of 11px text — so
      // they are named for what they open rather than what the feature is called
      // everywhere else. The shared id still says 「Need Analysis」 for the six
      // V1 screens that use it (AdviserHomePage, MeetingTools, Analysis, …);
      // renaming it there would rename a feature nobody asked to rename.
      { icon: RadarIcon, labelKey: 'clientv2-tile-analysis', action: 'analysis' },
      { icon: Cctv, labelKey: 'BusinessTracking', action: 'tracking', kind: 'tracking' }
    ]
  }
];

/**
 * Behind the ⋯ tile that closes the 客戶與跟進 row.
 *
 * Started as the three PRO controls; 聯絡 joined them because it already has a
 * shortcut on every client row, so in the drawer it was a second door rather
 * than the only one. (智能摘要 briefly followed it in and came back out: unlike
 * 聯絡 it is a selling tool, not a communication one, so it stays a visible
 * tile — now in 客戶與跟進, beside the client record it summarises.)
 */
/** localStorage flag: 1 when the adviser left 客戶與跟進 unfolded. */
export const QUIET_GROUP_OPEN_KEY = 'clientv2.quietGroupOpen';

const MORE_ITEMS: GroupItem[] = [
  { icon: MessageCircle, labelKey: 'Contact', action: 'contact' },
  { icon: AlarmClock, labelKey: 'premium-reminder-short', action: 'reminder', pro: true },
  { icon: UserPlus, labelKey: 'Ask Referral', action: 'referral', pro: true },
  {
    icon: Puzzle,
    labelKey: 'gpt-product-recommendation-client-entry-title',
    action: 'product-recommendation',
    pro: true
  }
];

/**
 * 招募 view — the same panel, for the other conversation this adviser is having
 * with the same person.
 *
 * Not a different KIND of client. A client marked 潛在招募 is still a client:
 * every one of them arrives as a sales prospect and stays one, and the flag is
 * something the adviser adds ON TOP. So this view subtracts nothing from the
 * record — it re-orders one panel while the adviser is doing recruitment work,
 * and 業務 is one tap away at the top of the body.
 *
 * Everything below already exists somewhere in the app. What is new is reaching
 * it from the person, rather than from the team-level 招募 page.
 *
 * Design, the slices still to come, and how to extend any of this:
 * docs/specs/2026-08-31-clientv2-recruitment-mode-design.md (§8 is the recipes).
 */

/**
 * The promoted three, filling the SAME three roles as the business row above
 * rather than three recruitment features picked at random: something to put in
 * front of the person, the smart tool, and the place to write down what
 * happened.
 *
 * 招募筆記 is deliberately NOT one of them, even though it is the most
 * recruitment-specific thing in the app. The AI recruitment note lives INSIDE
 * the meeting-note dialog (RemarkControl.js:210-228 renders it beside the
 * ordinary voice button), so a tile next to 會面筆記 would be a second door
 * onto one room.
 */
const RECRUIT_PROMOTED: PromotedCard[] = [
  // 招募 IDEA leads — the page read before the conversation, about THIS person.
  // It swapped places with 活動量計算, which now opens the 招募工具 row. Unlike
  // the other cards it is a dialog the drawer opens itself, not an `onAction`.
  { icon: Compass, labelKey: 'clientv2-recruit-idea', action: 'recruit-idea' },
  // The same tile as the business row's and the same page, but it now says
  // which conversation it was opened for: 'ai-recruit' lands on /gpt-options
  // filtered to the recruitment tools, with the 招募 IG-post preset already
  // chosen. Before this, the recruitment half of 智能助理 was reachable only
  // by knowing to change a radio inside one drawer.
  { icon: Bot, labelKey: 'clientv2-ai', action: 'ai-recruit' },
  // Same meeting-note dialog and recorder; only this recruitment-view label is
  // more explicit about which conversation the adviser is recording.
  { icon: NotebookPen, labelKey: 'clientv2-recruit-note', action: 'meeting-note', alwaysDot: true }
];

/**
 * Two groups, in the same two roles 業務 uses, so the panel has the same shape in
 * either conversation: **the selling work, then the record-keeping under it.**
 *
 * This was one group twice over, and both times the argument was that the tiles
 * were all one kind of thing and a heading per pair would be filing rather than
 * grouping. That held at four. It stopped holding once the tool row reached
 * eight controls — an unlabelled row of eight is a heap rather than a menu, and
 * 業務 answers exactly this by naming its halves and painting the quieter one
 * back (`is-quiet`). There is no reason for 招募 to answer it differently.
 *
 * > 🔒 **The split is by what the candidate sees.** 招募工具 is what an adviser
 * > puts IN FRONT of them — a page to show, a chart, a lazy-pack, the team's own
 * > files. 團隊Bot and AI Coach came out of that row and into 跟進 because
 * > neither is ever shown to a candidate: they are the adviser asking their own
 * > team's bot a question, and the adviser rehearsing. Same reason 招募 IDEA is
 * > not a card — but the opposite conclusion, because 招募 IDEA is at least about
 * > THIS person, and these two are not.
 *
 * Group 1 is 活動量計算 (swapped down from the cards when 招募 IDEA was
 * promoted), then the material in the order the 招募 page gives it (材料 →
 * 懶人包). 團隊文件 moved into the ⋯ — see RECRUIT_MORE_ITEMS.
 */
const RECRUIT_GROUPS: { titleKey: string; icon: typeof ChartPie; items: GroupItem[] }[] = [
  {
    titleKey: 'clientv2-recruit-group-tools',
    icon: Wrench,
    items: [
      { icon: Calculator, labelKey: 'clientv2-recruit-mdrt', action: 'mdrt-calculator' },
      { icon: BarChart3, labelKey: 'clientv2-recruit-materials', action: 'recruit-materials' },
      { icon: Smartphone, labelKey: 'clientv2-recruit-hot-topic', action: 'recruit-hot-topic' },
      { icon: QrCode, labelKey: 'clientv2-recruit-quiz', action: 'recruit-quiz' }
    ]
  },
  {
    // 跟進, to 客戶與跟進's tune — the quieter half, everything that is not put in
    // front of the candidate. Named in two characters rather than five 🔒: the
    // heading sits above a row of 11px labels, and 候選人與跟進 was longer than
    // the tiles it was introducing.
    //
    // 業務追蹤 has no counterpart here: it tracks a SALES relationship, which is
    // the other conversation.
    titleKey: 'clientv2-recruit-group-follow',
    icon: UserRound,
    // 詳細資料 leads, as it leads 客戶與跟進 in 業務. Three tiles land the ⋯ in
    // the fourth column and 快速資料 on the next row.
    items: [
      { icon: IdCard, labelKey: 'clientv2-tile-details', action: 'factsheet' },
      { icon: GraduationCap, labelKey: 'clientv2-recruit-ai-coach', action: 'ai-coach' },
      { icon: BookOpenCheck, labelKey: 'clientv2-recruit-iiqe', action: 'iiqe-study' }
    ]
  }
];

/**
 * Behind the ⋯ tile in 招募 view.
 *
 * > 🔒 **聯絡 alone, and the ⋯ stays.** This file argued twice that a ⋯ hiding a
 * > single item is a tap spent discovering there was nothing to discover, and
 * > that argument is why Phase 1 had no overflow at all. The user overruled it
 * > for this panel: 推薦QR came out, 聯絡 stayed put, and the ⋯ keeps its place.
 * > Recorded as a decision rather than dressed up as a derivation — if the rule
 * > is revisited, it is this line that has to be revisited with it.
 *
 * What the ⋯ still buys, for whoever does revisit it: 聯絡 is the one control
 * here that is not about preparing for the conversation, and the row it would
 * otherwise sit in is the one an adviser scans for what to open next.
 *
 * 詳細資料 is deliberately NOT in here — it is the door to the whole record and
 * it leads 客戶與跟進 in 業務, so it leads 跟進 here. (It was unreachable in 招募
 * altogether before this round: it travels with the 客戶與跟進 group, which this
 * view does not render.)
 *
 * 繳費提醒 and 產品推薦 do not cross over. What a career conversation needs
 * reminding of — an exam date, a notice period, a briefing night — is not what
 * 保費提醒 tracks, and the tile is named for the premium.
 */
const RECRUIT_MORE_ITEMS: GroupItem[] = [
  { icon: MessageCircle, labelKey: 'Contact', action: 'contact' },
  // 團隊文件, moved out of 招募工具. Still greyed when the adviser's team has no
  // TeamHub (recruitToolAvailable), same as it was as a tile.
  { icon: FolderOpen, labelKey: 'clientv2-recruit-team-hub', action: 'team-hub' },
  { icon: Bot, labelKey: 'clientv2-recruit-team-bot', action: 'team-bot' }
];

// 招募對話：如何開口 — the four rows behind the DISC readout in 招募 view. Moved
// to its own module when 招募 IDEA's 🗣 section started rendering the same table:
// see partials/recruitPlaybook.ts for why it is not PRO-gated and where the
// copy lives.

/** Which conversation the panel is laid out for. */
type DrawerView = 'business' | 'recruit';

/**
 * 建議下一步 — what to do about THIS client, first.
 *
 * The complaint the drawer's regrouping did not answer was "I don't know what
 * to click first", which is a question about priority, not quantity: no amount
 * of grouping or hiding says which control matters for the client on screen.
 * This line does, and it costs no new data — `signal` is the entry the page's
 * own 重點跟進 queue already computed (utils/client-queue.ts detectSignal), so
 * the suggestion and that list cannot disagree.
 *
 * The sentence is SIGNAL_MESSAGE_ID's, verbatim, for the same reason.
 */
type Suggestion = {
  /** i18n id for the sentence. */
  copyKey: string;
  /** Glyph colour, and the tint behind it — the queue's own two encodings. */
  color: string;
  tint: string;
  /** i18n id for the button. */
  labelKey: string;
  /** Withheld when the status withholds this, like every other control. */
  capability: ClientCapability;
  /** 生日 opens the greeting dialog, which is a callback rather than an action. */
  kind: 'action' | 'birthday';
  action?: ClientDrawerAction;
};

const suggestionFor = (signal: QueueSignal, rest: Omit<Suggestion, 'copyKey' | 'color' | 'tint'>): Suggestion => ({
  copyKey: SIGNAL_MESSAGE_ID[signal],
  color: SIGNAL_COLOR[signal],
  tint: SIGNAL_TINT[signal],
  ...rest
});

const SUGGESTION_BY_SIGNAL: Record<QueueSignal, Suggestion> = {
  // The reminder was set in the meeting-note dialog, so that is where it is
  // acted on and cleared.
  [QUEUE_SIGNALS.REMIND_TODAY]: suggestionFor(QUEUE_SIGNALS.REMIND_TODAY, {
    labelKey: 'meetingNotes',
    capability: 'meeting-note',
    kind: 'action',
    action: 'meeting-note'
  }),
  [QUEUE_SIGNALS.BIRTHDAY_IMMINENT]: suggestionFor(QUEUE_SIGNALS.BIRTHDAY_IMMINENT, {
    labelKey: 'Greeting',
    capability: 'edit-profile',
    kind: 'birthday'
  }),
  [QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH]: suggestionFor(QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH, {
    labelKey: 'Greeting',
    capability: 'edit-profile',
    kind: 'birthday'
  }),
  [QUEUE_SIGNALS.POLICY_DUE_SOON]: suggestionFor(QUEUE_SIGNALS.POLICY_DUE_SOON, {
    labelKey: 'Policy_Portfolio',
    capability: 'portfolio',
    kind: 'action',
    action: 'portfolio'
  }),
  [QUEUE_SIGNALS.LONG_TIME_NO_SEE]: suggestionFor(QUEUE_SIGNALS.LONG_TIME_NO_SEE, {
    labelKey: 'meetingNotes',
    capability: 'meeting-note',
    kind: 'action',
    action: 'meeting-note'
  })
};

/**
 * The fallback, used once the queue has nothing dated to say.
 *
 * It used to be 「補充客戶資料，分析更準確」 — and that is now the 補資料 tile's job,
 * done better: the tile names how many answers are left, takes them in place
 * instead of sending the adviser out to the legacy factsheet page, and stops
 * asking once they are given. Two controls making the same request, with the
 * louder one being the weaker one, is worse than one; and this one never
 * checked whether anything was actually missing, so a client with a complete
 * card was told to go and complete it.
 *
 * What replaces it says something the row cannot: this client has already
 * bought, and there is a category they have not. That is a conversation the
 * adviser can only otherwise reach by opening the portfolio and noticing.
 *
 * It is deliberately quiet — on a real 186-client book it speaks for 14 of the
 * 52 the queue has nothing dated for, and the other 38 get no line at all. The
 * old invariant that this line is never empty is gone with the copy that made
 * it possible: a line that always has something to say is a line that says
 * nothing, and the panel is better for the silence.
 */
const COVERAGE_GAP_SUGGESTION: Omit<Suggestion, 'copyKey'> = {
  color: '#a8730f',
  tint: '#fdf6ea',
  labelKey: 'need-analysis',
  capability: 'analysis',
  kind: 'action',
  action: 'analysis'
};

/**
 * The third rung: a client holding none of the five tracked categories.
 *
 * The copy claims nothing about what they hold, because it cannot — five of the
 * twelve subcategories is not enough to call anyone uninsured, which is exactly
 * the mistake the removed 「客戶尚未有任何保單」 line made. It offers a next step
 * instead of a diagnosis.
 *
 * A.I. rather than 需求分析, which is the more obvious answer and the wrong one:
 * on a real book 16 of these 19 clients have no monthly income on file, so 需求
 * 分析 opens an empty form and the "next step" turns back into a data-entry
 * chore. Their actual state is that the CONVERSATION has not happened — 18 of
 * the 19 have never been met — and A.I. is the one promoted tool that works
 * with no financial data at all. It reads DISC, 性別 and 婚姻, which is what the
 * 補資料 card collects, so the two features feed each other.
 *
 * One sentence for all of them, and that is accepted rather than overlooked:
 * they are 10% of the book, not the 100% the old 「補充客戶資料」 line addressed,
 * and it points at a sale rather than at housekeeping.
 */
const NO_COVER_SUGGESTION: Suggestion = {
  copyKey: 'clientv2-signal-no-cover',
  color: '#7c3aed',
  tint: '#f3efff',
  labelKey: 'clientv2-ai',
  capability: 'ai',
  kind: 'action',
  action: 'ai'
};

export type ClientDetailDrawerProps = {
  client: QueueClient | null;
  scores: AnalysisScores;
  onClose: () => void;
  /**
   * Forwarded to qualifyingFactsheetOf, same as ClientRow/ClientHealthCard -
   * without it, a client shared by multiple advisers resolves against the
   * FIRST advisorDetails entry, which can belong to someone else.
   */
  adviserEmail?: string;
  intl: IntlLike;
  /**
   * Reference instant for the birthday "is it today" check. Defaults to the
   * real clock (moment(undefined) === moment()) so callers can omit it;
   * exposed as a prop so tests can pin it, same as buildTodayQueue's `now`.
   */
  now?: moment.MomentInput;
  /**
   * Tap affordances the redesign turned back into interactive controls
   * (handoff PART 6n). These wire to existing ClientEntry dialogs
   * (connection/consent, birthday greeting, DISC edit, EditFamilyDialog) in the
   * container that owns `history`/`toggleDialog`; here they are optional so this
   * leaf stays free of that plumbing until the entry point (Task 11/12) passes
   * them down. Rendered as real buttons regardless - PART 6n stresses the
   * connection/consent flow especially must stay clickable.
   */
  onConnect?: (client: QueueClient) => void;
  /**
   * The C.P.I. explainer, behind the 潛力 tag. Same dialog the client-base
   * indicator above the list opens (ClientBasePopup) — it answers both readings
   * of the score, the adviser's own band and the per-client one, and the tag
   * was the one place the per-client reading appears with nothing to explain
   * it. Takes no client: the dialog is about what the score MEANS.
   */
  onCpiInfo?: () => void;
  onBirthday?: (client: QueueClient) => void;
  onEditPersonality?: (client: QueueClient) => void;
  onEditFamily?: (client: QueueClient) => void;
  onEditTags?: (client: QueueClient) => void;
  /** The adviser's tag palette (user.usedTags) — the colour authority for tag chips. */
  usedTags?: ClientTag[];
  /**
   * 現有客戶. `null` is the adviser clearing the answer by tapping the one that
   * is already set — the factsheet field is tri-state, so "not answered" has to
   * remain reachable and distinct from 否.
   */
  onEditExisting?: (client: QueueClient, value: ExistingClientChoice | null) => void;
  /** 重要性. `null` clears it, same toggle-off as 現有客戶 and the factsheet form. */
  onEditTier?: (client: QueueClient, value: ClientTierChoice | null) => void;
  /**
   * 補資料 — one C.P.I. factsheet field, written straight from the popup.
   * `null` clears it, the same tri-state the two 客戶狀態 answers use.
   *
   * Deliberately a single generic write rather than a callback per field: all
   * four are the same `updateFactSheet` call with a different key, and the
   * popup's whole point is that answering is one tap with no form in between.
   */
  onEditFact?: (client: QueueClient, key: QuickFactKey, value: string | number | boolean | null) => void;
  /**
   * Bumped whenever a factsheet write comes back failed (ClientV2Bridge watches
   * `updateFactSheetPhase`). Every echo below is a promise that a tap reached
   * the server; this is the retraction, and on a change they are all dropped
   * back to whatever the store holds.
   *
   * All of them, not the one that failed, because the phase is a single global
   * and cannot say which write it belonged to — and the adviser answers several
   * of these in a row. Falling back to the record is the safe direction: the
   * worst case is that an answer which DID land flickers until the refetch
   * arrives, against the alternative of leaving one on screen that never did.
   */
  factWriteFailed?: number;
  /**
   * Whether any factsheet write is in flight (`pendingFactWrites > 0`).
   * Not a spinner — nothing here waits on it. It is what keeps the echoes above
   * standing while the write they belong to is still out, so that a refreshed
   * client arriving for some other reason does not answer for it.
   */
  factWriteInFlight?: boolean;
  /**
   * Called the moment 生日解鎖 opens 智能 IDEA on a dob that has been WRITTEN but
   * not yet confirmed. The dialog is handed the new date directly, so it is the
   * one surface here that keeps showing an answer after the drawer itself has
   * retracted it — this is how the bridge learns there is a reward on screen it
   * may have to take back. See `openBirthdayUnlock`.
   */
  onBirthdayUnlock?: () => void;
  /**
   * The adviser's own DISC letter. The C.P.I.'s DISC axis scores nothing unless
   * BOTH letters are set (server utils/client.js), so without it the 補資料
   * popup must not offer DISC as a way to earn a ranking.
   */
  adviserPersonality?: string | null;
  /**
   * Whether this adviser holds each of the three borrowed 招募 tools, resolved
   * in ClientV2's container from the helpers their own pages use
   * (`showShowTeamLibrary`, `shouldEnableTeamChatBot`, `shouldEnableAiCoach`).
   *
   * A false one greys its tile rather than withholding it — feature access is a
   * different question from client status, and a tile that disappears makes the
   * row a different shape for different teams.
   */
  teamHubAvailable?: boolean;
  teamBotAvailable?: boolean;
  aiCoachAvailable?: boolean;
  /**
   * A.I. 智能助理 (FEATURE_GPT or Ultra). Without it the A.I. cards stay on screen,
   * greyed, and a tap offers the upgrade instead of opening /gpt-options — which
   * would send the adviser to /home.
   */
  aiAvailable?: boolean;
  /**
   * 招募模式's gate — the campaign code FEATURE_RECRUITMODE, resolved in
   * ClientV2's container (`shouldEnableRecruitmentMode`, utils/user.js).
   *
   * Withholds everything 招募模式 ADDED to this panel: the 業務｜招募
   * selector, the 招募 view behind it, and the 潛在招募 switch that reveals
   * the selector. What is left is the panel exactly as it read before the mode
   * existed — which includes the 潛在招募 是／否 pair in 客戶狀態, older than
   * the mode and deliberately kept. A locked adviser still has the 招募 tab and
   * the 💼 badge (both ungated, both V1's), so they must keep a way to put a
   * client on that list; taking the question away would gate the flag without
   * gating what the flag feeds.
   *
   * Deliberately defaults to WITHHELD. An unfinished feature that leaks
   * because a prop was forgotten is the failure worth designing against; a
   * gated adviser who cannot see it is a support message.
   */
  recruitmentMode?: boolean;
  /**
   * 名單本身正處於招募模式。抽屜從這一份名單點進來,問的就是招募那一場對話,
   * 所以預設落在 招募 面板 —— 點一個候選人卻看到保單欄位,等於要顧問每一次都
   * 自己撥回去。記住的那個 per-client 選擇仍然有效(clientViewState),只是在
   * 這個模式下,沒有記錄的人從 招募 開始而不是從 業務。
   */
  recruitListMode?: boolean;
  /**
   * Open one of 招募模式's readouts as soon as the drawer settles on this
   * client. Token-bumped like the page's other one-shot requests, so the same
   * surface can be asked for twice in a row.
   */
  recruitSurfaceRequest?: { token: number; clientId: string; surface: 'idea' | 'score' | 'quiz' | 'drawer' };
  /**
   * Open 補資料 on this client, outlined on one field — what 重點跟進's 補資料
   * task asks for. Token-bumped like `recruitSurfaceRequest`. Always the
   * business card: the field it names is a C.P.I. one.
   */
  factsRequest?: { token: number; clientId: string; focus: QuickFactKey };
  /**
   * This client's entry in the page's 重點跟進 queue, if they have one. Passed
   * down rather than recomputed so the drawer's 建議下一步 and the queue list
   * cannot contradict each other; absent means the queue found nothing.
   */
  // No `signalMeta` alongside it: all five SIGNAL_MESSAGE_ID strings are plain
  // sentences with no placeholder, in all three locales, so there was nothing
  // for the values to fill. Add it back the day one of them takes a {days}.
  signal?: QueueSignal;
  onTrackingMet?: (client: QueueClient, met: number) => void;
  /** V1's OpportunityControl: L/M/H, and null when the active one is tapped. */
  onTrackingOpportunity?: (client: QueueClient, opportunity: string | null) => void;
  onAction?: (action: ClientDrawerAction, client: QueueClient) => void;
  /**
   * Called INSTEAD of the real handler when the client's connection status
   * withholds the capability (clientEntitlements.ts). The host turns it into a
   * toast; this leaf only decides that a refusal happened, not how it is
   * announced — which keeps react-toastify out of its module graph and its
   * tests.
   *
   * Why the locked controls stay clickable at all: `title` never surfaces on
   * touch, so on the phone build a hard-`disabled` tile is a dead square with
   * no explanation anywhere. Clickable + `aria-disabled` keeps the control
   * findable (a real `disabled` button is skipped by some AT navigation), and
   * the tap earns the reason instead of nothing.
   */
  onRestricted?: (client: QueueClient, capability: ClientCapability) => void;
};

const DRAWER_EXIT_MS = 220;

// Outside-tap dismissal (below): what a tap outside the panel must NOT close it
// for. A row means 「switch to that client」 — the strip a phone leaves exposed
// is deliberately live list — and anything belonging to an overlay the adviser
// opened FROM this panel is that overlay's tap, not ours, so without the second
// half a tap inside 需求分析 or the C.P.I. explainer would close the panel
// underneath it.
// `cal-day-row` is the calendar's day-detail list: below 901px the panel covers
// the page, but the day list stays exposed beside/under it, and its rows switch
// client exactly like a list row does. Left off this list, tapping client B's
// row while client A's panel is open read as an outside tap — the panel closed
// and swallowed the event, so B never opened.
const SELECTS_A_CLIENT = '[data-testid="client-row"],[data-testid="queue-row"],[data-testid="cal-day-row"]';
// Not all of these are portalled, which is what the name used to claim and what
// made this list wrong. `.MuiBackdrop-root` is the case that proves it: below
// 600px — the same width that arms this whole rule — BottomSheetOrDialog swaps
// the MUI Dialog for a react-spring-bottom-sheet with `blocking={false}`, so the
// sheet renders NO backdrop of its own and the component supplies a standalone
// MUI <Backdrop> instead (BottomSheetOrDialog.js:71, z-index 1043, under this
// panel's 1046). 補資料 is the one caller that overrides `blocking` to true, for
// the focus trap; it draws an rsbs backdrop as well, but its overlay covers the
// viewport so that one is never exposed to a tap. See the FactsSurface comment. That Backdrop is not a Modal, so it has no `.MuiModal-root`
// ancestor and it is rendered in place — ClientEntryDialogs sits as a plain
// sibling in ClientV2Bridge, i.e. outside this component but not in a portal.
// Its onClick IS the sheet's dismiss, so leaving it off this list meant the one
// tap that closes 會面筆記 or 智能 IDEA on a phone closed the client panel behind
// them instead, and the swallow window below then ate the click so the sheet
// stayed open. Read this list as 「belongs to an overlay」, not 「is portalled」.
const OVERLAY_OF_ITS_OWN =
  '.MuiModal-root,.MuiDialog-root,.MuiPopover-root,.MuiMenu-root,.MuiTooltip-popper,.MuiSnackbar-root,.MuiBackdrop-root,[role="dialog"],[role="alertdialog"],[role="tooltip"]';
// The dismissing tap is swallowed, so the click behind it has to be eaten too.
// Short, because it now only has to cover the gap between the pointerup that
// dismissed and the click behind it; the window is also retired by the next
// pointerdown, so it can never outlive the gesture that opened it.
const OUTSIDE_CLICK_SWALLOW_MS = 400;
// What separates a tap from a scroll. The dismissal is decided on pointerup and
// only for a finger that stayed put: the exposed strip is the only piece of page
// a phone leaves reachable, and dragging it to scroll the list behind is an
// ordinary thing to do — deciding on pointerdown threw the panel away the moment
// the finger landed, before there was anything to decide from. (The page really
// does scroll under there: no scroll lock, `touch-action: auto`.)
const TAP_SLOP_PX = 10;
const TAP_MAX_MS = 700;
// 601px, because 600 is where the exposed strip is defined (styles.scss) — the
// rule and the thing it exists for turn on together. Written as a min-width and
// negated, the way component.tsx's own viewport check is, rather than as a
// max-width: same idiom, and jsdom (every query reports `matches: false`) then
// reads as the phone, which is the case worth testing by default.
const WIDER_THAN_PHONE = '(min-width: 601px)';
const isWiderThanPhone = () => typeof window.matchMedia === 'function' && window.matchMedia(WIDER_THAN_PHONE).matches;

// Swipe-right-to-dismiss, the other way out a touch device gets (and it gets it
// at every width, unlike the tap above). Deliberately a detector
// and not a drag: the panel does not follow the finger, it plays the same 220ms
// `is-closing` exit the ✕ plays, so the animation stays where it already lives
// (CSS) and this stays three handlers.
const SWIPE_MIN_X = 56;
const SWIPE_MAX_MS = 600;

/**
 * 會面次數's range, and how long a burst of taps is allowed to settle.
 *
 * 12, not the 9 the select this replaced offered. Everywhere ELSE this same
 * field is edited it goes through MetControl, which caps at 12
 * (controls/MetControl/MetControl.js, used by MeetingDialog and TrackOffDialog)
 * — so a count raised to 10, 11 or 12 from one of those dialogs came back to a
 * select with no matching option, and the control rendered blank rather than
 * the number on file.
 *
 * The delay is what a stepper needs and a dropdown did not. A select committed
 * once per choice; two buttons commit once per TAP, and `enableTracking` is a
 * plain thunk (store/client/duck.js) with no ordering between calls — so going
 * from 0 to 5 would put five writes on the wire, each chaining its own
 * GET_CLIENT_BY_ID, and whichever answered last would decide what the server
 * kept. Held long enough for the next tap of a burst to land, short enough that
 * nobody waits for it; the count on screen moves on the tap regardless, and the
 * pending write is flushed if the drawer closes first.
 */
const MET_MIN = 0;
const MET_MAX = 12;
const MET_WRITE_DELAY_MS = 500;
// How far the finger may drift down before it counts as scrolling rather than
// swiping — the drawer body scrolls, and that gesture must survive a wobble.
const SWIPE_MAX_DRIFT_Y = 24;

const displayName = (client: QueueClient): string => clientNameOf(client) || '-';

// Absent vs. "today" (same rule as before the v46 restyle, dash aligned with
// displayName's own fallback): no dob on file is "-", never a blank or coerced
// date; a dob landing on `now` is called out as "Today" rather than a date the
// adviser must cross-check. Month+day only - a birthday recurs yearly, so
// equality ignores the birth year.
// `today` is passed in rather than read here: this is a module-level helper with
// no intl of its own, and the word has to be the queue's own
// (clientv2-queue-today, as TodayQueue renders it) rather than an English
// literal — the drawer and that list name the same day for the same client.
// The year comes back separately, and split at the century, so the narrow-phone
// stylesheet can drop 「19」 (1996 → 96) and then, narrower still, the year
// altogether. 「1996-07-22 (30)」 is the longest value in this band and was
// wrapping mid-date on a 360px screen; which digits to lose is a width decision,
// so it is made in CSS where the width is known, not here.
//
// ISO, matching the 生日 field in 補資料 that this readout is corrected from
// (2026-09-02). It used to print `D MMM YYYY`, whose month abbreviation is
// English in all three locales — so the panel named the date one way, the editor
// a second way, and the record a third. `MM-DD` is what survives both truncation
// steps, which is why the year (and the hyphen that joins it) is the part handed
// back separately: dropping a trailing 「 1996」 leaves a readable date, and
// dropping a leading 「1996-」 has to take the separator with it or the cell
// reads 「-07-22」.
type BirthdayReadout = { text: string; year: string | null; age: number | null };

const describeBirthday = (dob: string | null | undefined, now: moment.Moment, today: string): BirthdayReadout => {
  // Em dash, like every other empty readout in this panel (DISC, 現有客戶,
  // 重要性, 上次會面, 提醒 — all '—'). A hyphen here was a shorter, higher mark
  // than the one sitting beside it in the DISC box, so the two boxes read as
  // saying different kinds of nothing.
  const empty: BirthdayReadout = { text: '—', year: null, age: null };
  if (!dob) {
    return empty;
  }
  // `parseZone`, not a bare `moment`. A dob is a CALENDAR DATE, and the server
  // stores it as a Date — so it arrives as UTC midnight, which a plain `moment`
  // then re-reads in the browser's own zone and lands a day earlier for any
  // adviser behind UTC. Everything else that reads this field already knows
  // that: V1's factsheet editor parses it this way (clientFactsheet.js:960) and
  // so does the queue's own birthday signal (utils/client-queue.ts nextBirthday),
  // which is what `daysUntilBirthday` below counts with. Without this the
  // countdown and the date it sits beside named different days in the same box.
  const parsed = moment.parseZone(dob);
  if (!parsed.isValid()) {
    return empty;
  }
  const isToday = parsed.month() === now.month() && parsed.date() === now.date();
  // Calendar years between two DATES, not elapsed time between two instants.
  // `parsed` carries the offset it was written in and `now` carries the
  // browser's, so `now.diff(parsed, 'years')` compares moments that are hours
  // apart even when both name the same clock time — and a dob stored as UTC
  // midnight is 8 hours ahead of local midnight in the market this ships to. It
  // read one year low for the whole of the birthday morning: 「今天 (35)」 at
  // 00:30 and 「今天 (36)」 at 09:00, same client, same day, on the one day the
  // adviser is most likely to open the panel. The line above already decided
  // the day from calendar fields; this now does its arithmetic on the same
  // footing instead of undoing it.
  const age = moment([now.year(), now.month(), now.date()]).diff(
    moment([parsed.year(), parsed.month(), parsed.date()]),
    'years'
  );
  return {
    text: isToday ? today : parsed.format('MM-DD'),
    // 「今天」 carries no year to shorten.
    year: isToday ? null : parsed.format('YYYY'),
    age: age >= 0 ? age : null
  };
};

// Days to the next occurrence of the birthday, via the same leap-year-safe
// rollover the today-queue's own birthday signal uses (utils/client-queue.ts
// nextBirthday - a 29 Feb dob still fires every year). Null on "today" (the
// value above already reads "Today") and on no dob on file, so the countdown
// only adds information describeBirthday doesn't already carry.
const daysUntilBirthday = (dob: string | null | undefined, now: moment.Moment): number | null => {
  const startOfToday = now.clone().startOf('day');
  const birthday = nextBirthday(dob, startOfToday);
  if (!birthday) return null;
  const days = birthday.diff(startOfToday, 'days');
  return days > 0 ? days : null;
};

/** The two answers 現有客戶 takes, in the order the factsheet form offers them. */
const EXISTING_CHOICES: ExistingClientChoice[] = ['Yes', 'No'];

/**
 * Answer colours for the two settable cells. The cells report their answer the
 * way every other readout in the grid does — a dot plus a word — so the colour
 * has to be available to the JSX, as 上次會面's and DISC's already are.
 *
 * These are the cells' own colours now, and only theirs. The picker used to
 * paint its buttons to match — 是 amber, V.VIP its badge's gold — on the idea
 * that the readout and the control that set it should look like one answer.
 * What that actually produced was a private code: five answers in five colours,
 * none of it explained anywhere, and no colour left over to mean simply
 * "chosen". The picker now fills one blue pill whatever the question
 * (FactToggle), and the meaning-carrying colours stay out here on the readouts,
 * where a dot beside a word is all they ever had to be.
 */
const EXISTING_DOT: Record<ExistingClientChoice, string> = { Yes: '#f59e0b', No: '#94a3b8' };
const TIER_DOT: Record<ClientTierChoice, string> = {
  Normal: '#94a3b8',
  VIP: '#e0a80a',
  'V.VIP': '#d4562f'
};

/**
 * 補資料 — the C.P.I. fields the popup can answer, in the order it asks.
 *
 * Tap-only, and that is the whole design. The C.P.I. is a dead loop from the
 * adviser's side (no data, no ranking, so no reason to enter data), and the way
 * out is not a better form — it is not needing a form. Three of these four are
 * a single tap on a closed set; the fourth is a date. 月入 is not offered at
 * all: it is worth 0.9 but it is the one question that cannot be asked
 * casually, and the arithmetic says it is never required (clientCpiGap.ts).
 *
 * DISC leads because it is the cheapest answer in the set — one tap on four
 * letters, and the drawer is already showing the adviser what it means.
 */
/**
 * Every field the quick card can answer. A superset of `CpiField`: the C.P.I.
 * is not the only reason to know something about a client.
 */
export type QuickFactKey = CpiField | 'gender' | 'source' | 'isRecruitment' | 'occupation';

/**
 * The card's two halves, and the reason it has two.
 *
 * 認識客戶 is what the AI reads — `client_personality`, `client_gender`,
 * `client_marriage_status`, `client_child` and `client_monthly_income` are the
 * literal payload ChatGPTPersonalAnalysis and ChatGPTIceBreakingDrawerV2 send
 * about a client. Answering these changes what the adviser is told.
 *
 * 分類客戶 is where the client sits in the book: it drives the filters, the sort,
 * the 招募 tab and the badges on the row. Answering these changes what the
 * adviser can find.
 *
 * They are NOT interchangeable, and the split says so. The first half also
 * expires — once known, a person's DISC is not asked again — while the second
 * moves with the relationship (陌生 → 認識, 普通 → VIP), which is why only the
 * first half is ever counted or chased (see the tile's badge).
 */
const FACT_CHOICES: {
  key: Exclude<QuickFactKey, 'dob' | 'monthlyIncome' | 'personality'>;
  labelKey: string;
  choices: string[];
  /** Written as a boolean, not a string — 潛在招募 is a flag on the factsheet. */
  boolean?: boolean;
}[] = [
  { key: 'gender', labelKey: 'Gender', choices: ['Male', 'Female'] },
  { key: 'marriageStatus', labelKey: 'Marriage Status', choices: ['Single', 'Married'] },
  // 3 rather than 3+: the field is a number the C.P.I. reads directly, and the
  // score keeps rising past 3, so an open-ended bucket would quietly cap it.
  // Four taps covers the overwhelming majority; the factsheet form takes the
  // rest.
  { key: 'child', labelKey: 'No. of Child', choices: ['0', '1', '2', '3'] }
];

/**
 * 認識客戶's questions, in the order the card asks them. The tile counts these
 * and lives exactly as long as one is unanswered — they are what the AI reads,
 * which is what the card is selling.
 */
const KNOW_FIELDS: QuickFactKey[] = ['personality', 'gender', 'marriageStatus', 'child', 'dob'];

/**
 * 了解這位候選人 — the 招募 view's own version of the card above, and the reason
 * this view can show anything at all.
 *
 * The five facts it asks for all EXIST on the factsheet already, but existing
 * is not the same as being there: nothing in ClientV2 has ever asked for 現職
 * or 現時月入, so for most of a book they are empty, and a panel that reports
 * empty values is worse than one that asks the questions.
 *
 * Every one of them is SHARED with the business card — same field, same write,
 * visible in both views. (Each row used to carry a 共用 tag saying so; the
 * user asked for it to come off.)
 *
 * What is NOT here, and why:
 *  - 生日 — the reward behind it is a birthday greeting, which belongs to the
 *    relationship the business card is about. Still one tap away there.
 *  - 性別 — it feeds the AI's client copy, and no recruitment feature reads it.
 *  - 現有客戶 / 重要性 — they rank a client against the book for SELLING.
 *
 * DISC leads, for the reason it leads the business card: it is the cheapest
 * question on either, two taps about a person the adviser has met.
 *
 * 現時月入 comes second rather than last, which is the reverse of the business
 * card's own ranking (clientCpiGap.ts puts it at the bottom as the one question
 * an adviser cannot ask casually — and this dialog does not offer it there at
 * all). In a career conversation it is not an intrusion into the subject, it IS
 * the subject: what someone earns now is where every income comparison starts.
 */
const RECRUIT_FACT_FIELDS: (QuickFactKey | 'recruitRoute' | 'recruitStage')[] = [
  'recruitRoute',
  'recruitStage',
  'source',
  'personality',
  'monthlyIncome',
  'occupation',
  'marriageStatus',
  'child'
];

/**
 * How long the 補資料 tile outlives its own last answer, so the bar can fill and
 * be seen filling. Long enough to read as a result rather than a flicker, short
 * enough that it never reads as a tile that failed to go away — it is a receipt
 * for work just finished, not a state.
 */
const FACTS_COMPLETE_MS = 1600;

type FactsTileProps = {
  /** 認識客戶 questions still unanswered. */
  remaining: number;
  /** Answered out of askable — the progress bar's fill. */
  answered: number;
  total: number;
  /** Mirrors the old inline tile's `data-plan` hook. */
  plan: string;
  factsOpen: boolean;
  disabled: boolean;
  lockTitle?: string;
  label: React.ReactNode;
  onClick: () => void;
};

/**
 * 補資料, as its own component so it can outlive its own condition.
 *
 * It is a component and not inline JSX for one reason: the completion beat
 * needs state, and the drawer returns null above this point when it has no
 * client (`if (!activeClient)`), so a hook added to the drawer body for this
 * would be a conditional hook. Owning the state here keeps the hook order flat
 * in both files.
 *
 * The parent no longer decides whether there is anything to show — this does,
 * because "nothing left to ask" and "nothing left to ask, a moment ago" are the
 * same input and different answers. Mount it with the client's id as `key` and
 * a client switch remounts it, which is what resets both the celebration and
 * the baseline it is measured against; without that a drawer moved from a
 * half-filled client to a complete one would read the change as an answer
 * landing.
 */
const FactsTile = (props: FactsTileProps) => {
  const { remaining, answered, total, plan, factsOpen, disabled, lockTitle, label, onClick } = props;

  /**
   * Set only by a real 「was unfinished, now finished」 transition seen in THIS
   * mount. A drawer opening on an already-complete client has no transition to
   * see — the ref starts null and the first run only primes it — so it renders
   * nothing, exactly as the inline tile did.
   */
  const [justCompleted, setJustCompleted] = useState(false);
  const remainingRef = useRef<number | null>(null);

  useEffect(() => {
    const previous = remainingRef.current;
    remainingRef.current = remaining;
    // A question came back, so the receipt is no longer true — drop it NOW
    // rather than waiting for the timer, which this run's cleanup has already
    // cancelled. Without this branch the celebration latched: answering the
    // last question armed a 1.6s timer, and taking any answer back inside that
    // window ran the cleanup (clearing the timer) and then fell out on
    // `previous === 0` without re-arming, leaving the tile showing a ✓ and no
    // count for a card that still had a question in it. Nothing cleared it
    // short of the remount a client or view switch brings.
    if (remaining > 0) {
      setJustCompleted(false);
      return undefined;
    }
    if (previous === null || previous === 0) return undefined;
    setJustCompleted(true);
    const timer = window.setTimeout(() => setJustCompleted(false), FACTS_COMPLETE_MS);
    return () => window.clearTimeout(timer);
  }, [remaining]);

  if (remaining === 0 && !justCompleted) return null;

  return (
    <button
      type="button"
      data-testid="drawer-facts"
      data-plan={plan}
      data-complete={justCompleted || undefined}
      className={`clientv2-facts-tile${justCompleted ? ' is-complete' : ''}`}
      aria-haspopup="dialog"
      aria-expanded={factsOpen}
      aria-disabled={disabled || undefined}
      title={lockTitle}
      onClick={onClick}
    >
      <span className="clientv2-facts-glow" aria-hidden="true" />
      <span className="clientv2-facts-icon" aria-hidden="true">
        {/* A clipboard of items to fill in, which is literally what the tile
            opens: five tap-sized questions, a count of the ones still blank, and
            a bar for how far in. It replaced a magnifier over a person, whose
            promise was 「look this client up」 — a search, when nothing here
            searches anything and everything here is answered.

            Not Lightbulb: 智能摘要 two rows up already wears it, and two identical
            glyphs in one panel read as two doors to the same thing. Not a person
            with a pen either — the panel already carries four person glyphs
            (UserRound, User, Users, UserPlus) and a pen on a notebook
            (會面筆記), and this would have collided with both. A clipboard is
            the only shape in the row that is not already spoken for (IdCard,
            Cctv, ⋯). It gives way to a tick once there is nothing left to ask. */}
        {/* 20, the size every tile in this row draws its glyph at. */}
        {justCompleted ? <Check size={20} /> : <ClipboardList size={20} />}
        {/* The cost, as a number, on the same glyph as the prize — the whole
            negotiation the tile is making, "this much work, that much better".
            At 1 it reads as nearly done without needing a second sentence for
            that case. It goes once the answer is 0: a badge reading zero is a
            chore that has not noticed it is over. */}
        {!justCompleted && <span className="clientv2-facts-count">{remaining}</span>}
      </span>
      {/* Names what filling it BUYS, not what it costs. 「補資料」 described the
          chore; an adviser has no reason to want a fuller record, and "a
          ranking" is a scoreboard rather than a reason. What they want is to
          walk into the next meeting knowing something — which is literally what
          these fields do, since they are what the AI features read (see the
          popup's own line). */}
      <span className="clientv2-facts-label">{label}</span>
      {/* The same count, drawn a second way. The number in the badge states the
          work LEFT; this states how far in they already are — 「3 questions」
          reads as a chore, 「2 of 5 done」 reads as nearly there.

          A bar along the bottom edge, not the arc that used to ride the icon.
          The arc shared its 26px circle with the count badge, which is white,
          sits at top-right, and covered the stretch of the arc between twelve
          and three o'clock — so on a phone the two white marks read as one
          smudged ring, and the progress could not be recovered from it at any
          value. Reported from the device, 2026-08-31. This has the full width of
          the tile to itself and nothing overlapping it.

          `aria-hidden`: the badge and the popup's own 「還有 N 題」 already carry
          this in words, so the bar adds no announcement. */}
      <span className="clientv2-facts-progress" aria-hidden="true">
        <span
          className="clientv2-facts-progress-fill"
          data-testid="facts-progress"
          data-answered={answered}
          data-total={total}
          style={{ width: `${total === 0 ? 0 : Math.round((answered / total) * 100)}%` }}
        />
      </span>
    </button>
  );
};

/** A boolean field's two buttons reuse 現有客戶's 是 / 否, already in every locale. */
type FactTextGroupProps = {
  fieldKey: QuickFactKey;
  label: string;
  hint: string;
  clearLabel: string;
  icon: React.ReactNode;
  /** The answer on file, already stringified. '' means never answered. */
  value: string;
  /** Writes a number instead of a string, and asks for the numeric keypad. */
  numeric?: boolean;
  focused: boolean;
  disabled: boolean;
  onCommit: (value: string | number | null) => void;
};

/**
 * What a grouped figure carries that the number does not: the separators this
 * app's three locales use for thousands (all of them a comma — en, zh and
 * zh-Hant-HK group the same way), plus every kind of space, since a non-breaking
 * one can arrive with a paste and is invisible in the field afterwards.
 *
 * Not the decimal point: none of these locales spells it with a comma, and
 * eating it would turn a decimal into an integer a hundred times too big.
 */
const GROUPING_CHARS = /[\s,，]/g;

/**
 * A typed answer on 了解這位候選人 — 現職 and 現時月入, the two questions on that
 * card that no set of buttons can ask.
 *
 * Its own component for the same reason FactsTile is: it needs state (the
 * half-typed value) and the drawer returns null above this point when it has no
 * client, so a hook for it in the drawer body would be a conditional hook.
 *
 * Commits on BLUR, not on change — the one lesson the 生日 field on this card
 * already paid for. Every other answer here writes the moment it is tapped
 * because a tap is a finished answer; a keystroke is not, and writing per
 * keystroke would send 「市」,「市場」,「市場推」 to the server and leave whichever
 * arrived last as the record. Enter commits too, so a phone keyboard's 完成 key
 * does what it looks like it does.
 */
const FactTextGroup = (props: FactTextGroupProps) => {
  const { fieldKey, label, hint, clearLabel, icon, value, numeric, focused, disabled, onCommit } = props;
  const [draft, setDraft] = useState(value);

  // Follow the record when it changes underneath — a write landing, a retracted
  // echo after a failure, or the adviser switching client without this
  // unmounting. Never while the field has focus: that would rewrite what
  // somebody is in the middle of typing.
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraft(value);
  }, [value]);

  const commit = () => {
    const next = draft.trim();
    if (next === value) return;
    if (next === '') {
      onCommit(null);
      return;
    }
    if (!numeric) {
      onCommit(next);
      return;
    }
    // Grouping is display, not data — and it is the display this very card
    // hands back: the 現時月入 cell prints the answer through `toLocaleString`,
    // so 「50,000」 is the shape an adviser has just been shown and the shape
    // they retype or paste to correct it. `Number('50,000')` is NaN, so before
    // this the field answered its own format by silently reverting.
    //
    // Stripped rather than blocked on input: a keystroke filter would fight the
    // Android numeric keypad (which offers the separator) and would still leave
    // a paste to deal with. Whitespace goes with it — a nbsp is what some
    // locales group with, and it is invisible to whoever pasted it.
    const digits = next.replace(GROUPING_CHARS, '');
    // `Number('')` is 0, so a field holding nothing but separators would write a
    // salary of zero. It has no answer in it; treat it as unparseable.
    const parsed = digits === '' ? NaN : Number(digits);
    // A number field that cannot parse has no answer to send. Put the record
    // back rather than writing NaN, which Mongoose would store as null anyway
    // and which would read here as "cleared" — a silent deletion of an answer
    // the adviser was trying to correct.
    if (!Number.isFinite(parsed)) {
      setDraft(value);
      return;
    }
    onCommit(parsed);
  };

  return (
    <div
      className={`clientv2-statusgroup${focused ? ' is-focus' : ''}`}
      data-testid="drawer-facts-group"
      data-section="know"
      data-field={fieldKey}
      data-value={value}
    >
      {icon}
      <div className="clientv2-statusgroup-body">
        <div className="clientv2-statusgroup-k">
          {label}
          <span className="clientv2-facts-hint">{hint}</span>
          {value !== '' && (
            <button
              type="button"
              className="clientv2-statusgroup-clear"
              data-testid={`drawer-facts-clear-${fieldKey}`}
              onClick={() => {
                setDraft('');
                onCommit(null);
              }}
            >
              {clearLabel}
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          // `inputMode` rather than `type="number"`: a number input on a phone
          // still accepts 'e' and '-', and its spinner is meaningless for a
          // salary. This asks for the numeric keypad and validates on commit.
          inputMode={numeric ? 'numeric' : 'text'}
          className="clientv2-facts-text"
          data-testid={`drawer-facts-input-${fieldKey}`}
          disabled={disabled}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
      </div>
    </div>
  );
};

const BOOL_LABEL: Record<string, string> = { true: 'Yes', false: 'No' };

/**
 * The answers that are a symbol rather than a word.
 *
 * Only where the symbol is at least as fast to read as the text, which rules
 * most of this card out: 普通 / VIP / V.VIP is a rank with no glyph anyone
 * shares, 認識 / 介紹 / 陌生 is a provenance, and 子女 is already a numeral.
 * What is left is the four rows below, where the mark is understood before the
 * word would have been — and where the two answers are opposites, so the pair
 * reads as a switch rather than as a list.
 *
 * Single and married are drawn as what actually changes: one figure, then two.
 * Yes and no take ✓ and ✕, which is why the selected-answer tick does NOT ride
 * along on an icon button — a ✓ beside a ✓ says nothing about which of them
 * means "chosen". Those buttons are marked by their own pressed styling
 * instead, which every other button on this card already carries as well as
 * its tick.
 *
 * 性別 is NOT here, and was: ♂ / ♀ are the one genuinely universal pair on this
 * card, and they still lost to 男性 / 女性. Two characters cost no more width
 * than the glyph did, they need no `aria-label` to have a name, and they are
 * the same two characters V1's factsheet has always used for this field.
 */
const CHOICE_ICON: Record<string, typeof Check> = {
  Single: User,
  Married: Users,
  Yes: Check,
  No: X
};

/**
 * Answers this card words differently from the rest of the app.
 *
 * 男 / 女 rather than 男性 / 女性 (M / F rather than Male / Female): a two-button
 * row wants the shortest thing that is still unambiguous, and the suffix is
 * carried by the question above it. The app-wide `Male` / `Female` keep their
 * full form — six other places print them, including a chart's axis labels
 * (UserDistributionChart) and V1's own factsheet, and none of those asked to be
 * shortened.
 */
const CHOICE_LABEL: Record<string, string> = {
  Male: 'clientv2-gender-male',
  Female: 'clientv2-gender-female'
};

/**
 * Questions that ask themselves.
 *
 * Every other row on this card earns its subtitle: 現有客戶 turns on a
 * distinction the words do not make ("closed" vs "still being worked"), 潛在招募
 * has a consequence elsewhere in the app, 生日 is selling a reward. 性別 and
 * 婚姻狀況 explain nothing an adviser did not already know from the label, and a
 * subtitle that says nothing still costs the label its width — which is what
 * pushed 「婚姻狀況」 onto two lines on a phone.
 */
const HINTLESS_FACTS = new Set<string>(['gender', 'marriageStatus']);

/** 分類客戶's own two, beside 現有客戶 and 重要性 (which come from `statusPicks`). */
/**
 * One glyph per question on 補資料.
 *
 * The card is nine questions of the same shape stacked on each other, which is
 * a lot of grey to read your way down when the thing you want is one particular
 * question. A mark per row gives the eye something to aim at that is not a word.
 *
 * The chip is `.clientv2-infoicon` — the same tinted square the panel's 現有客戶
 * / 重要性 / 上次會面 / 提醒 cells wear, at the smaller size the question row was
 * already sized for (styles.scss sizes `.clientv2-statusgroup-k .clientv2-infoicon`
 * to 26px, which is this design arriving rather than a new one).
 *
 * Glyph AND tone, so a question is findable by shape or by colour. 現有客戶 and
 * 重要性 take the same symbol and the same hue as their own cells out in the 2×2
 * grid, so the cell tapped and the question landed on are recognisably the same
 * thing; 潛在招募 gets 💼's line equivalent for the same reason.
 *
 * The tones only have to hold up against their NEIGHBOURS — nine hues nobody
 * could confuse would be a rainbow, and this card is a form, not a chart. So
 * rose and amber each come round twice, at opposite ends, while no two
 * questions next to each other share one.
 *
 * DISC is not in here: it already has a mark in this panel, and that mark is a
 * dot that switches colour with the letter. See factIcon.
 */
const FACT_ICON: Record<string, { icon: typeof Star; tone: string }> = {
  // personality is absent on purpose — DISC's mark is its letter's colour, not
  // a glyph. See factIcon.
  existing: { icon: CircleCheck, tone: 'emerald' },
  tier: { icon: Star, tone: 'rose' },
  source: { icon: Share2, tone: 'indigo' },
  isRecruitment: { icon: Briefcase, tone: 'amber' },
  gender: { icon: UserRound, tone: 'violet' },
  marriageStatus: { icon: Heart, tone: 'rose' },
  child: { icon: Baby, tone: 'teal' },
  dob: { icon: Cake, tone: 'amber' },
  // 招募 view's two. Neither appears on the business card, so neither can
  // collide with a neighbour there; here they sit either side of DISC.
  occupation: { icon: Building2, tone: 'indigo' },
  monthlyIncome: { icon: Wallet, tone: 'emerald' },
  // 招募路線 — 招募 card only, and the only question on either card that is not
  // a factsheet field (see the group itself).
  recruitRoute: { icon: Compass, tone: 'violet' },
  // 招募階段 — same standing as 招募路線: a recruitment field, not a factsheet one.
  recruitStage: { icon: Milestone, tone: 'indigo' }
};

const SEGMENT_CHOICES: {
  key: Exclude<QuickFactKey, 'dob' | 'monthlyIncome' | 'personality'>;
  labelKey: string;
  choices: string[];
  boolean?: boolean;
}[] = [
  { key: 'source', labelKey: 'client-type', choices: ['Warm', 'Refer', 'Cold'] },
  { key: 'isRecruitment', labelKey: 'recruit-lead', choices: ['true', 'false'], boolean: true }
];

/**
 * The badge `recruit-lead` carries in every locale ('潛在招募 💼', '潜在招募 💼',
 * 'Recruit-Lead 💼'). Named because the switch's label splits it off the words
 * to align it, and a bare '💼' in that code would read as decoration rather
 * than as the half of a message this has to match exactly.
 */
const RECRUIT_BADGE = '💼';

/**
 * 候選人分類 — the 招募 card's own top half.
 *
 * 潛在招募 is NOT on it, even though it is the most recruitment-specific answer
 * in the app. Answering 否 to a question on this card closes the view the
 * card belongs to, WHILE the card is open: the dialog stays up, its own
 * questions change under the adviser's thumb, and the panel behind it becomes a
 * different panel. That is a data-entry form rearranging itself in response to
 * data entry, which reads as a glitch however correct it is.
 *
 * The flag is the always-available switch beside the view selector instead,
 * where turning it off is a deliberate act and the selector closing is the
 * visible, obvious consequence of the thing just switched.
 *
 * 現有客戶 and 重要性 do not cross over either. Both rank a client for SELLING —
 * whether the policy closed, and who to chase first — and neither says anything
 * about whether this person would join.
 */
// Looked up by key rather than by index, so inserting anything into the list
// above cannot silently change which question this picks.
const RECRUIT_SEGMENT_CHOICES: typeof SEGMENT_CHOICES = SEGMENT_CHOICES.filter(choice => choice.key === 'source');

/**
 * DISC as the two questions it actually is.
 *
 * MIRROR of the app's own picker (ChatGPTPresetDrawer/Inputs/DISCPicker.jsx
 * `getDiscFromTraits`): 外向/內向 crossed with 邏輯/感覺 gives the letter. That
 * component is not imported — it is MUI + tss-react, and pulling it into a
 * ClientV2 leaf drags both into this file's test suite for what is, below, a
 * four-entry table.
 *
 * Two questions rather than four letters because they are not the same ask. A
 * row of D I S C asks the adviser to recall a framework and classify someone
 * against it; 外向定內向 asks about a person they have met. The letter is a
 * conclusion, and the popup should not be asking anyone for a conclusion.
 */
const DISC_AXES: { key: string; choices: [string, string] }[] = [
  { key: 'tone', choices: ['outgoing', 'introverted'] },
  { key: 'drive', choices: ['logical', 'emotional'] }
];

const DISC_FROM_TRAITS: Record<string, string> = {
  'outgoing|logical': 'D',
  'outgoing|emotional': 'I',
  'introverted|emotional': 'S',
  'introverted|logical': 'C'
};

const TRAITS_FROM_DISC: Record<string, [string, string]> = {
  D: ['outgoing', 'logical'],
  I: ['outgoing', 'emotional'],
  S: ['introverted', 'emotional'],
  C: ['introverted', 'logical']
};

/** Which of the two answer pickers is open, if either. */
type StatusPickKey = 'existing' | 'tier';

const LAST_MEETING_LEGEND = [
  { color: '#3999f8', before: null, after: 6 },
  { color: '#3eda7d', before: 6, after: 12 },
  { color: '#ffa600', before: 12, after: 18 },
  { color: '#ff0000', before: 18, after: null }
] as const;

/**
 * Has the needs analysis ever produced anything to draw?
 *
 * An empty radar is worse than no radar: five axes at zero look like a finding
 * ("this client has no coverage") when they actually mean "nobody has run the
 * analysis". Below this, the section shows a one-line prompt towards 需求分析
 * instead — which is the control that would fill it in.
 */
const hasCoverageData = (scores: AnalysisScores): boolean =>
  getCoverageSegments(scores).some(segment => segment.score !== null);

/**
 * Client detail side panel, opened from a ClientRow click. It overlays the
 * filter sidebar rather than pushing the client list - the list stays visible
 * and clickable underneath, so re-selecting a different row while the drawer is
 * open is a real, supported path, not an edge case to guard against.
 *
 * That is why this is a *non-modal* dialog (role="dialog" without aria-modal):
 * a true modal would make the rest of the page inert, breaking that reselection.
 * Declaring aria-modal without genuinely inert-ing the background would lie to
 * assistive tech, so it is deliberately omitted.
 *
 * Four ways out, all of them ending in onClose(): the header ✕, Escape, a tap
 * outside that does not already mean something else, and a right-swipe across
 * the panel. The last two are what a phone has instead of Escape — see their
 * own notes below for what they deliberately do NOT close for.
 *
 * Focus handling: focus moves to the close button whenever the selected client
 * (id) changes; Escape calls onClose() regardless of focus; no focus trap
 * (correct for a non-modal dialog per the WAI-ARIA APG). Restoring focus to the
 * trigger on close is the parent's job (see component.tsx) - only it can tell
 * "closed" from "re-selected to a different client".
 */
const ClientDetailDrawer = (props: ClientDetailDrawerProps) => {
  const { client, scores, onClose, adviserEmail, now, intl, signal } = props;
  const { onConnect, onCpiInfo, onBirthday, onEditPersonality, onEditFamily, onEditTags, onTrackingMet, onAction } =
    props;
  const { onTrackingOpportunity, onRestricted, onEditExisting, onEditTier, onEditFact, adviserPersonality } = props;
  // 補資料's sheet sizes its scrolling box off this — see `BottomSheetProps`
  // below for why it cannot be a percentage. Same hook MeetingDialog uses for
  // the same job, so the two sheets follow a rotation identically.
  const [, windowHeight] = useWindowSize();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  // The panel itself, so an outside tap can tell 「outside」 from 「inside」, and
  // so the swipe below can walk up from its target and stop at the panel.
  const drawerRef = useRef<HTMLElement | null>(null);
  // Where the current one-finger gesture started. Null = no gesture worth
  // measuring (multi-touch, a scroll that has declared itself, or a start
  // inside something that scrolls sideways).
  const swipeRef = useRef<{ x: number; y: number; at: number } | null>(null);
  // Deadline for eating the click behind a dismissing tap. A ref, and read by a
  // listener that outlives the panel, because the click lands after the panel
  // is already gone — see the effect below.
  const swallowUntilRef = useRef(0);
  // Wraps the ⋯ tile AND its menu, so the outside-click check below treats the
  // two as one thing — a tap on the tile must reach its own toggle rather than
  // being closed from underneath it.
  const moreWrapRef = useRef<HTMLDivElement | null>(null);
  const [lastMeetingHintOpen, setLastMeetingHintOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // The quieter second tile group (客戶與跟進 / 跟進) starts folded: it is
  // record-keeping, and folded it lets the panel open on the selling work.
  // The adviser's choice is remembered on this device.
  const [quietGroupOpen, setQuietGroupOpenState] = useState(() => {
    try {
      return window.localStorage.getItem(QUIET_GROUP_OPEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const setQuietGroupOpen = (open: boolean) => {
    setQuietGroupOpenState(open);
    try {
      window.localStorage.setItem(QUIET_GROUP_OPEN_KEY, open ? '1' : '0');
    } catch (e) {
      // Storage blocked — the fold still works for this session.
    }
  };
  // 招募對話：如何開口 — where the DISC readout goes while in 招募 view (DiscTips).
  const [discPlaybookOpen, setDiscPlaybookOpen] = useState(false);
  // 招募 IDEA. Drawer-owned like the two above it: it opens over the panel,
  // navigates nowhere, and closes back onto the same client.
  const [recruitIdeaOpen, setRecruitIdeaOpen] = useState(false);

  /**
   * 招募模式's list can send an adviser straight to one of these surfaces —
   * a suggestion's CTA, or a row's shortcut. Neither 招募 IDEA nor 候選人評估 is
   * a ClientDrawerAction: they are readouts this drawer MOUNTS while it is
   * open, so there is nothing for the dispatcher to dispatch (see
   * clientEntitlements' note on `recruit-idea`). A token request is the seam
   * instead — the same shape the page already uses for focusClientRequest and
   * calendarRequest — and it is honoured only once the drawer is actually on
   * this client's 招募 view, so it can never open a recruitment readout over a
   * business panel.
   */
  const lastSurfaceToken = useRef<number | null>(null);
  const lastFactsToken = useRef<number | null>(null);

  const [recruitQuizOpen, setRecruitQuizOpen] = useState(false);
  const [coverQuizOpen, setCoverQuizOpen] = useState(false);
  const [quizChooserOpen, setQuizChooserOpen] = useState(false);
  // The part QuizChooserDialog picked; the quiz dialog links to it. Only the
  // chooser picks part 2: every other way in opens part 1.
  const [coverQuizPart, setCoverQuizPart] = useState<1 | 2>(1);
  const [recruitQuizPart, setRecruitQuizPart] = useState<1 | 2>(1);
  // 補資料. One card for every tap-sized fact about a client, reached three
  // ways: the tile (which opens it at the top, to close a gap) and the 現有客戶
  // and 重要性 cells (which open it on their own row, to change one answer).
  //
  // It absorbed the separate 客戶狀態 popup rather than sitting beside it: two
  // cards asking tap-sized questions about the same client, from controls a
  // thumb apart, is the same duplication the 建議下一步 line was criticised for.
  // `factsFocus` is what lets one card serve both errands — it is the field to
  // outline on open, or empty for "the adviser came for whatever is missing".
  //
  // A LIST, not one field. Two cells in the 2×2 report more than one answer —
  // 現有客戶 carries the tier, 家庭狀況 is 婚姻狀況 plus 子女 — and a cell that
  // reads two facts has to open on both. Outlining only the first left the
  // adviser looking at 「否・VIP」, tapping it to change the VIP half, and
  // landing on a card that had marked the other row.
  const [factsOpen, setFactsOpen] = useState(false);
  // The picker is portalled outside the bottom sheet at both responsive
  // variants (Popper with a fine pointer, Dialog with a coarse pointer). While
  // it is open, the sheet's focus trap must stand down or it immediately pulls
  // focus back to the trigger and turns every year/day tap into a no-op.
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  // 生日解鎖's payout, waiting to be taken. It is the DATE and not a flag
  // because 智能 IDEA is handed the value directly (see `openBirthdayUnlock`) —
  // the record is three round trips behind, and the button must not pay out on
  // whatever the store happens to be showing by the time it is pressed.
  //
  // It outlives the CARD and dies with the DRAWER. Clearing it in `closeFacts`
  // read as tidy and was a way to lose the feature outright: the offer can only
  // ever be earned while the client has no dob on file, so an adviser who
  // answered 生日 and then closed the card — the ✕, the backdrop, or simply
  // finishing the other three questions first — could never get it back, for
  // that client, ever. Reopening the card finds `!shownDobValue` false and no
  // standing offer, and the one thing this whole flow exists to hand over is
  // gone. Held here instead until the drawer itself moves on (the `clientId`
  // effect below clears it, and `client` going null on close counts), or until
  // it is spent, or the write it stands on comes back failed.
  const [birthdayUnlockOffer, setBirthdayUnlockOffer] = useState<string | null>(null);
  const [factsFocus, setFactsFocus] = useState<string[]>([]);
  const openFacts = (focus: string | string[] | null) => {
    setFactsFocus(focus === null ? [] : Array.isArray(focus) ? focus : [focus]);
    setFactsOpen(true);
  };
  const closeFacts = () => {
    setDobPickerOpen(false);
    setFactsOpen(false);
  };
  /** Whether one row of the card is among what the cell that opened it asked for. */
  const isFactFocused = (key: string) => factsFocus.includes(key);
  // A half-answered DISC — 外向 chosen, 邏輯/感覺 not yet — is not a letter, so
  // there is nothing to write and nothing the factsheet could hold. This keeps
  // that intermediate state for as long as it takes to answer the second
  // question. `null` means "follow whatever letter is on file".
  const [discTraits, setDiscTraits] = useState<(string | null)[] | null>(null);
  const [selectedMet, setSelectedMet] = useState(0);
  // `undefined` means "follow the store"; `null` is a band the adviser has just
  // cleared, which is why this cannot be a plain `?? tracking.opportunity`.
  const [selectedOpportunity, setSelectedOpportunity] = useState<string | null | undefined>(undefined);
  // The same echo for the two 客戶狀態 answers, and for the same reason. One tap
  // there costs three round trips before the store can answer — updateFactSheet,
  // then the GET_CLIENT_BY_ID and GET_USER its epic chains after a successful
  // write (store/client/duck.js) — so a button that waits for the store looks
  // broken for as long as all three take.
  const [selectedExisting, setSelectedExisting] = useState<ExistingClientChoice | null | undefined>(undefined);
  const [selectedTier, setSelectedTier] = useState<ClientTierChoice | null | undefined>(undefined);
  // And the same echo again for 補資料's answers, which cost the same three
  // round trips. One map rather than a state per field: the popup asks four
  // questions and the adviser is expected to answer several in a row, so what
  // matters is that every one of them lands instantly, not which one it was.
  // A key present here is an answer in flight; absent means "follow the store".
  const [factEcho, setFactEcho] = useState<Partial<Record<QuickFactKey, string | number | boolean | null>>>({});
  const [renderedClient, setRenderedClient] = useState<QueueClient | null>(client);
  const [renderedScores, setRenderedScores] = useState<AnalysisScores>(scores);
  const clientId = client ? client._id : null;

  /**
   * 業務 or 招募 — which conversation this panel is laid out for.
   *
   * The segmented selector only exists for a client marked 潛在招募, and it
   * re-orders one panel; it never hides a record. Business is the resting state: a flagged
   * client is still a client, and opening their drawer to find the policy tiles
   * gone would read as data loss rather than as a view.
   *
   * But it STICKS per client (clientViewState.ts), so an adviser working
   * through their recruitment list does not flip the same switch on every
   * record. Initialised lazily rather than in the effect below, so a client
   * already left on 招募 does not render one frame of the business panel first.
   */
  const defaultsToRecruit = (id: string | null | undefined) => Boolean(props.recruitListMode) || readsAsRecruitView(id);
  const [view, setView] = useState<DrawerView>(() => (defaultsToRecruit(clientId) ? 'recruit' : 'business'));
  // 客戶列上的雷達捷徑要開的那張評分表(見下面的 'score' 分支)。
  const [recruitScoreRequest, setRecruitScoreRequest] = useState(0);
  // Must run BEFORE the surface-request effect below: both fire in the commit
  // that selects a new client, and effects run in declaration order. Declared
  // after it, this reset closed the 招募 IDEA the row shortcut had just opened.
  useEffect(() => {
    if (clientId) {
      closeButtonRef.current?.focus();
    }
    setLastMeetingHintOpen(false);
    setMoreOpen(false);
    setFactsOpen(false);
    setDobPickerOpen(false);
    setBirthdayUnlockOffer(null);
    setFactsFocus([]);
    setDiscPlaybookOpen(false);
    setRecruitIdeaOpen(false);
    setRecruitQuizOpen(false);
    setCoverQuizOpen(false);
    setRecruitQuizPart(1);
    setCoverQuizPart(1);
    setDiscTraits(null);
    setFactEcho({});
    setView(defaultsToRecruit(clientId) ? 'recruit' : 'business');
  }, [clientId]);

  useEffect(() => {
    const request = props.recruitSurfaceRequest;
    if (!request || request.token === lastSurfaceToken.current) return;
    if (!clientId || request.clientId !== clientId) return;
    // The full condition the recruitment panel itself is rendered under
    // (`recruitView` below): the code, the view, AND the flag. Without the
    // flag the panel does not exist, so 招募 IDEA would open over the business
    // one and the radar the 'score' branch looks for would not be on the page.
    if (!props.recruitmentMode) return;
    if (!client || !adviserFactsheetOf(client, adviserEmail)?.isRecruitment) return;

    // The drawer re-reads its remembered view only when the CLIENT changes
    // (the reset effect's deps), so a request for whoever is already on screen
    // in 業務 view used to be dropped in silence — the adviser tapped 招募 IDEA
    // on that row and nothing happened. Switch the view and let this effect
    // run again; the token is deliberately not consumed yet.
    if (view !== 'recruit') {
      setView('recruit');
      rememberRecruitView(clientId, true);
      return;
    }

    lastSurfaceToken.current = request.token;
    if (request.surface === 'idea') setRecruitIdeaOpen(true);
    // 職涯測驗的對話框也是抽屜掛著的,不是派發得出去的動作 —— 今日招募那張
    // 「做完職涯測驗」卡的 CTA 之前派發 'recruit-quiz',而沒有人接,按下去什麼
    // 都不會發生。
    // Part 1 always: 今日招募's 發送職涯測驗 promises the DISC, concerns and
    // strengths only part 1 asks — not whatever part the chooser last picked.
    else if (request.surface === 'quiz') {
      setRecruitQuizPart(1);
      setRecruitQuizOpen(true);
    } else if (request.surface === 'score') {
      // 評分表本身,不只是捲到雷達那一段。列上那個雷達圖示的意思是「我要替這個
      // 候選人打分」,而把面板捲到一張還要再按一次的五角形,只答了一半 —— 而且
      // 在手機上,捲動之後看起來就像什麼都沒發生。捲動仍然留著,好讓對話框關掉
      // 之後,後面停在剛剛寫過的那一段。
      setRecruitScoreRequest(request.token);
      window.requestAnimationFrame?.(() => {
        document
          .querySelector('[data-testid="drawer-recruit-radar"]')
          ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
    }
    // 'drawer' asks for nothing beyond being here, which it now is.
  }, [props.recruitSurfaceRequest, props.recruitmentMode, client, adviserEmail, clientId, view]);

  // Declared after the reset effect above for the same reason the surface
  // request is: both fire in the commit that selects a new client.
  useEffect(() => {
    const request = props.factsRequest;
    if (!request || request.token === lastFactsToken.current) return;
    if (!clientId || request.clientId !== clientId) return;
    lastFactsToken.current = request.token;
    // Unconditional: in the commit that selects a new client the reset effect
    // above has just queued setView('recruit'), which `view` here cannot see
    // yet — and the facts card's 生日 row is only on the business view.
    setView('business');
    openFacts(request.focus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.factsRequest, clientId]);

  // What the document-level dismissal listeners need to read, kept fresh
  // without re-registering them. Assigned during render on purpose: it is a
  // ref, so it holds no stale closure and costs no commit — the listeners are
  // bound once for the component's life (see the effect below).
  const latestRef = useRef({ clientId, moreOpen, onClose });
  latestRef.current = { clientId, moreOpen, onClose };

  useEffect(() => {
    if (!client) return;
    setRenderedClient(client);
    setRenderedScores(scores);
  }, [client, scores]);

  useEffect(() => {
    if (client || !renderedClient) return undefined;
    const exitTimer = window.setTimeout(() => setRenderedClient(null), DRAWER_EXIT_MS);
    return () => window.clearTimeout(exitTimer);
  }, [client, renderedClient]);

  // A write came back failed: retract every optimistic answer, so the panel is
  // showing the record again rather than what the adviser meant to put in it.
  // Skips the first run — `factWriteFailed` starts at 0 and only a CHANGE is a
  // failure, so mounting next to a page that failed earlier retracts nothing.
  const previousWriteFailedRef = useRef(props.factWriteFailed);
  useEffect(() => {
    if (props.factWriteFailed === previousWriteFailedRef.current) return;
    previousWriteFailedRef.current = props.factWriteFailed;
    setFactEcho({});
    setSelectedExisting(undefined);
    setSelectedTier(undefined);
    setDiscTraits(null);
    // The dob went with them, so the offer standing on it has to go too:
    // pressing it would open 智能 IDEA on a birthday the record refused, over a
    // card that has already gone back to blank. Same retraction the dialog
    // itself gets in ClientV2Bridge once it is open.
    setBirthdayUnlockOffer(null);
  }, [props.factWriteFailed]);

  /**
   * Bring 生日解鎖's button into view the moment it is offered.
   *
   * 生日 is the LAST row of 認識客戶, and the card scrolls (its Box is given a
   * real height so it can). The adviser reaches the field by scrolling to the
   * bottom, taps it, and answers in a picker that covers the screen on a phone;
   * when that closes, the sheet comes back at the offset it left and ~36px of
   * new content has been inserted below the field. The button can therefore
   * appear entirely off-screen — and since it is the only route to the reward,
   * nobody would ever know it was there.
   *
   * `block: 'nearest'` so a button already visible is left alone rather than
   * yanked to an edge. Optional-called: jsdom has no `scrollIntoView`, and
   * neither do some older WebViews.
   */
  const unlockButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!birthdayUnlockOffer || !factsOpen) return;
    unlockButtonRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [birthdayUnlockOffer, factsOpen]);

  useEffect(() => {
    if (!clientId) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Innermost thing first: an open ⋯ menu takes the Escape, and only a
      // further press closes the drawer behind it. Closing both at once would
      // lose the panel the adviser was still reading.
      //
      // 補資料 is not in this chain any more. It is a portalled MUI dialog, and
      // MUI's Modal handles Escape on its own root and calls stopPropagation
      // there expressly so a listener like this one never sees it — so a branch
      // here would be unreachable, and the dialog's own onClose is what runs.
      if (moreOpen) {
        setMoreOpen(false);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [clientId, onClose, moreOpen]);

  /**
   * A tap anywhere outside the ⋯ menu closes it.
   *
   * Escape alone was not a way out: this drawer's real surface is a phone,
   * where there is no Escape key, and the other two exits both commit to
   * something — activating a row, or leaving the client. An adviser who opened
   * the menu just to see what was in it had to pick one, or read the rest of
   * the panel through it, because the menu overlays the tiles and the tracking
   * editor below it.
   *
   * `pointerdown`, not `click`: it fires before the browser moves focus, so the
   * menu is gone by the time the tap lands on whatever is underneath — the tap
   * dismisses AND does its own job, which is what a menu you dismissed by
   * looking away should do. (補資料 needs none of this: it is a portalled MUI
   * dialog, so its backdrop catches the tap and closes it, and PORTALLED_OVERLAY
   * keeps that same tap from reaching the panel underneath.)
   */
  useEffect(() => {
    if (!moreOpen) return undefined;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && moreWrapRef.current?.contains(target)) return;
      setMoreOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown as EventListener);
    return () => document.removeEventListener('pointerdown', handlePointerDown as EventListener);
  }, [moreOpen]);

  /**
   * On a phone, a tap outside the panel dismisses — where that tap does not
   * already mean something else.
   *
   * The panel is non-modal and leaves a strip of page exposed on a phone (52px,
   * 36px below 390px). That strip is deliberately live list: it lines up with
   * each row's avatar, so tapping it switches the drawer to that client. What
   * it exposes ABOVE the list is another matter — the top bar's hamburger and
   * the sliced left edge of the command bar, the stat tiles and 重點跟進, all
   * of them controls the adviser can see too little of to aim at. There the tap
   * now means 「close」, which is the exit the phone was short of: Escape wants a
   * keyboard, and the other two ways out (the header ✕, re-tapping the same
   * row) both want aim.
   *
   * A TAP, decided on pointerup, and only for a finger that stayed within
   * TAP_SLOP_PX. Deciding on pointerdown meant the panel was gone the moment a
   * finger landed — including a finger that had landed to SCROLL, which the
   * strip fully supports (the page behind is 2400px of scrollable list, there is
   * no scroll lock, and `preventDefault` on a pointerdown does not stop a
   * scroll). The adviser lost the record they were reading and the page scrolled
   * anyway. A drag now cancels the dismissal instead, as does a pointercancel —
   * which is what the browser sends when it takes the gesture over for a scroll.
   *
   * What it dismisses is innermost-first, the order Escape already uses: with
   * the 客戶狀態 popup open the tap closes THAT and leaves the panel, because
   * the popup's own scrim covers the panel only (`position: absolute` inside a
   * transformed ancestor) — so before this, a tap on the strip fell straight
   * through to whatever stat tile was underneath while the popup stayed open.
   * The ⋯ menu is the exception: it has its own outside-tap dismissal below, so
   * this one stands aside and lets it run.
   *
   * The tap is swallowed rather than passed through — capture phase, so it
   * never reaches React's root listener, plus a short window that eats the
   * click behind it. A tap on a control the adviser can only see a sliver of is
   * far likelier to have meant 「get me out of this panel」 than to have meant
   * that control.
   *
   * Phone only, and that is the whole reason the rule can afford to swallow.
   * Above 600px the page beside the 400px panel is not a sliver, it is the
   * working page: the command bar, the sort, the pagination and 重點跟進 are all
   * visible and all meant to stay usable while a client is open — that is what
   * non-modal is FOR here. Eating the first click of「sort by name」to close a
   * panel the mouse can close precisely, with a ✕ and an Escape both to hand,
   * would be taking something away to solve a problem the mouse does not have.
   * The swipe below is not gated: touch events only reach a touch device, and a
   * right-swipe across the panel means nothing else at any width.
   *
   * Bound once for the component's life, reading live values from a ref: the
   * parent re-creates onClose on every render (component.tsx), so a dependency
   * on it re-registered every listener here on every keystroke in the search
   * box.
   */
  useEffect(() => {
    /** The finger currently down, when it could still end in a dismissal. */
    let pending: { x: number; y: number; at: number; pointerId: number; intent: 'panel' | 'popup' } | null = null;

    const strayedFrom = (start: { x: number; y: number }, event: PointerEvent) =>
      Math.abs(event.clientX - start.x) > TAP_SLOP_PX || Math.abs(event.clientY - start.y) > TAP_SLOP_PX;

    /** What this touch would dismiss if it turns out to be a tap, or null. */
    const intentOf = (event: PointerEvent): 'panel' | null => {
      const live = latestRef.current;
      if (!live.clientId) return null;
      // Asked per event rather than held in state: it costs nothing here, and a
      // rotation or a resized window is answered correctly without a listener.
      if (isWiderThanPhone()) return null;
      // Secondary buttons open a context menu; that is not a dismissal.
      if (event.button > 0) return null;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || drawerRef.current?.contains(target)) return null;
      // These mean something already, in every state: a row switches client, and
      // an overlay — its surface or its backdrop — belongs to whatever the
      // adviser opened from this panel, so the tap is that overlay's to answer.
      if (target.closest(SELECTS_A_CLIENT) || target.closest(OVERLAY_OF_ITS_OWN)) return null;
      // The ⋯ menu closes itself on an outside tap (effect above); leave it to.
      if (live.moreOpen) return null;
      return 'panel';
    };

    const handlePointerDown = (event: PointerEvent) => {
      // A new gesture retires any swallow window left over from the last one.
      // The window is armed on pointerup and consumed by the click a few
      // milliseconds behind it; if that click never came — the finger lifted off
      // target, the pointer was cancelled, or an engine suppressed it — the
      // window must not still be standing when the adviser taps something else.
      swallowUntilRef.current = 0;
      const intent = intentOf(event);
      pending = intent
        ? { x: event.clientX, y: event.clientY, at: Date.now(), pointerId: event.pointerId, intent }
        : null;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!pending || event.pointerId !== pending.pointerId) return;
      if (strayedFrom(pending, event)) pending = null;
    };

    const handlePointerUp = (event: PointerEvent) => {
      const tap = pending;
      pending = null;
      if (!tap || event.pointerId !== tap.pointerId) return;
      if (strayedFrom(tap, event)) return;
      // A finger resting on the strip for a second is not asking for anything.
      if (Date.now() - tap.at > TAP_MAX_MS) return;
      swallowUntilRef.current = Date.now() + OUTSIDE_CLICK_SWALLOW_MS;
      event.preventDefault();
      event.stopPropagation();
      latestRef.current.onClose();
    };

    const handlePointerCancel = () => {
      pending = null;
    };

    /**
     * Eats the click behind a dismissing tap.
     *
     * It has to outlive the panel: the click lands after the finger lifts,
     * behind the panel's own 220ms exit, by which time the parent has dropped
     * the client and any listener tied to that would be gone — so the tap would
     * land on the control it was supposed to be taken away from.
     *
     * Exactly one click, and only inside the window, which pointerdown retires
     * anyway: the adviser's next tap is never touched, however fast it follows.
     */
    const swallowClick = (event: MouseEvent) => {
      if (!swallowUntilRef.current || Date.now() > swallowUntilRef.current) return;
      swallowUntilRef.current = 0;
      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener('pointerdown', handlePointerDown as EventListener, true);
    document.addEventListener('pointermove', handlePointerMove as EventListener, true);
    document.addEventListener('pointerup', handlePointerUp as EventListener, true);
    document.addEventListener('pointercancel', handlePointerCancel as EventListener, true);
    document.addEventListener('click', swallowClick as EventListener, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown as EventListener, true);
      document.removeEventListener('pointermove', handlePointerMove as EventListener, true);
      document.removeEventListener('pointerup', handlePointerUp as EventListener, true);
      document.removeEventListener('pointercancel', handlePointerCancel as EventListener, true);
      document.removeEventListener('click', swallowClick as EventListener, true);
    };
  }, []);

  // A gesture that starts inside something scrolling sideways belongs to that
  // thing, not to the panel. Walks up to the panel and no further.
  const startsInSideScroller = (node: EventTarget | null) => {
    let element = node instanceof Element ? node : null;
    while (element && element !== drawerRef.current) {
      if (element.scrollWidth - element.clientWidth > 4) {
        const { overflowX } = window.getComputedStyle(element);
        if (overflowX === 'auto' || overflowX === 'scroll') return true;
      }
      element = element.parentElement;
    }
    return false;
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    swipeRef.current = null;
    // A popup of its own is open: it has its own way out, and yanking the whole
    // panel from under it is not what a stray drag meant.
    if (event.touches.length !== 1 || moreOpen || factsOpen) return;
    if (startsInSideScroller(event.target)) return;
    const touch = event.touches[0];
    swipeRef.current = { x: touch.clientX, y: touch.clientY, at: Date.now() };
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    const start = swipeRef.current;
    if (!start) return;
    if (event.touches.length !== 1) {
      swipeRef.current = null;
      return;
    }
    const touch = event.touches[0];
    const driftY = Math.abs(touch.clientY - start.y);
    // The moment the finger commits to vertical this is a scroll of the body,
    // and can no longer become a dismissal however it ends.
    if (driftY > SWIPE_MAX_DRIFT_Y && driftY > Math.abs(touch.clientX - start.x)) swipeRef.current = null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const movedX = touch.clientX - start.x;
    const movedY = touch.clientY - start.y;
    // Rightward, decisively horizontal, and quick enough to read as a flick
    // rather than as a finger that wandered while resting on the panel.
    if (movedX < SWIPE_MIN_X) return;
    if (movedX <= Math.abs(movedY) * 2) return;
    if (Date.now() - start.at > SWIPE_MAX_MS) return;
    onClose();
  };

  // Pulled out of the dependency arrays below so the hooks lint can check them
  // statically — an inline `client?.trackingId?.met` is a complex expression it
  // has to give up on.
  const storedMet = client?.trackingId?.met;
  const storedOpportunity = client?.trackingId?.opportunity;

  /**
   * 會面次數: shown immediately, written once the tapping stops.
   *
   * `pendingMetRef` holds the write the last tap earned, `metTimerRef` the wait
   * it is sitting out. Kept as the whole call rather than just the number so a
   * flush can send it against the client it was tapped FOR — the drawer closing
   * is one of the things that flushes, and by then `activeClient` may already be
   * somebody else.
   */
  const pendingMetRef = useRef<(() => void) | null>(null);
  const metTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushMet = () => {
    if (metTimerRef.current) {
      clearTimeout(metTimerRef.current);
      metTimerRef.current = null;
    }
    const pending = pendingMetRef.current;
    pendingMetRef.current = null;
    pending?.();
  };

  const commitMet = (next: number) => {
    // `activeClient` is settled further down, and this only ever runs from a tap
    // on markup rendered past that point — read here, at call time, so the write
    // is aimed at whoever the drawer is actually showing.
    const target = activeClient;
    if (!target) return;
    const met = Math.min(MET_MAX, Math.max(MET_MIN, next));
    if (met === selectedMet) return;
    setSelectedMet(met);
    pendingMetRef.current = () => onTrackingMet?.(target, met);
    if (metTimerRef.current) clearTimeout(metTimerRef.current);
    metTimerRef.current = setTimeout(flushMet, MET_WRITE_DELAY_MS);
  };

  // A tap must not be lost to leaving. The cleanup runs both when the drawer
  // closes over a different client and when it unmounts, and sends whatever is
  // still waiting rather than dropping it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => flushMet, [clientId]);

  useEffect(() => {
    // Not while a tap is still waiting to be sent. React runs every cleanup
    // before any effect body, so a client change has already flushed by the time
    // this reads the ref — what it skips is only a store refresh arriving
    // mid-burst, which would otherwise pull the number back to the value the
    // adviser is in the middle of changing.
    if (pendingMetRef.current) return;
    setSelectedMet(storedMet ?? 0);
  }, [clientId, storedMet]);

  // Same local echo V1 keeps for the opportunity buttons (ClientEntry.js:227,
  // 238-253): the tap shows immediately, and the moment the store's own value
  // changes — enableTracking's GET_CLIENT_BY_ID refresh, or another client
  // being opened — the echo stands down and the store wins again.
  useEffect(() => {
    setSelectedOpportunity(undefined);
  }, [clientId, storedOpportunity]);

  // The 客戶狀態 answers stand down the same way: the moment the refreshed client
  // carries the value the adviser chose (or a different one, set from elsewhere),
  // the store is the only thing on screen again.
  const storedExisting = client ? existingClientOf(client, adviserEmail) : null;
  const storedTier = client ? tierValueOf(client, adviserEmail) : null;

  useEffect(() => {
    setSelectedExisting(undefined);
  }, [clientId, storedExisting]);

  useEffect(() => {
    setSelectedTier(undefined);
  }, [clientId, storedTier]);

  // 補資料's answers stand down too — and they were the one echo that never did.
  // `factEcho` was cleared only when the client changed or a write came back
  // failed, so on the success path a key set here outlived its own confirmation
  // and kept answering for the record for as long as the panel stayed open. A
  // write that returned 200 without persisting (a value the server declined, a
  // field it does not accept) then looked exactly like one that landed, which is
  // the failure the retraction above exists to prevent, arriving by the other
  // door; and a value set for the same client from anywhere else — the DISC
  // dialog (clientActions.tsx), V1 in another tab — stayed invisible behind it.
  //
  // The record speaks when a refreshed factsheet arrives (UPDATE_FACTSHEET's
  // epic chains GET_CLIENT_BY_ID, and GET_CLIENT_BY_ID_SUCCESS splices the
  // client back into clientData, store/client/duck.js:643). That payload is the
  // answer, whether or not it agrees, so the whole echo goes rather than only
  // the keys it happens to match.
  //
  // `factWriteInFlight` read from a ref, and only `storedFacts` in the deps: the
  // phase settles when the write returns, one round trip BEFORE the refetch it
  // chained, so triggering on the phase itself would drop the echo back to the
  // stale record for exactly as long as the echo exists to cover. Read this way
  // it does the opposite job — an unrelated list refresh landing mid-write is
  // ignored, and the echo survives to the refetch that actually answers it.
  const storedFacts = client ? adviserFactsheetOf(client, adviserEmail) : undefined;
  const factWriteInFlightRef = useRef(props.factWriteInFlight);
  factWriteInFlightRef.current = props.factWriteInFlight;

  useEffect(() => {
    if (factWriteInFlightRef.current) return;
    setFactEcho(previous => (Object.keys(previous).length ? {} : previous));
  }, [storedFacts]);

  const activeClient = client || renderedClient;
  // A drawer opened on its own (tests, deep links) still reads this client's notes.
  // Seeded above the note readers below, and above the early return (rules of hooks).
  const seededClients = useMemo(() => (activeClient ? [activeClient] : null), [activeClient]);
  useSeedClientNotes(seededClients, adviserEmail);
  const canSaveNotes = useCanEditClientNotes(clientId);
  // 你希望的服務方式, once the adviser has imported it from 保障小測驗: shown under
  // 跟進 so it is in front of them when they reach out.
  const servicePrefsLine = servicePrefsSummary(useServicePrefs(clientId), quizLangOf(intl.locale));

  /**
   * 招募路線 — 應屆畢業生 or 有經驗人士, asked HERE rather than in 招募 IDEA.
   *
   * It used to be two big buttons at the top of 招募 IDEA, which made a fact
   * about the candidate look like a mode the adviser puts the page into. It is
   * one answer of the same shape as every other question on this card, so it is
   * asked the same way and once; 招募 IDEA and the candidate's own quiz both
   * read it (RecruitIdeaPath's storage), and 建議開場 crosses it with the
   * DISC letter answered two questions below.
   *
   * An adviser note (factsheet.recruitment.path, through ../clientNotesStore),
   * like 候選人評估 and the chips — not a 補資料 field. Hence its own write path
   * rather than commitFact, and hence its absence from the card's own count,
   * which counts what the AI reads.
   */
  const recruitRoute = useRecruitIdeaPath(clientId);
  const recruitStage = useRecruitStage(clientId);

  if (!activeClient) {
    return null;
  }

  const activeScores = client ? scores : renderedScores;
  const closing = !client;
  const name = displayName(activeClient);
  const referenceNow = moment(now);
  // What is on screen: the adviser's last tap while it is still in flight,
  // otherwise whatever the store holds. The badge beside the name is derived
  // from the same value, so the chip and the popup can never disagree.
  const tierValue = selectedTier === undefined ? tierValueOf(activeClient, adviserEmail) : selectedTier;
  const tier = tierBadgeOf(tierValue);
  const tierLabel = tier ? intl.formatMessage({ id: tier.key }) : '';
  const existing = selectedExisting === undefined ? existingClientOf(activeClient, adviserEmail) : selectedExisting;
  const connection = connectionStatusOf(activeClient);
  const connected = connection.status === 'Connected';
  const cpi = cpiOf(activeClient, intl);
  const elapsed = elapsedOf(activeClient, intl, adviserEmail);
  const lastMeetingDuration = lastMeetingDurationOf(activeClient, intl, adviserEmail, referenceNow);
  const families = familiesOf(activeClient, adviserEmail);
  const tags = tagsOf(activeClient, adviserEmail, props.usedTags);
  const personality = personalityMetaOf(activeClient, adviserEmail);
  const tracking = trackingDetailOf(activeClient);
  // The band on screen: the adviser's last tap while it is still in flight,
  // otherwise whatever the store holds.
  const shownOpportunity = selectedOpportunity === undefined ? tracking?.opportunity ?? null : selectedOpportunity;
  const reminder = reminderOf(activeClient, adviserEmail, referenceNow);
  // The avatar mark. Same reference time as the 提醒 cell below, so the badge
  // and the countdown can never tell different stories.
  const remindMark = reminderMarkOf(activeClient, adviserEmail, referenceNow);
  const factsheet = adviserFactsheetOf(activeClient, adviserEmail);

  /** What a 補資料 field shows: the answer in flight, else the stored one. */
  const factValueOf = (key: QuickFactKey): string | number | boolean | null | undefined =>
    key in factEcho ? factEcho[key] : (factsheet?.[key] as string | number | boolean | null | undefined);

  // `factValueOf('dob')`, not `factsheet?.dob`: the date input the adviser
  // corrects this from already reads the echo (`shownDob` below), so reading the
  // store here left the panel's 生日 row and its countdown showing the old date
  // for the three round trips the write costs — the panel contradicting its own
  // editor, which is the thing the parseZone fix in `describeBirthday` was for.
  const shownDobValue = factValueOf('dob');
  const birthdayCountdown = daysUntilBirthday(shownDobValue as string | null | undefined, referenceNow);
  const coverageKnown = hasCoverageData(activeScores);
  // 生日 and 提醒 both land on "today" often enough that the word is worth
  // resolving once. Same id the 重點跟進 list uses, so the two never disagree.
  const todayLabel = intl.formatMessage({ id: 'clientv2-queue-today' });
  const birthday = describeBirthday(shownDobValue as string | null | undefined, referenceNow, todayLabel);

  // Per-status entitlements (clientEntitlements.ts). Every control below asks
  // `can(...)` rather than testing the status itself, so 已中斷 — or any future
  // restriction — is described in exactly one place.
  const capabilities = clientCapabilitiesOf(activeClient);
  const can = (capability: ClientCapability): boolean => capabilities.has(capability);
  // One sentence naming the status, reused as the tooltip on every control the
  // status disabled and as the copy of the notice at the top of the panel.
  // Asked about `portfolio` because that is the capability the notice is about —
  // the factsheet is adviser-owned and is never withheld (clientEntitlements.ts).
  const restrictionKey = clientRestrictionReasonOf(activeClient, 'portfolio');
  const restrictionText = restrictionKey ? intl.formatMessage({ id: restrictionKey }) : undefined;
  const lockTitle = (capability: ClientCapability): string | undefined =>
    can(capability) ? undefined : restrictionText;
  const canEditProfile = can('edit-profile');
  /**
   * Run `allow` when the status permits the capability, otherwise report the
   * refusal. Every control the entitlements can lock goes through this, so
   * "what happens on a locked tap" is decided once.
   */
  /**
   * Writes the DISC fact the way the header's own picker does. Returns whether it
   * was allowed, so the quiz sync can say 已填入性格類型 only when it was.
   */
  const changePersonality = (letter: string): boolean => {
    if (!can('edit-profile')) {
      onRestricted?.(activeClient, 'edit-profile');
      return false;
    }
    setDiscTraits(TRAITS_FROM_DISC[letter] || [null, null]);
    commitFact('personality', letter);
    return true;
  };

  const guard = (capability: ClientCapability, allow: () => void) => () => {
    if (can(capability)) {
      allow();
      return;
    }
    onRestricted?.(activeClient, capability);
  };

  /**
   * Is this person marked 潛在招募 — read through the echo, so answering 否
   * inside 了解這位候選人 closes the view on the tap rather than three round
   * trips later.
   */
  const isRecruitment = Boolean(factValueOf('isRecruitment'));

  /**
   * Is 招募模式 unlocked for this adviser at all? Everything recruitment in
   * this panel hangs off this one boolean — see the `recruitmentMode` prop.
   */
  const recruitmentUnlocked = Boolean(props.recruitmentMode);

  /**
   * The 招募 panel renders only while the flag is on. Clearing the flag is the
   * one answer that can pull the view out from under the adviser, and it is
   * meant to: 招募 view describes a recruitment conversation, and 否 says
   * there isn't one. Derived rather than a second piece of state, so the flag
   * and the layout cannot disagree for a frame.
   *
   * The gate is folded in HERE rather than at each of the panel's own blocks:
   * this is already the single truth the whole 招募 layout reads, so a locked
   * adviser and an unflagged client take the same, well-travelled path back to
   * 業務. A remembered 'recruit' view from before the code was revoked is
   * harmless for the same reason.
   */
  const recruitView = recruitmentUnlocked && view === 'recruit' && isRecruitment;

  // 潛在招募's label, in two pieces so the badge can be aligned to the words —
  // see the switch itself for why the split lives here and not in the message.
  // Split on the character rather than on position or a trailing-pictograph
  // regex: it is the same 💼 in all three locales, and a locale that ever drops
  // it falls back to printing the label whole rather than to an empty span.
  const recruitLabel = intl.formatMessage({ id: 'recruit-lead' });
  const recruitBadgeAt = recruitLabel.indexOf(RECRUIT_BADGE);
  const recruitLabelText = recruitBadgeAt < 0 ? recruitLabel : recruitLabel.slice(0, recruitBadgeAt).trimEnd();
  const recruitLabelBadge = recruitBadgeAt < 0 ? '' : RECRUIT_BADGE;

  /** Switch view, and remember it for the next time this client is opened. */
  const chooseView = (next: DrawerView) => {
    setView(next);
    rememberRecruitView(clientId, next === 'recruit');
  };

  /**
   * The three 招募 tools that are borrowed rather than built here, and so carry
   * their own campaign-code gates. Everything else the drawer offers is the
   * page's own and answers only to the client's status.
   */
  const recruitToolAvailable = (action: ClientDrawerAction): boolean => {
    if (action === 'team-hub') return Boolean(props.teamHubAvailable);
    if (action === 'team-bot') return Boolean(props.teamBotAvailable);
    if (action === 'ai-coach') return Boolean(props.aiCoachAvailable);
    return true;
  };

  /**
   * How far this client is from a C.P.I. band, and which field is the cheapest
   * way there. Drives the 補資料 tile's caption — the tile says what answering
   * BUYS rather than what it costs, because "3 fields missing" is a chore and
   * "one tap and this client is ranked" is an offer.
   */
  // The gap sees the in-flight answers too, so the countdown inside the popup
  // and the tile that opened it both move on the tap rather than three round
  // trips later.
  const cpiGap = cpiGapOf(activeClient, { adviserEmail, adviserPersonality, overrides: factEcho });

  /**
   * 生日 is the one answer that pays for itself on the spot: 智能 IDEA derives
   * the birthday number, the soul number, the star sign and the age from a dob
   * and nothing else (SmartIdeaDialog.js), so the field an adviser has the
   * least reason to fill is also the one with a finished reward already sitting
   * behind it. Nothing said so, and nothing showed it — the answer went into
   * the record and what came back was a countdown.
   *
   * OFFERED, not sprung. This used to run straight off `commitFact`: the
   * adviser pressed OK on the date picker and the 補資料 card they were working
   * through was pulled out from under them and replaced by 智能 IDEA. Answering
   * one question is not a request to leave — the card asks four, and the reward
   * arriving as an eviction reads as the popup malfunctioning rather than as
   * something being unlocked (reported from the app, 2026-09-02: 「the ux is so
   * weird」). So the payout now waits behind a button in the 生日 row, and this
   * runs when that button is pressed. Everything downstream is unchanged —
   * including closing the card, which is correct once leaving is what was
   * asked for.
   *
   * Opened with the answer PATCHED IN rather than with `activeClient` as it
   * stands. That dialog reads the client it was HANDED (`smartIdeaDialog.client`,
   * SmartIdeaDialog.js) and not the store, and this write costs three round
   * trips before the store could answer — so the unpatched client would have
   * opened the reward on the empty state the adviser has just left behind.
   * Patching the root `factsheetId` is what survives the hop: that is the field
   * `actionableClientOf` resolves through `adviserFactsheetOf`, which prefers
   * the root document on any live payload.
   *
   * Only the FIRST dob. Correcting a date is not a discovery, and a dialog that
   * reopens over a correction reads as a misfire. Never on a clear. That rule
   * now decides whether the OFFER appears (`commitFact`), which is the same
   * once-per-client shape it always had.
   *
   * The `can` test is a backstop rather than a live branch — the 補資料 tile is
   * already withheld from a 已中斷 client (`cpiGap.rankable`), so the input this
   * runs from cannot be reached without it. Silent rather than `guard`ed: this
   * is a reward the adviser never pressed, and a locked-feature toast answers a
   * question they did not ask.
   */
  const openBirthdayUnlock = (dob: string) => {
    if (!onAction || !can('smart-idea')) return;
    // No factsheet id, no write: ClientV2Bridge drops the answer on exactly
    // this condition (`handleEditFact`), so opening the reward would pay out a
    // birthday that never left the screen — and 智能 IDEA would then be handed a
    // dob the record does not have.
    if (!factsheet?._id) return;
    // Spent. This is what `closeFacts` used to do on the way past, and it has to
    // be said here now that the offer outlives the card: without it, coming back
    // to 補資料 after taking the reward would find the button still standing.
    setBirthdayUnlockOffer(null);
    closeFacts();
    onAction('smart-idea', { ...activeClient, factsheetId: { ...(factsheet || {}), dob } });
    // What the `factsheet?._id` guard above cannot cover. It only rules out the
    // write that was never SENT; a dob that goes out and then fails — timeout,
    // 401, a 200 saying `success: false` — leaves 智能 IDEA deriving a star
    // sign, a birthday number and a soul number from a date the record does not
    // have, and it has no reason to close on its own. Worse than a stale panel:
    // the drawer behind it retracts the date on the same failure, so the two
    // surfaces contradict each other while a toast says the answer did not save.
    // Told here rather than gated on success, because the reward landing
    // instantly is the entire point of the unlock — taking it back on the rare
    // failure costs nothing the adviser was enjoying.
    props.onBirthdayUnlock?.();
  };

  /** Whether 生日解鎖 has anything to pay out at all — the same three tests
   *  `openBirthdayUnlock` makes, asked before the offer is put on screen so a
   *  button never appears that would do nothing when pressed. */
  const canUnlockBirthday = Boolean(onAction && can('smart-idea') && factsheet?._id);

  /** Write one answer, and show it immediately. */
  const commitFact = (key: QuickFactKey, value: string | number | boolean | null) => {
    setFactEcho(previous => ({ ...previous, [key]: value }));
    onEditFact?.(activeClient, key, value);
    if (key !== 'dob') return;
    const dob = typeof value === 'string' && value ? value : null;
    setBirthdayUnlockOffer(previous => {
      // A clear takes the offer back with the date: the button must never open
      // 智能 IDEA on a birthday the card no longer shows.
      if (!dob) return null;
      // Fixing a slip is not a second unlock, and it is not spending the first
      // one either — the offer follows the corrected date instead of vanishing
      // because the adviser mistyped the year before pressing the button.
      if (previous) return dob;
      // Otherwise the original rule: only the FIRST dob this client has, and
      // only where there is something to pay out.
      return !shownDobValue && canUnlockBirthday ? dob : null;
    });
  };

  /**
   * One answer, as `FactToggle` wants it. A glyph where `CHOICE_ICON` has one,
   * otherwise the word — and where it is a glyph, the word comes along as the
   * `aria-label`, since a button with no text has no name without it.
   *
   * No tick any more: the sliding pill under the chosen answer is what says
   * "this one", and a tick riding on top of it would be saying it twice.
   *
   * Shared by all three question shapes below, so 現有客戶's 是 / 否,
   * 潛在招募's, and DISC's two axes are the same control — which they read as.
   */
  const factChoiceOption = (value: string, labelId: string): FactToggleOption => {
    const Icon = CHOICE_ICON[labelId];
    const label = intl.formatMessage({ id: CHOICE_LABEL[labelId] ?? labelId });
    return Icon ? { value, label: <Icon size={15} aria-hidden="true" />, ariaLabel: label } : { value, label };
  };

  /**
   * One closed-answer question on the quick card. Shared by both halves, so a
   * question about the client and a question about where they sit in the book
   * are answered with the same gesture and look the same doing it.
   */
  const renderFactGroup = (field: (typeof FACT_CHOICES)[number], section: 'know' | 'sort') => {
    const current = factValueOf(field.key);
    const value = current === null || current === undefined ? '' : String(current);
    return (
      <div
        key={field.key}
        className={`clientv2-statusgroup${isFactFocused(field.key) ? ' is-focus' : ''}`}
        data-testid="drawer-facts-group"
        data-section={section}
        data-field={field.key}
        data-value={value}
      >
        {factIcon(field.key)}
        <div className="clientv2-statusgroup-body">
          <div className="clientv2-statusgroup-k">
            {intl.formatMessage({ id: field.labelKey })}
            {!HINTLESS_FACTS.has(field.key) && (
              <span className="clientv2-facts-hint">
                {intl.formatMessage({ id: `clientv2-facts-hint-${field.key}` })}
              </span>
            )}
            {value !== '' && (
              <button
                type="button"
                className="clientv2-statusgroup-clear"
                data-testid={`drawer-facts-clear-${field.key}`}
                onClick={() => commitFact(field.key, null)}
              >
                {intl.formatMessage({ id: 'Clear' })}
              </button>
            )}
          </div>
          <FactToggle
            optionTestId="drawer-facts-option"
            dataField={field.key}
            ariaLabel={intl.formatMessage({ id: field.labelKey })}
            value={value === '' ? null : value}
            // 子女 is already a numeral in every locale, so it is neither a
            // message id nor a candidate for a glyph; the rest go through the
            // shared option above — 潛在招募's two resolving to 是 / 否, and so
            // to ✓ / ✕.
            options={field.choices.map(choice =>
              field.key === 'child'
                ? { value: choice, label: choice }
                : factChoiceOption(choice, field.boolean ? BOOL_LABEL[choice] : choice)
            )}
            // Three answer shapes, one control: 子女 is the number the C.P.I.
            // reads, 潛在招募 is a flag, everything else is the string itself.
            // `null` arrives when the answer already on file is tapped again,
            // which is the only way back to "never answered".
            onChange={next =>
              commitFact(
                field.key,
                next === null ? null : field.boolean ? next === 'true' : field.key === 'child' ? Number(next) : next
              )
            }
          />
        </div>
      </div>
    );
  };

  // 生日 as the date input wants it, off the same `shownDobValue` the panel's own
  // 生日 readout is built from — one value, so the readout and the control that
  // edits it can never name different days.
  // `parseZone` for the same reason `describeBirthday` uses it: the stored dob is
  // UTC midnight, and re-reading it in the browser's zone would put a date input
  // — the control the adviser CORRECTS the record from — a day behind the record
  // itself west of UTC. It would also have disagreed with V1's own editor of the
  // same field, which is the comparison a parallel V1/V2 rollout invites.
  const shownDob = shownDobValue ? moment.parseZone(shownDobValue as string).format('YYYY-MM-DD') : '';

  // The two DISC halves on screen: whatever the adviser has tapped so far,
  // otherwise the pair the current letter decomposes into. A letter is only ever
  // written once BOTH are answered, so a half-answer lives here and nowhere
  // else.
  const shownTraits: (string | null)[] = discTraits ||
    TRAITS_FROM_DISC[factValueOf('personality') as string] || [null, null];
  const discLetter = DISC_FROM_TRAITS[`${shownTraits[0]}|${shownTraits[1]}`] || '';

  /**
   * The question's mark, in the slot the label row was already sized for.
   *
   * DISC takes the one this panel already gives it: the dot from the 客戶檔案
   * cell above, which switches colour with the letter (discColorOf). It is the
   * same mark for the same fact, and it answers itself as the two halves land —
   * grey while the letter is incomplete, the letter's own hue once it resolves.
   * Its chip stays the quiet indigo so the dot inside it is the thing carrying
   * colour; every other question gets a fixed glyph in a tone of its own.
   *
   * Decorative in the strict sense: the question's name sits right beside it and
   * says the same thing in words, so announcing this as well would say it twice.
   */
  const factIcon = (key: string) => {
    if (key === 'personality') {
      return (
        <span className="clientv2-infoicon indigo" aria-hidden="true">
          <i className="clientv2-dot" style={{ backgroundColor: discColorOf(discLetter) }} />
        </span>
      );
    }
    const spec = FACT_ICON[key];
    if (!spec) return null;
    const Icon = spec.icon;
    return (
      <span className={`clientv2-infoicon ${spec.tone}`} aria-hidden="true">
        <Icon size={14} />
      </span>
    );
  };

  /**
   * The 認識客戶 questions still unanswered — what the tile counts, and how long
   * it lives.
   *
   * NOT the C.P.I. plan it used to count. The plan is the cheapest route to a
   * band, which is a different set from "what the AI still does not know": it
   * skips 性別 entirely (unscored, but in the prompt) and stops the moment the
   * threshold is crossed. A card showing five questions under a badge reading
   * "3" was counting something the adviser could not see, and a tile that
   * vanished on the ranking left the AI half-briefed with nothing left to say
   * so. Counting the visible questions makes the badge, the countdown and the
   * tile's own lifetime one number.
   *
   * DISC only counts where it can be answered — an adviser without their own
   * letter is never shown that question (see the card).
   */
  const unansweredKnow = KNOW_FIELDS.filter(key => {
    if (key === 'personality' && !adviserPersonality) return false;
    const value = key === 'personality' ? discLetter : factValueOf(key);
    return value === null || value === undefined || value === '';
  });

  /**
   * How many of the 認識客戶 questions this adviser can be asked at all — the
   * denominator behind the tile's ring. It tracks the same DISC exclusion the
   * filter above applies, so an adviser without their own letter sees a ring
   * out of four rather than one that can never fill.
   */
  const knowAskable = KNOW_FIELDS.filter(key => key !== 'personality' || adviserPersonality).length;
  const knowAnswered = knowAskable - unansweredKnow.length;

  /**
   * 了解這位候選人's unanswered questions — what its tile counts, and how long
   * that tile lives. The business counter above cannot be reused: it counts the
   * five the AI reads and stops at the C.P.I. threshold, and neither of those
   * is what a career conversation is missing.
   *
   * DISC counts here whatever the adviser's own letter is, unlike above. That
   * exclusion exists because the C.P.I. scores the axis only when BOTH letters
   * are set, so asking would promise a ranking the tap cannot pay for. This
   * view promises no ranking: it asks how to open the conversation, and the
   * candidate's own letter answers that by itself.
   */
  /**
   * Where the header's DISC readout goes, which depends on the conversation.
   *
   * 業務 — 智能 IDEA, marked on its 性格與溝通 card, PRO-gated, as before.
   * 招募 — the recruitment playbook (DiscTips, mode="recruit"): four lines about how to
   *        open a career conversation with this letter. No gate: it generates
   *        nothing, so there is nothing to charge for.
   *
   * With no letter on file, 招募 sends the tap to the picker that sets one
   * rather than opening a card with nothing in it. The ⓘ beside the cell does
   * the same thing, and two doors onto the one useful action beats one door
   * onto a dead end.
   */
  const openDiscReadout = () => {
    if (!recruitView) {
      guard('smart-idea-disc', () => onAction?.('smart-idea-disc', activeClient))();
      return;
    }
    if (!discLetter) {
      guard('edit-profile', () => onEditPersonality?.(activeClient))();
      return;
    }
    setDiscPlaybookOpen(true);
  };

  const recruitUnanswered = RECRUIT_FACT_FIELDS.filter(key => {
    if (key === 'recruitRoute') return !recruitRoute;
    if (key === 'recruitStage') return !recruitStage;
    const value = key === 'personality' ? discLetter : factValueOf(key as QuickFactKey);
    return value === null || value === undefined || value === '';
  });

  /**
   * Answer one half of DISC. Writes only when both halves are known — and when
   * an answer is taken back, clears the stored letter, because half a letter is
   * not a weaker answer, it is no answer.
   */
  const commitDiscTrait = (index: number, choice: string | null) => {
    const next = index === 0 ? [choice, shownTraits[1]] : [shownTraits[0], choice];
    setDiscTraits(next);
    const letter = DISC_FROM_TRAITS[`${next[0]}|${next[1]}`] || '';
    if (letter) {
      commitFact('personality', letter);
    } else if (discLetter) {
      commitFact('personality', null);
    }
  };

  /**
   * 現有客戶 and 重要性 — the two facts that rank a client against the rest of
   * the book, and the only two the adviser sets from this panel.
   *
   * One spec drives both the cell and the popup, so the readout can never name
   * a different set of answers from the one the popup offers.
   */
  const statusPicks: {
    key: StatusPickKey;
    testId: string;
    optionTestId: string;
    label: string;
    icon: typeof CircleCheck;
    tone: string;
    value: string | null;
    dot?: string;
    choices: readonly string[];
    enabled: boolean;
    commit: (choice: string | null) => void;
  }[] = [
    {
      key: 'existing',
      testId: 'drawer-existing',
      optionTestId: 'drawer-existing-option',
      label: intl.formatMessage({ id: 'clientv2-existing' }),
      icon: CircleCheck,
      tone: 'emerald',
      value: existing,
      dot: existing ? EXISTING_DOT[existing] : undefined,
      choices: EXISTING_CHOICES,
      enabled: Boolean(onEditExisting),
      commit: choice => {
        setSelectedExisting(choice as ExistingClientChoice | null);
        onEditExisting?.(activeClient, choice as ExistingClientChoice | null);
      }
    },
    {
      key: 'tier',
      testId: 'drawer-tier-pick',
      optionTestId: 'drawer-tier-option',
      label: intl.formatMessage({ id: 'Importance' }),
      icon: Star,
      tone: 'rose',
      value: tierValue,
      dot: tierValue ? TIER_DOT[tierValue] : undefined,
      choices: CLIENT_TIER_CHOICES,
      enabled: Boolean(onEditTier),
      commit: choice => {
        setSelectedTier(choice as ClientTierChoice | null);
        onEditTier?.(activeClient, choice as ClientTierChoice | null);
      }
    }
  ];

  /**
   * 業務's two readouts — the same 2×2 shape 招募 uses, and the same trick.
   *
   * 現有客戶 carries the tier with it (「是・VIP」) instead of the tier having a
   * cell of its own. It is the move 家庭狀況 already makes with 婚姻狀況 + 子女:
   * two answers that are only ever read together, on one line.
   *
   * 普通 is omitted rather than printed — `tierBadgeOf` returns nothing for it,
   * which is the same reason the chip beside the client's name stays away for a
   * 普通 client. It is the resting value, and 「是・普通」 spends half a cell
   * saying that nothing is unusual. All three ranks stay fully editable on the
   * card this cell opens; what changes is only what the readout reports.
   *
   * The cell that frees up goes to 家庭狀況, which 業務 has never shown — the
   * card asks for both answers and three AI features read them, and until now
   * the panel reported neither back.
   *
   * `statusPicks` above is untouched: it still drives BOTH rows in the popup, so
   * 重要性 keeps its own editor exactly as before.
   */
  /**
   * 家庭狀況 — 婚姻狀況 and 子女 on one line, read by BOTH views.
   *
   * 招募 had it first. 業務 shows it too as of this change, in the cell 重要性
   * used to hold: the card has always asked for both answers and three AI
   * features read them, but the business panel never reported either back.
   *
   * Declared here rather than down with the 招募 readouts because `businessPicks`
   * below needs it — the 招募 block is further down the function.
   */
  const marriageValue = factValueOf('marriageStatus');
  const childValue = factValueOf('child');
  const childCount = typeof childValue === 'string' ? Number(childValue) : childValue;
  const hasChildCount = typeof childCount === 'number' && Number.isFinite(childCount) && childCount >= 0;
  const familyStatus = [
    marriageValue ? intl.formatMessage({ id: String(marriageValue) }) : null,
    hasChildCount ? intl.formatMessage({ id: 'clientv2-recruit-child-count' }, { count: childCount }) : null
  ].filter(Boolean);
  const familyStatusText = familyStatus.length > 0 ? familyStatus.join('・') : '—';

  const businessStatus = [
    existing ? intl.formatMessage({ id: existing }) : null,
    // '' for 普通 and for an unset tier, so `filter` drops it.
    tierLabel || null
  ].filter(Boolean);
  const businessPicks: {
    key: string;
    /**
     * Every row the card should outline when this cell opens it — one per fact
     * the cell reports. A cell that reads two answers and marks one of them is
     * pointing at the wrong question half the time it is tapped.
     */
    focus: string[];
    testId: string;
    label: string;
    value: string;
    filled: boolean;
    enabled: boolean;
    dot?: string;
  }[] = [
    {
      key: 'existing',
      focus: ['existing', 'tier'],
      testId: 'drawer-existing',
      label: intl.formatMessage({ id: 'clientv2-existing' }),
      value: businessStatus.length > 0 ? businessStatus.join('・') : '—',
      filled: businessStatus.length > 0,
      // Either editor: the cell now stands for two answers and the card it
      // opens carries a row for each, so an adviser who can set the tier must
      // be able to get in even where 現有客戶 itself is not wired up.
      enabled: Boolean(onEditExisting || onEditTier),
      // 現有客戶's own answer colour. The tier rides behind it, so the dot keeps
      // reporting the question the cell is named for rather than trying to
      // colour two facts at once.
      dot: existing ? EXISTING_DOT[existing] : undefined
    },
    {
      key: 'marriageStatus',
      // Same rule: this cell prints 婚姻狀況 and 子女 as one line, so both rows
      // come up marked.
      focus: ['marriageStatus', 'child'],
      testId: 'drawer-family-status',
      label: intl.formatMessage({ id: 'clientv2-recruit-family-status' }),
      value: familyStatusText,
      filled: familyStatus.length > 0,
      enabled: Boolean(onEditFact)
    }
  ];

  /**
   * 認識候選人's four readouts. Two are direct facts, one combines the two
   * family answers, and 認識途徑 prefers the named introducer over the broader
   * channel/source already on file.
   *
   * Readouts only — every one of them opens that card marked on its own
   * question rather than editing in place, which is the trade the whole panel
   * makes (see `statusPicks` above): the answer is read far more often than it
   * is changed, and half a drawer is not enough width for the controls.
   *
   * 現時月入 is printed with thousands separators and nothing else. No currency
   * symbol: the factsheet stores a bare number and the adviser's own currency
   * is a page-level setting this panel does not read, so a symbol here would be
   * asserting a unit nobody chose.
   */
  const recruitIncome = factValueOf('monthlyIncome');
  const recruitIncomeNumber = typeof recruitIncome === 'string' ? Number(recruitIncome) : recruitIncome;
  // Resolved to a number or nothing ONCE, because two surfaces read it: this
  // panel's 現時月入 cell and 招募 IDEA, which is opened from a tile four inches
  // away and used to sample the figure instead. One value is what stops the
  // dialog and the panel underneath it naming two different salaries for the
  // same person while the candidate is looking at the screen.
  const recruitIncomeValue =
    typeof recruitIncomeNumber === 'number' && Number.isFinite(recruitIncomeNumber) ? recruitIncomeNumber : null;
  const recruitOccupation = factValueOf('occupation');
  const recruitPicks: {
    key: QuickFactKey;
    /** The rows this cell opens the card on — see `businessPicks.focus`. */
    focus: string[];
    label: string;
    value: React.ReactNode;
    filled: boolean;
  }[] = [
    {
      key: 'occupation',
      focus: ['occupation'],
      label: intl.formatMessage({ id: 'Occupation' }),
      value: recruitOccupation || '—',
      filled: Boolean(recruitOccupation)
    },
    {
      key: 'marriageStatus',
      // 婚姻狀況 and 子女 on one line, so the card marks both rows.
      focus: ['marriageStatus', 'child'],
      label: intl.formatMessage({ id: 'clientv2-recruit-family-status' }),
      value: familyStatusText,
      filled: familyStatus.length > 0
    }
  ];

  // 建議下一步, in three rungs. The queue's dated signal wins; failing that, a
  // cross-sell gap in a client who has already bought; failing that, a client
  // holding none of the tracked categories gets pointed at a conversation.
  //
  // All three read policy figures, so all three require the server to have SENT
  // them — for a 已中斷 client every aggregate is withheld and every predicate
  // reads "holds nothing", which would make the third rung fire on a client
  // whose portfolio might be full. They get no line, which is the only honest
  // thing to say about figures nobody sent.
  const policiesVisible = policyFiguresVisible(activeClient);
  const coverageGap = signal || !policiesVisible ? null : crossSellGapOf(activeClient);
  const noCover = !signal && policiesVisible && !holdsAnyTrackedCategory(activeClient);
  const suggestionSpec = signal
    ? SUGGESTION_BY_SIGNAL[signal]
    : coverageGap
    ? { ...COVERAGE_GAP_SUGGESTION, copyKey: 'clientv2-signal-coverage-gap' }
    : noCover
    ? NO_COVER_SUGGESTION
    : null;
  // The queue's copy is used verbatim, so the drawer and the 重點跟進 list
  // describe the same client in the same words.
  /**
   * The tracking record's editor. It sits directly under the 客戶與跟進 row —
   * under the 進度跟進 tile that switches it on — rather than in the pinned area
   * above the tile grid, where it split the promoted cards from the controls
   * with an editor for something neither of them did.
   *
   * A status that cannot edit tracking does not get it, matching V1, which
   * forces the 進度跟進 icon to INACTIVE for a disconnected client.
   */
  const trackingBar =
    tracking && can('tracking') ? (
      <div
        className="clientv2-trackbar"
        data-testid="tracking-detail"
        // The whole bar is tinted by the band, so it has to follow the tap
        // too — not just the button.
        style={{ backgroundColor: `${tracking.colorFor(shownOpportunity)}28` }}
      >
        <div className="clientv2-trackbar-row">
          <span className="clientv2-trackbar-k">{intl.formatMessage({ id: 'Opportunity' })}</span>
          {OPPORTUNITY_LEVELS.map(level => {
            const on = shownOpportunity === level;
            return (
              <button
                key={level}
                type="button"
                data-testid="tracking-opportunity"
                className="clientv2-oppbtn"
                aria-pressed={on}
                disabled={!onTrackingOpportunity}
                style={{
                  backgroundColor: on ? tracking.colorFor(level) : '#cccccc',
                  color: on ? '#fff' : '#000'
                }}
                onClick={() => {
                  // V1's OpportunityControl toggles the active one off by
                  // sending null (OpportunityControl.js:22).
                  const next = on ? null : level;
                  setSelectedOpportunity(next);
                  onTrackingOpportunity?.(activeClient, next);
                }}
              >
                {intl.formatMessage({ id: `opp${level}` })}
              </button>
            );
          })}
          <span className="clientv2-trackbar-k clientv2-trackbar-k-gap">
            {intl.formatMessage({ id: 'Expected-biz' })}
          </span>
          <span className="clientv2-trackbar-v">{formatBusinessVolume(tracking.businessVol) ?? '—'}</span>
        </div>
        <div className="clientv2-trackbar-row">
          <span className="clientv2-trackbar-k">{intl.formatMessage({ id: 'Day' })}</span>
          {/* Grouped, because a tracking run old enough to reach four digits is
              exactly the one an adviser reads at a glance and 2725 has to be
              counted. Pinned to en-US for the same reason 預期生意 beside it is
              (formatBusinessVolume): all three locales this ships in group by
              thousands with a comma, and pinning keeps the two numbers on this
              bar formatted the same way whatever the device is set to. */}
          <span className="clientv2-trackbar-v" data-testid="tracking-days">
            {tracking.days.toLocaleString('en-US')}
          </span>
          {/* `clientv2-trackbar-met`, not the app-wide `Met`: V1's own tracking
              row prints that same id (ClientEntry.js), and 「Met」 on its own
              reads as a verb rather than a count. Renaming the shared id would
              rename V1's row too, which nobody asked for. */}
          <span className="clientv2-trackbar-k clientv2-trackbar-k-gap">
            {intl.formatMessage({ id: 'clientv2-trackbar-met' })}
          </span>
          {/* 「開完會 +1」 is what this control is for, and a select made that
              three actions — open, scroll the OS sheet, choose — with the sheet
              covering the drawer while you did it. Two buttons make the common
              answer one tap and a correction one more, which is also how this
              same field is edited everywhere else in the app (MetControl, in
              MeetingDialog and TrackOffDialog). The pattern is borrowed, not the
              component: that one carries V1's MUI button styling, which would
              not sit beside the L/M/H pills on the row above. */}
          <div
            className="clientv2-metstep"
            data-testid="tracking-met"
            role="group"
            aria-label={intl.formatMessage({ id: 'clientv2-trackbar-met' })}
          >
            <button
              type="button"
              data-testid="tracking-met-down"
              className="clientv2-metstep-btn"
              aria-label={intl.formatMessage({ id: 'clientv2-met-fewer' })}
              disabled={!onTrackingMet || selectedMet <= MET_MIN}
              onClick={() => commitMet(selectedMet - 1)}
            >
              <Minus size={13} aria-hidden="true" />
            </button>
            {/* `aria-live`, because the buttons keep focus across a tap: without
                it the count changes with nothing announced. */}
            <span className="clientv2-metstep-v" data-testid="tracking-met-value" aria-live="polite">
              {selectedMet}
            </span>
            <button
              type="button"
              data-testid="tracking-met-up"
              className="clientv2-metstep-btn"
              aria-label={intl.formatMessage({ id: 'clientv2-met-more' })}
              disabled={!onTrackingMet || selectedMet >= MET_MAX}
              onClick={() => commitMet(selectedMet + 1)}
            >
              <Plus size={13} aria-hidden="true" />
            </button>
          </div>
          {/* Bottom right, not top right. `margin-left: auto` puts it at the end
              of whichever row it sits in, and the top one had already run out of
              width — 機會 + L/M/H + 預期生意 + the figure overflowed a 330px
              drawer and pushed the gear off the edge, which is a control the
              adviser cannot then reach at all. The lower row is the shorter of
              the two, so the corner it anchors to is one that exists. */}
          <button
            type="button"
            data-testid="tracking-summary"
            className="clientv2-trackbar-gear"
            disabled={!onAction}
            aria-disabled={!can('tracking-summary') || undefined}
            title={lockTitle('tracking-summary')}
            aria-label={intl.formatMessage({ id: 'clientv2-tracking-summary' })}
            onClick={guard('tracking-summary', () => onAction?.('tracking-summary', activeClient))}
          >
            <Settings size={15} aria-hidden="true" />
          </button>
        </div>
      </div>
    ) : null;

  const suggestion = suggestionSpec && {
    // The gap line names the category; the queue's five carry no placeholder,
    // and react-intl leaves the extra value unused.
    copy: intl.formatMessage(
      { id: suggestionSpec.copyKey },
      { category: coverageGap ? intl.formatMessage({ id: `clientv2-axis-${coverageGap}` }) : '' }
    ),
    color: suggestionSpec.color,
    tint: suggestionSpec.tint,
    label: intl.formatMessage({ id: suggestionSpec.labelKey }),
    capability: suggestionSpec.capability,
    // An A.I. suggestion without FEATURE_GPT / Ultra: shown greyed like the A.I.
    // card, and the tap offers the upgrade (clientActions 'ai').
    featureLocked:
      (suggestionSpec.action === 'ai' || suggestionSpec.action === 'ai-recruit') && props.aiAvailable === false,
    // The birthday greeting is a callback, not a dispatched action, so the two
    // paths disable on different props.
    disabled: suggestionSpec.kind === 'birthday' ? !onBirthday : !onAction,
    run: () => {
      if (suggestionSpec.kind === 'birthday') {
        onBirthday?.(activeClient);
        return;
      }
      if (suggestionSpec.action) onAction?.(suggestionSpec.action, activeClient);
    }
  };

  /**
   * 招募模式的下一步沿用同一條建議列，但只根據共用的關係跟進資料：
   * 下次會面和提醒。過期項目排在未來項目前面；同一狀態則較早日期
   * 優先。沒有安排時也保留一個明確入口，因為會面筆記正是設定這
   * 兩個日期及記錄招募對話的現有位置。
   */
  const recruitNextEvents: { kind: 'meeting' | 'reminder'; date: moment.Moment }[] = [];
  const nextMeeting = factsheet?.nextMeetingDate ? moment.parseZone(factsheet.nextMeetingDate) : null;
  if (nextMeeting?.isValid()) recruitNextEvents.push({ kind: 'meeting', date: nextMeeting.startOf('day') });
  const nextReminder =
    factsheet?.fsReminderActive && factsheet.fsReminderDate ? moment.parseZone(factsheet.fsReminderDate) : null;
  if (nextReminder?.isValid()) recruitNextEvents.push({ kind: 'reminder', date: nextReminder.startOf('day') });
  recruitNextEvents.sort((left, right) => left.date.valueOf() - right.date.valueOf());
  const recruitNextEvent = recruitNextEvents[0];
  const recruitNextOverdue = Boolean(
    recruitNextEvent && recruitNextEvent.date.isSameOrBefore(referenceNow.clone().startOf('day'))
  );
  const recruitNextCopyKey = !recruitNextEvent
    ? 'clientv2-recruit-next-empty'
    : recruitNextEvent.kind === 'reminder'
    ? recruitNextOverdue
      ? 'clientv2-recruit-next-reminder-due'
      : 'clientv2-recruit-next-reminder'
    : recruitNextOverdue
    ? 'clientv2-recruit-next-meeting-due'
    : 'clientv2-recruit-next-meeting';
  const recruitSuggestion = {
    copy: intl.formatMessage(
      { id: recruitNextCopyKey },
      { date: recruitNextEvent ? recruitNextEvent.date.format('D MMM YYYY') : '' }
    ),
    color: recruitNextOverdue ? '#dc2626' : '#d97706',
    tint: recruitNextOverdue ? '#fee2e2' : '#fff7ed',
    label: intl.formatMessage({ id: 'meetingNotes' }),
    capability: 'meeting-note' as ClientCapability,
    disabled: !onAction,
    run: () => onAction?.('meeting-note', activeClient),
    signal: !recruitNextEvent
      ? 'recruit-plan-next'
      : `recruit-${recruitNextEvent.kind}${recruitNextOverdue ? '-overdue' : ''}`
  };
  const shownSuggestion = recruitView ? recruitSuggestion : suggestion;

  return (
    <aside
      data-testid="detail-drawer"
      ref={drawerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        swipeRef.current = null;
      }}
      className={`clientv2-drawer${closing ? ' is-closing' : ' is-open'}${recruitView ? ' is-recruit' : ''}`}
      role="dialog"
      aria-hidden={closing || undefined}
      aria-labelledby="clientv2-drawer-name"
    >
      <header className="clientv2-drawer-head">
        <div className="clientv2-drawer-top">
          {/* The same avatar the list row draws, marked the same way — a booked
              reminder above the circle — so the client the adviser tapped looks
              like the row they tapped it from. The date behind the mark is
              printed in full in the 提醒 cell below. */}
          <span aria-hidden="true" className="clientv2-drawer-avatar-wrap">
            <span
              data-testid="drawer-avatar"
              className={`clientv2-row-avatar clientv2-av-${avatarGenderClass(activeClient, adviserEmail)}`}
            >
              {name.charAt(0)}
            </span>
            {remindMark && (
              <span
                data-testid="drawer-remind"
                data-mark={remindMark}
                className={`clientv2-av-remind is-${remindMark}`}
              >
                <BellRing size={14} aria-hidden="true" />
              </span>
            )}
          </span>
          <div className="clientv2-drawer-id">
            <div className="clientv2-drawer-nameline">
              <strong id="clientv2-drawer-name" data-testid="drawer-name">
                {name}
              </strong>
              {tier && (
                <span data-testid="drawer-tier" className={`clientv2-row-tier tier-${tier.cls}`}>
                  {tierLabel}
                </span>
              )}
              {/* Nothing on the header said the record could be edited — the
                  name is not a control and 詳細資料 is a tile further down, so
                  the affordance was several inches from the thing it edits.
                  Same destination as that tile; two doors on purpose, because
                  this one is where the eye already is. */}
              <button
                type="button"
                data-testid="drawer-edit"
                className="clientv2-drawer-edit"
                disabled={!onAction}
                aria-disabled={!can('factsheet') || undefined}
                title={lockTitle('factsheet')}
                // 修改, not 詳細資料: it shares that tile's destination, but two
                // buttons announcing the same name is unusable with a screen
                // reader, and "edit" is what this glyph promises anyway.
                aria-label={intl.formatMessage({ id: 'Edit' })}
                onClick={guard('factsheet', () => onAction?.('factsheet', activeClient))}
              >
                <Pencil size={13} aria-hidden="true" />
              </button>
            </div>
            <div className="clientv2-drawer-markers">
              {recruitView && clientId ? (
                // 招募: connection and C.P.I. say nothing about a candidate, so
                // the same two slots carry 評估 and 階段 (RecruitHeaderMarkers).
                <RecruitHeaderMarkers clientId={clientId} intl={intl} onOpenIdea={() => setRecruitIdeaOpen(true)} />
              ) : (
                <>
                  {/* 已中斷 has nothing behind the pill: V1 renders it `disabled`
                  (ClientEntry.js:1257) and its ConnectionDialog shows only the
                  word 已中斷 for that status — the connect/consent flow belongs
                  to the other three. The notice below the header is what
                  explains the state here, so the chevron goes too. */}
                  <button
                    type="button"
                    data-testid="drawer-connpill"
                    className={`clientv2-connpill conn-${connection.cls}`}
                    disabled={connection.status === 'Disconnected'}
                    onClick={() => onConnect?.(activeClient)}
                  >
                    <i className="clientv2-connpill-dot" aria-hidden="true" />
                    {intl.formatMessage({ id: connection.key })}
                    {connection.status !== 'Disconnected' && <ChevronRight size={12} aria-hidden="true" />}
                  </button>
                  {/* 潛力指數 rides beside the connection pill rather than taking a
                  header cell of its own. It is one coloured word — the server
                  scores it, there is nothing to EDIT behind it — and a word
                  does not need a labelled box. The cell it gave up goes to
                  DISC, which does have somewhere to go when tapped.
                  There is something to READ behind it though: a coloured word
                  the adviser never chose and cannot change is exactly the kind
                  that needs saying what it means, so the tag opens the same
                  C.P.I. explainer the client-base indicator above the list
                  does. Still a plain span when no handler is wired, so nothing
                  advertises a tap that would do nothing. */}
                  <button
                    type="button"
                    className="clientv2-cpitag is-info"
                    data-testid="drawer-cpi"
                    onClick={() => onCpiInfo?.()}
                    aria-label={`${intl.formatMessage({ id: 'clientv2-cpi-label' })}: ${cpi ? cpi.wording : '—'}`}
                  >
                    {/* 潛力, not 潛力指數 — 指數 is the part of the name that says
                    nothing here, and this tag shares a line with the connection
                    pill. A drawer-only id: `clientv2-cpi-label` still reads
                    潛力指數 on the six other screens that print it with room. */}
                    {intl.formatMessage({ id: 'clientv2-cpi-short' })}
                    {cpi ? (
                      <>
                        <i className="clientv2-dot" style={{ backgroundColor: cpi.color }} />
                        {cpi.wording}
                      </>
                    ) : (
                      // Wrapped, not bare. This tag is an inline-flex row whose
                      // `gap` separates FLEX ITEMS, and adjacent text nodes collapse
                      // into a single anonymous one — so 潛力 and the dash rendered
                      // as 「潛力—」 while the answered state reads 「潛力 ● 中」,
                      // spaced by the dot element between them. A span makes the
                      // dash an item too, and it picks up the same 5px the dot gets
                      // rather than a hand-tuned margin that could drift from it.
                      <span>—</span>
                    )}
                    <Info size={11} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          </div>
          <button type="button" ref={closeButtonRef} data-testid="drawer-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* 生日 + DISC. 潛力指數 held this second slot and has moved up beside
            the connection pill as a tag: it is one word, and a labelled box was
            more furniture than the word needed. DISC comes back up here from a
            full-width row in the body — the two facts a conversation opens with
            are when to call and how to talk, so they belong side by side at the
            top rather than a scroll apart. */}
        <div className="clientv2-drawer-stats">
          <button
            type="button"
            data-testid="drawer-birthday"
            className="clientv2-drawer-stat"
            aria-disabled={!canEditProfile || undefined}
            title={lockTitle('edit-profile')}
            onClick={guard('edit-profile', () =>
              // No dob to greet on: route to the factsheet to fill one in,
              // the same navigation the 詳細資料 tile uses, rather than
              // opening a greeting dialog with nothing to show.
              factsheet?.dob ? onBirthday?.(activeClient) : onAction?.('factsheet', activeClient)
            )}
          >
            <span className="clientv2-drawer-stat-k">
              {intl.formatMessage({ id: 'clientv2-birthday' })}
              {birthdayCountdown !== null && (
                <span className="clientv2-drawer-stat-countdown" data-testid="drawer-birthday-countdown">
                  {intl.formatMessage({ id: 'clientv2-queue-in-days' }, { days: birthdayCountdown })}
                </span>
              )}
            </span>
            <span className="clientv2-drawer-stat-v">
              {/* One inline span, because the stat value is a flex row: left as
                  loose children, every part of the date would become a flex item
                  with a 6px gap between it and the next. */}
              <span className="clientv2-bd">
                {/* The year LEADS now that the readout is ISO, and the hyphen
                    that joins it to the month lives inside its span: the
                    narrowest phones drop `.clientv2-year` whole, and a separator
                    left outside would leave the cell reading 「-07-22」. */}
                {birthday.year && (
                  <span className="clientv2-year">
                    <span className="clientv2-century">{birthday.year.slice(0, 2)}</span>
                    {birthday.year.slice(2)}-
                  </span>
                )}
                {birthday.text}
                {/* The space lives here, so dropping the year on the narrowest
                    phones still reads 「07-22 (30)」 rather than 「07-22(30)」. */}
                {birthday.age !== null && ` (${birthday.age})`}
              </span>
            </span>
          </button>
          {/* Two doors, and the small one is a sibling rather than a nested
              button, exactly as 上次會面's ⓘ is (a button cannot contain one).
              The cell itself opens 智能 IDEA marked on its 性格與溝通 card: what
              an adviser wants from a DISC letter minutes before a meeting is the
              advice behind it (「較重外表，多讚美，多閒聊」), not the letter again.
              The ⓘ opens the small picker that sets the letter — which is also
              what keeps this readout usable without PRO, since 智能 IDEA is
              gated and the picker is not.
              The letter leads and the sentence behind it (「S · 較沉穩，人、感覺
              主導」, the longest string in the panel) is trimmed to the cell and
              kept whole in the tooltip. */}
          <div className="clientv2-drawer-stat-wrap">
            <button
              type="button"
              data-testid="drawer-disc"
              data-goes-to={recruitView ? 'recruit-playbook' : 'smart-idea'}
              className="clientv2-drawer-stat"
              // In 招募 the destination is a local dialog: no action is
              // dispatched, so the PRO gate in the action handler
              // (`requirePro`, clientActions.tsx) never runs and an adviser
              // without PRO gets the playbook rather than an upgrade prompt.
              disabled={recruitView ? false : !onAction}
              aria-disabled={recruitView ? undefined : !can('smart-idea-disc') || undefined}
              title={
                personality
                  ? discFull(personality.letter, intl.locale)
                  : recruitView
                  ? intl.formatMessage({ id: 'choose-personality' })
                  : lockTitle('smart-idea-disc')
              }
              onClick={openDiscReadout}
            >
              <span className="clientv2-drawer-stat-k">
                {intl.formatMessage({ id: 'clientv2-drawer-personality' })}
                {/* The letter sits beside the label, so the name keeps the whole
                    value line before it ellipsises. */}
                {personality && <span className="clientv2-drawer-stat-letter">{personality.letter}</span>}
              </span>
              <span className="clientv2-drawer-stat-v">
                {personality ? (
                  <span className="clientv2-drawer-stat-name">{discTag(personality.letter, intl.locale)}</span>
                ) : (
                  '—'
                )}
              </span>
            </button>
            <button
              type="button"
              data-testid="drawer-disc-edit"
              className="clientv2-drawer-stat-info"
              aria-disabled={!canEditProfile || undefined}
              aria-label={intl.formatMessage({ id: 'choose-personality' })}
              title={intl.formatMessage({ id: 'choose-personality' })}
              onClick={guard('edit-profile', () => onEditPersonality?.(activeClient))}
            >
              <Info size={13} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className="clientv2-drawer-body">
        {/* One scrolling column, with 主推三大 sticky inside it.
            It used to be two: a fixed `.clientv2-drawer-pinned` block holding
            everything above the tiles, and a scroller below. That pinned four
            things where only one earns the space — the 潛在招募 row, the 已中斷
            notice and 建議下一步 are all read once and then in the way, and on a
            phone they cost a third of the panel before a single tile appears.
            Now everything scrolls and only the three cards stay, by sticking at
            the top of this column once the reader passes them. */}
        <div className="clientv2-drawer-scroll" data-testid="drawer-scroll">
          {/* 潛在招募, with 業務 ｜ 招募 revealed on its left when enabled.
              In the BODY, not in the header band above it. The header is the
              one part of this panel that says which person is in front of you,
              and it looks the same for every client in the book — adding a row
              of controls to it for the small minority who are also recruitment
              leads changes the thing that should not change. Here it costs one
              line, sits directly under the name it applies to, and scrolls
              away with the rest.
              Once 招募模式 is unlocked, the 潛在招募 switch itself is always
              available. The two-view selector appears only after it is enabled,
              because before that there is no recruitment conversation to
              switch to.
              The whole row goes when 招募模式 is locked — the switch too, since
              its only job here is to reveal the selector. Setting the flag is
              NOT withheld with it: the 潛在招募 是／否 pair in 客戶狀態
              predates the mode and stays for everyone, as do the 招募 tab and
              the 💼 badge it feeds. */}
          {recruitmentUnlocked && (
            <div className="clientv2-viewswitch" data-testid="drawer-viewswitch">
              {isRecruitment && (
                <div
                  className={`clientv2-viewswitch-seg${recruitView ? ' is-recruit' : ''}`}
                  role="tablist"
                  aria-label={intl.formatMessage({ id: 'clientv2-view-switch' })}
                >
                  {/* The white pill, as a sibling of the two tabs rather than a
                      background on the active one — that is what makes the
                      change slide instead of blink, and it is the same control
                      the DISC picker draws (PersonalityCard's sliding thumb,
                      Soft Depth) on the same curve. `aria-hidden`
                      because the fact it carries is already in each tab's
                      `aria-selected`. */}
                  <span className="clientv2-viewswitch-pill" aria-hidden="true" />
                  {(['business', 'recruit'] as DrawerView[]).map(option => (
                    <button
                      key={option}
                      type="button"
                      role="tab"
                      data-testid="drawer-view"
                      data-view={option}
                      aria-selected={recruitView === (option === 'recruit')}
                      className={`clientv2-viewswitch-btn${recruitView === (option === 'recruit') ? ' is-active' : ''}`}
                      onClick={() => chooseView(option)}
                    >
                      {intl.formatMessage({ id: option === 'recruit' ? 'recruit' : 'clientv2-view-business' })}
                    </button>
                  ))}
                </div>
              )}
              <label
                className={`clientv2-recruit-switch-label${isRecruitment ? ' is-on' : ''}`}
                data-testid="drawer-recruit-switch-label"
              >
                {/* The 💼 is baked into `recruit-lead` itself, and six other
                    screens read that same message (MeetingDialog,
                    clientFactsheet, clientPage, personalDetailsForm, and
                    ClientRow twice as an aria-label/title) — so it is split
                    HERE rather than lifted out of the string, which would
                    change all of them.
                    Split because the badge cannot be aligned while it shares a
                    text node with the words: it renders from the system emoji
                    font, whose ink sits a pixel below Noto's CJK at this size,
                    and no rule can reach half of a text run. Its own element
                    can be nudged back onto their centre line.
                    Nested INSIDE the words rather than set beside them as a
                    second flex item: `align-items: center` on the label would
                    then centre the two BOXES, which puts their baselines ~2px
                    apart and makes the sag worse than it started. Sharing the
                    words' line box is what keeps one baseline to nudge from.
                    `aria-hidden` because the switch below already carries the
                    whole phrase, emoji included, as its `aria-label`. */}
                <span>
                  {recruitLabelText}
                  {recruitLabelBadge && (
                    <span className="clientv2-recruit-switch-badge" aria-hidden="true">
                      {recruitLabelBadge}
                    </span>
                  )}
                </span>
                <Switch
                  checked={isRecruitment}
                  size="small"
                  color="primary"
                  disabled={!onEditFact || !canEditProfile}
                  slotProps={{
                    input: {
                      'aria-label': intl.formatMessage({ id: 'recruit-lead' }),
                      'data-testid': 'drawer-recruit-switch'
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }}
                  onChange={event => {
                    const enabled = event.target.checked;
                    commitFact('isRecruitment', enabled);
                    if (!enabled && view === 'recruit') chooseView('business');
                  }}
                />
              </label>
            </div>
          )}
          {/* Say it once, up front. Without this the adviser meets a panel of
              greyed-out tiles with no explanation and reads it as a bug rather
              than as the client having revoked access. role="status" because it
              appears in place when a refresh flips the client's status while
              the drawer is open. */}
          {isClientRestricted(activeClient) && restrictionText && (
            <div className="clientv2-drawer-locked" data-testid="drawer-locked" role="status">
              <Lock size={14} aria-hidden="true" />
              <span>{restrictionText}</span>
            </div>
          )}

          {/* One sentence about this client and the one control that answers it.
              The signal's colour used to ride on the icon chip alone, which left
              the row itself looking like every other grey block in the panel —
              the one line that is ABOUT this client specifically was the one
              hardest to pick out of the stack. The border carries it too now.

              Inline rather than a class per signal: the colour is already keyed
              off the signal in one place (SIGNAL_COLOR, via suggestionSpec), and
              a set of CSS rules restating those five hexes is a second place for
              them to drift. The fill stays the neutral #f8fafc so the tinted
              chip beside it does not dissolve into a row of its own colour. */}
          {/* 業務 uses its queue/policy signal; 招募 uses the shared next-meeting
              and reminder dates above. Same component and destination, so the
              user learns one pattern: sentence first, the control that resolves
              it on the right. */}
          {shownSuggestion && (
            <div
              className="clientv2-suggest"
              data-testid="drawer-suggest"
              data-signal={recruitView ? recruitSuggestion.signal : signal || suggestionSpec?.copyKey}
              style={{ borderColor: shownSuggestion.color }}
            >
              <span
                className="clientv2-infoicon"
                style={{ backgroundColor: shownSuggestion.tint, color: shownSuggestion.color }}
              >
                <WandSparkles size={16} aria-hidden="true" />
              </span>
              <span className="clientv2-suggest-copy">{shownSuggestion.copy}</span>
              <button
                type="button"
                data-testid="drawer-suggest-cta"
                className={`clientv2-suggest-cta${
                  'featureLocked' in shownSuggestion && shownSuggestion.featureLocked ? ' is-locked' : ''
                }`}
                data-feature-unavailable={
                  ('featureLocked' in shownSuggestion && shownSuggestion.featureLocked) || undefined
                }
                disabled={shownSuggestion.disabled}
                aria-disabled={!can(shownSuggestion.capability) || undefined}
                title={
                  'featureLocked' in shownSuggestion && shownSuggestion.featureLocked
                    ? intl.formatMessage({ id: 'pro-dialog-msg-ultra' })
                    : lockTitle(shownSuggestion.capability)
                }
                onClick={guard(shownSuggestion.capability, shownSuggestion.run)}
              >
                {shownSuggestion.label}
                <ChevronRight size={13} aria-hidden="true" />
              </button>
            </div>
          )}

          <div className="clientv2-promorow" data-testid="promoted">
            {(recruitView ? RECRUIT_PROMOTED : PROMOTED).map(card => {
              const Icon = card.icon;
              const allowed = can(card.action);
              const label = intl.formatMessage({ id: card.labelKey });
              const opensIdea = card.action === 'recruit-idea';
              const { action } = card;
              // A.I. without FEATURE_GPT / Ultra: greyed, never withheld (the row keeps
              // its shape), and the tap offers the upgrade (clientActions 'ai').
              const aiLocked = (action === 'ai' || action === 'ai-recruit') && props.aiAvailable === false;
              return (
                <div className="clientv2-promocard-wrap" key={card.labelKey}>
                  <button
                    type="button"
                    // `disabled` still means "no handler wired at all"; a
                    // status lock uses aria-disabled so the tap can explain
                    // itself (see onRestricted).
                    disabled={!opensIdea && !onAction}
                    aria-disabled={!allowed || undefined}
                    aria-haspopup={opensIdea ? 'dialog' : undefined}
                    data-testid="drawer-promoted"
                    data-action={card.action}
                    data-locked={allowed ? undefined : 'true'}
                    data-feature-unavailable={aiLocked || undefined}
                    title={aiLocked ? intl.formatMessage({ id: 'pro-dialog-msg-ultra' }) : lockTitle(card.action)}
                    className={`clientv2-promocard${allowed && !aiLocked ? '' : ' is-locked'}`}
                    onClick={guard(action, () =>
                      action === 'recruit-idea' ? setRecruitIdeaOpen(true) : onAction?.(action, activeClient)
                    )}
                  >
                    {/* A locked card drops its attention dot — a red badge
                        calling for action on a control that cannot be used is
                        the opposite of what it is for. */}
                    {allowed && card.alwaysDot && <span className="clientv2-tile-dot hint" aria-hidden="true" />}
                    {/* A second `.note` dot used to sit here for "a reminder is
                        set", suppressed on any card that already had a permanent
                        badge so the two could not stack. Both cards that reach
                        this branch carry `alwaysDot`, so it could never render.
                        Nothing is lost: the 提醒 cell below states the reminder
                        date outright, which a dot never did. */}
                    <Icon size={24} aria-hidden="true" />
                    <span className="clientv2-promocard-title">{label}</span>
                  </button>
                </div>
              );
            })}
          </div>

          {(recruitView ? RECRUIT_GROUPS : GROUPS).map(group => {
            const GroupIcon = group.icon;
            // Which group's row closes with the 補資料 tile. Both panels have two
            // groups now and both give it to the second.
            const carriesFactsTile =
              group.titleKey === (recruitView ? 'clientv2-recruit-group-follow' : 'clientv2-group-client');
            // Both panels close the same row with the ⋯, and both are three
            // levels deep because of it — cards, tiles, overflow. What each one
            // hides differs; see MORE_ITEMS / RECRUIT_MORE_ITEMS.
            const carriesOverflow = carriesFactsTile;
            const overflowItems = recruitView ? RECRUIT_MORE_ITEMS : MORE_ITEMS;
            // The second group is the quieter of the two in BOTH views —
            // record-keeping rather than the selling work above it — so its tiles
            // drop their box and keep only the label.
            const followUp = group.titleKey === 'clientv2-group-client' || carriesFactsTile;
            // …but only 業務's carries the tracking editor. 進度跟進 is the tile
            // that switches it on, it tracks a SALES relationship, and 招募 does
            // not render it.
            const carriesTrackingBar = group.titleKey === 'clientv2-group-client';
            // The quiet group folds; a running 跟進中 record keeps it open so
            // its editor is never hidden behind a fold.
            const collapsed = followUp && !quietGroupOpen && !(carriesTrackingBar && tracking);
            return (
              // Both groups share the `worktiles` id on purpose — "every tile
              // group" is a real query. `data-group` is what addresses ONE of
              // them, so nothing has to reach for getByTestId and hit the
              // "found multiple elements" wall.
              <div
                className="clientv2-worktile-group"
                data-testid="worktiles"
                data-group={group.titleKey}
                key={group.titleKey}
              >
                {followUp ? (
                  <button
                    type="button"
                    className={`clientv2-group-head is-toggle${collapsed ? ' is-collapsed' : ''}`}
                    data-testid="drawer-group-head"
                    aria-expanded={!collapsed}
                    onClick={() => setQuietGroupOpen(collapsed)}
                  >
                    <GroupIcon size={13} aria-hidden="true" />
                    {intl.formatMessage({ id: group.titleKey })}
                    {/* A bare chevron after a grey caption did not read as
                        tappable; the worded pill says what the tap does. */}
                    <span className="clientv2-group-toggle">
                      {intl.formatMessage({ id: collapsed ? 'clientv2-group-expand' : 'clientv2-group-collapse' })}
                      <ChevronDown size={14} aria-hidden="true" className="clientv2-group-chevron" />
                    </span>
                  </button>
                ) : (
                  <div className="clientv2-group-head" data-testid="drawer-group-head">
                    <GroupIcon size={13} aria-hidden="true" />
                    {intl.formatMessage({ id: group.titleKey })}
                  </div>
                )}
                {/* Folded, the row keeps only 補資料 — the one tile that is an
                    offer rather than a destination. */}
                {(!collapsed || carriesFactsTile) && (
                  <div
                    className={`clientv2-worktile-row${followUp ? ' is-quiet' : ''}${collapsed ? ' is-folded' : ''}`}
                    // A row of tiles alone (no ⋯ / 補資料 after them) takes one
                    // column per tile, so a fifth tile does not wrap onto a row
                    // of its own. See `.clientv2-worktile-row[data-count]`.
                    data-count={carriesOverflow ? undefined : group.items.length}
                  >
                    {!collapsed &&
                      group.items.map(item => {
                        const Icon = item.icon;
                        // Two different reasons a tile can be dead, kept apart. The
                        // status decides `allowed` and can explain itself on a tap
                        // (onRestricted); a tool the adviser does not hold has
                        // nothing to say and nowhere to go, so that tile is inert —
                        // shown and greyed, never withheld, so the row keeps its
                        // shape across advisers and teams.
                        const featureUnavailable = recruitView && !recruitToolAvailable(item.action);
                        const allowed = can(item.action) && !featureUnavailable;
                        // A locked 進度跟進 does not advertise 跟進中 — V1 drops the
                        // label back to 進度跟進 and greys the icon for a
                        // disconnected client (ClientEntry.js:1211-1231).
                        const trackingOn = item.kind === 'tracking' && Boolean(tracking) && allowed;
                        const label = trackingOn
                          ? intl.formatMessage({ id: 'Track Biz On' })
                          : intl.formatMessage({ id: item.labelKey });
                        return (
                          <button
                            key={item.labelKey}
                            type="button"
                            disabled={!onAction || featureUnavailable}
                            aria-disabled={!allowed || undefined}
                            data-testid="drawer-tile"
                            data-action={item.action}
                            data-locked={allowed ? undefined : 'true'}
                            data-feature-unavailable={featureUnavailable || undefined}
                            title={
                              featureUnavailable
                                ? intl.formatMessage({ id: 'clientv2-recruit-feature-unavailable' })
                                : lockTitle(item.action)
                            }
                            className={`clientv2-worktile${trackingOn ? ' is-tracking' : ''}${
                              allowed ? '' : ' is-locked'
                            }`}
                            onClick={guard(item.action, () => {
                              // Either quiz tile asks which part of its quiz to send first.
                              if (item.action === 'recruit-quiz' || item.action === 'cover-quiz') {
                                setQuizChooserOpen(true);
                                return;
                              }
                              onAction?.(item.action, activeClient);
                            })}
                          >
                            {((item.labelKey === 'Contact' && connected) || trackingOn) && (
                              <span className="clientv2-actdot" aria-hidden="true" />
                            )}
                            <Icon size={20} aria-hidden="true" />
                            <span>{label}</span>
                            {item.pro && <span className="clientv2-pro">PRO</span>}
                          </button>
                        );
                      })}
                    {/* ⋯ sits third, and 補資料 closes the row. The overflow is
                      the least interesting control in the group, so it gives the
                      end of the row — the position a thumb reaches first and the
                      eye leaves last — to the one tile that is an offer. */}
                    {!collapsed && carriesOverflow && (
                      <div className="clientv2-more-wrap" ref={moreWrapRef}>
                        <button
                          type="button"
                          data-testid="drawer-more"
                          className={`clientv2-worktile clientv2-more-tile${moreOpen ? ' is-open' : ''}`}
                          aria-expanded={moreOpen}
                          aria-haspopup="menu"
                          disabled={!onAction}
                          onClick={() => setMoreOpen(open => !open)}
                        >
                          <MoreHorizontal size={20} aria-hidden="true" />
                          {/* 其他功能, not 專業版功能: what is behind this tile is
                            simply the rest of the controls. Some of them happen
                            to be PRO, and each says so on its own row — naming
                            the whole overflow after the subscription made three
                            free controls look locked. */}
                          <span>{intl.formatMessage({ id: 'clientv2-group-more' })}</span>
                        </button>
                        {moreOpen && (
                          // Opens inward from whichever column the ⋯ lands in.
                          // 業務's lands fourth (right-anchored); 招募's 跟進 row is
                          // 團隊Bot · AI Coach · IIQE 備試 · 詳細資料 · ⋯, so its ⋯
                          // wraps to the first column and needs `is-start`, or the
                          // menu hangs off the drawer's left edge and is clipped.
                          <div
                            className={`clientv2-more-menu${group.items.length % 4 < 2 ? ' is-start' : ''}`}
                            data-testid="drawer-more-menu"
                            role="menu"
                          >
                            {overflowItems.map(item => {
                              const Icon = item.icon;
                              // Same two reasons as the tiles: a status lock
                              // explains itself on tap; a tool the team lacks
                              // (團隊文件 without TeamHub) is inert.
                              const featureUnavailable = recruitView && !recruitToolAvailable(item.action);
                              const allowed = can(item.action) && !featureUnavailable;
                              return (
                                <button
                                  key={item.labelKey}
                                  type="button"
                                  role="menuitem"
                                  disabled={featureUnavailable || undefined}
                                  data-testid="drawer-more-item"
                                  data-action={item.action}
                                  data-locked={allowed ? undefined : 'true'}
                                  data-feature-unavailable={featureUnavailable || undefined}
                                  aria-disabled={!allowed || undefined}
                                  title={
                                    featureUnavailable
                                      ? intl.formatMessage({ id: 'clientv2-recruit-feature-unavailable' })
                                      : lockTitle(item.action)
                                  }
                                  className={`clientv2-more-item${allowed ? '' : ' is-locked'}`}
                                  onClick={guard(item.action, () => {
                                    setMoreOpen(false);
                                    onAction?.(item.action, activeClient);
                                  })}
                                >
                                  <Icon size={16} aria-hidden="true" />
                                  <span>{intl.formatMessage({ id: item.labelKey })}</span>
                                  {/* 聯絡's connected dot travels with it into the
                                    menu — collapsing a control must not silently
                                    drop the state it was reporting. */}
                                  {item.labelKey === 'Contact' && connected && (
                                    <span className="clientv2-actdot is-inline" aria-hidden="true" />
                                  )}
                                  {item.pro && <span className="clientv2-pro is-inline">PRO</span>}
                                </button>
                              );
                            })}
                            {carriesFactsTile &&
                              onEditFact &&
                              canEditProfile &&
                              (recruitView ? recruitUnanswered.length === 0 : unansweredKnow.length === 0) && (
                                <button
                                  key="facts"
                                  type="button"
                                  role="menuitem"
                                  data-testid="drawer-more-item"
                                  data-action="facts"
                                  className="clientv2-more-item"
                                  onClick={guard('edit-profile', () => {
                                    setMoreOpen(false);
                                    openFacts(null);
                                  })}
                                >
                                  <ClipboardList size={16} aria-hidden="true" />
                                  <span>{intl.formatMessage({ id: 'clientv2-facts-tile' })}</span>
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* 補資料 closes the 客戶與跟進 row.

                      Here rather than out on the list, because this is where an
                      adviser already IS when they notice a readout saying 「—」:
                      the panel is full of blanks this popup fills, and the same
                      blanks are what stop the C.P.I. from ranking the client.

                      It is the one tile in the drawer that is an OFFER rather
                      than a destination, and it is painted like one — solid,
                      warm and lit, against a grid of pale outlined tiles. That
                      is not decoration: the grid is a menu of places to go, and
                      a tile that looks like the rest of the menu reads as a
                      twelfth place to go rather than as the thing that makes the
                      other eleven worth more.

                      Shown only while there is something to gain. Once the
                      client is ranked the tile has nothing to offer, and a
                      permanent 補資料 button would be a standing complaint that
                      the record is never finished — every factsheet has empty
                      fields, and most of them do not matter. Withheld entirely
                      for a 已中斷 client: their band is withheld whatever is
                      filled in (server isClientCPIValid), so asking would be
                      asking for nothing. `cpiGapOf` returns an empty plan in
                      both cases, which is the one condition below. */}
                    {/* `cpiGap.rankable` gates the BUSINESS tile only: it asks
                      whether a C.P.I. band is reachable, and a 已中斷 client's
                      never is however much is filled in. The 招募 card offers no
                      ranking, so nothing about it turns on that. */}
                    {carriesFactsTile && (recruitView || cpiGap.rankable) && onEditFact && (
                      <FactsTile
                        // Remounted per client, which is what resets both the
                        // celebration and the baseline it is measured against.
                        // And per view: the two cards count different questions,
                        // so a ring carried across the switch would animate from
                        // one card's progress to the other's.
                        key={`${clientId || 'none'}-${recruitView ? 'recruit' : 'business'}`}
                        remaining={recruitView ? recruitUnanswered.length : unansweredKnow.length}
                        answered={recruitView ? RECRUIT_FACT_FIELDS.length - recruitUnanswered.length : knowAnswered}
                        total={recruitView ? RECRUIT_FACT_FIELDS.length : knowAskable}
                        plan={recruitView ? recruitUnanswered.join(',') : cpiGap.plan.join(',')}
                        factsOpen={factsOpen}
                        disabled={!canEditProfile}
                        lockTitle={lockTitle('edit-profile')}
                        label={intl.formatMessage({ id: 'clientv2-facts-tile' })}
                        // `openFacts(null)`, not a bare `setFactsOpen(true)`:
                        // `factsFocus` outlives every close (it is cleared only
                        // when the client changes), so opening from here after
                        // the 現有客戶 or 重要性 cell had opened it left that
                        // cell's row still outlined — and its `aria-expanded`
                        // still true — for a card the tile opened. The tile's
                        // errand is 「whatever is missing」, which is what null
                        // means.
                        onClick={guard('edit-profile', () => openFacts(null))}
                      />
                    )}
                  </div>
                )}
                {!collapsed && carriesTrackingBar && trackingBar}
              </div>
            );
          })}

          {/* 現有客戶 and 重要性 — the two facts that rank a client against the
              rest of the book, and the only two the adviser sets from here.
              重要性 used to be a read-only chip beside the name; it is settable
              now because being VIP ranks a client exactly as much as being an
              existing one does. The chip stays: that is where the answer is
              read, this is where it is set.
              They are half-width cells like the readouts beside them, showing
              only the answer on file — 是 / 普通 / a dash — and the answers
              themselves live in a popup the cell opens. Laying all five options
              out in the panel cost both cells the full drawer width (three tier
              labels side by side do not fit half of 400px in English) for two
              facts that are read far more often than they are changed. Same
              trade as DISC: the readout is small, the editor is a dialog. */}
          {/* 認識候選人 — four concise readouts in the same 2×2 the business
              panel uses for 現有客戶 / 重要性. 家庭狀況 combines two answers;
              認識途徑 uses the most specific relationship clue on file. Each
              cell opens that card marked on its primary question, exactly as
              those two do: the adviser came to change one answer, not to read four.
              現職 and 現時月入 have never had a readout OR an editor anywhere in
              ClientV2 — they exist on the factsheet and V1's form writes them,
              which is why they are so often empty here. */}
          {recruitView && (
            <div className="clientv2-infogrid" data-testid="drawer-recruit-facts">
              {recruitPicks.map(pick => (
                <button
                  key={pick.key}
                  type="button"
                  data-testid="drawer-recruit-fact"
                  data-field={pick.key}
                  data-value={pick.filled ? 'set' : ''}
                  className="clientv2-infoitem"
                  disabled={!onEditFact}
                  aria-disabled={!canEditProfile || undefined}
                  aria-haspopup="dialog"
                  aria-expanded={factsOpen && isFactFocused(pick.key)}
                  title={lockTitle('edit-profile')}
                  onClick={guard('edit-profile', () => openFacts(pick.focus))}
                >
                  {factIcon(pick.key)}
                  <div className="clientv2-infobody">
                    <div className="clientv2-infok">{pick.label}</div>
                    <div className="clientv2-infov">{pick.value}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {!recruitView && (
            <div className="clientv2-infogrid">
              {businessPicks.map(pick => {
                return (
                  <button
                    key={pick.key}
                    type="button"
                    data-testid={pick.testId}
                    data-value={pick.filled ? 'set' : ''}
                    className="clientv2-infoitem"
                    disabled={!pick.enabled}
                    aria-disabled={!canEditProfile || undefined}
                    aria-haspopup="dialog"
                    aria-expanded={factsOpen && isFactFocused(pick.key)}
                    title={lockTitle('edit-profile')}
                    // Same card the 補資料 tile opens, outlined on this cell's own
                    // question — the adviser came to change one answer, not to
                    // read eight.
                    onClick={guard('edit-profile', () => openFacts(pick.focus))}
                  >
                    {factIcon(pick.key)}
                    <div className="clientv2-infobody">
                      <div className="clientv2-infok">{pick.label}</div>
                      <div className="clientv2-infov">
                        {pick.dot && <i className="clientv2-dot" style={{ backgroundColor: pick.dot }} />}
                        {pick.value}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Keyed on the view so switching 業務 ｜ 招募 REMOUNTS it.
              Everything above this in the scroll column is view-conditional
              JSX, so a switch unmounts one branch and mounts the other and the
              drawer's rise-in cascade (styles.scss) plays again for each. This
              block is not: it is the same element in both views, at the same
              position, so React reused the DOM node — and a CSS animation only
              runs on mount. The result was a panel where everything above these
              two cells animated across the switch and the cells themselves sat
              there untouched, which read as them having no animation at all.
              Measured on the running app: the three blocks above came back as
              new nodes at opacity 0, these came back as the SAME node at
              opacity 1. Reported 2026-08-31. */}
          <div className="clientv2-infogrid" key={`follow-up-${recruitView ? 'recruit' : 'business'}`}>
            {/* The item itself opens the editor for the number it reports — the
                meeting-note dialog, marked on its 最近會面 row, because that
                number being wrong or missing is the only reason to open it from
                here. The colour scale keeps its dot, and the ⓘ that explains it
                is a sibling button rather than a nested one, so opening the
                legend never opens the dialog. */}
            <div className="clientv2-last-meeting-wrap">
              <button
                type="button"
                data-testid="drawer-last-meeting-control"
                className="clientv2-infoitem clientv2-last-meeting-item"
                disabled={!onAction}
                aria-disabled={!can('meeting-date') || undefined}
                title={lockTitle('meeting-date')}
                onClick={guard('meeting-date', () => onAction?.('meeting-date', activeClient))}
              >
                <span className="clientv2-infoicon indigo">
                  <CalendarClock size={16} aria-hidden="true" />
                </span>
                <div className="clientv2-infobody">
                  <div className="clientv2-infok">{intl.formatMessage({ id: 'clientv2-last-meeting' })}</div>
                  <div className="clientv2-infov" data-testid="drawer-last-meeting">
                    {elapsed ? (
                      <>
                        <i className="clientv2-dot" style={{ backgroundColor: elapsed.color }} />
                        {elapsed.label}
                      </>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </button>
              <button
                type="button"
                data-testid="drawer-last-meeting-info"
                className="clientv2-last-meeting-info"
                aria-expanded={lastMeetingHintOpen}
                aria-label={intl.formatMessage({ id: 'show-passed-time' })}
                onClick={() => setLastMeetingHintOpen(open => !open)}
              >
                <Info size={13} aria-hidden="true" />
              </button>
              {lastMeetingHintOpen && (
                <div className="clientv2-last-meeting-hint" data-testid="last-meeting-hint">
                  <div className="clientv2-last-meeting-copy">{intl.formatMessage({ id: 'show-passed-time' })}</div>
                  {LAST_MEETING_LEGEND.map(pair => (
                    <div className="clientv2-last-meeting-legend" key={`${pair.before}-${pair.after}`}>
                      <i style={{ backgroundColor: pair.color }} aria-hidden="true" />
                      <span>
                        {pair.before ? `${pair.before} ${intl.formatMessage({ id: 'Mth' })} < ` : ''}
                        {intl.formatMessage({ id: 'Day' })}
                        {pair.after ? ` < ${pair.after} ${intl.formatMessage({ id: 'Mth' })}` : ''}
                      </span>
                    </div>
                  ))}
                  <div className="clientv2-last-meeting-now">
                    {intl.formatMessage({ id: 'client-long-time-no-see' })}
                    <strong>{lastMeetingDuration?.label || '—'}</strong>
                  </div>
                </div>
              )}
            </div>
            {/* The next 提醒我 date. A reminder is the one thing on this panel
                that is about to expire, so it counts down: the days go on the
                label row (same shape as the birthday countdown above), the date
                reads below. A date already past still shows — hiding a reminder
                the adviser missed is the one thing a reminder must never do —
                and drops the countdown for an overdue mark instead. */}
            {/* Opens 會面筆記, because that dialog is where this date is set:
                meetingDialogContent carries fsReminderDate and fsReminderActive
                into it (clientActions.tsx). So the readout and its editor are
                the same tap — including when the readout is a dash, which is
                exactly when the adviser wants to set one. It opens MARKED on the
                提醒我 row, exactly as 上次會面 opens marked on its own: the row is
                one of eight in that form, and the adviser should not have to
                find the field they just tapped. */}
            <button
              type="button"
              className={`clientv2-infoitem${reminder && reminder.days < 0 ? ' is-overdue' : ''}`}
              data-testid="drawer-reminder"
              data-days={reminder ? String(reminder.days) : undefined}
              disabled={!onAction}
              aria-disabled={!can('meeting-reminder') || undefined}
              title={lockTitle('meeting-reminder')}
              onClick={guard('meeting-reminder', () => onAction?.('meeting-reminder', activeClient))}
            >
              <span className="clientv2-infoicon amber">
                <BellRing size={16} aria-hidden="true" />
              </span>
              <div className="clientv2-infobody">
                <div className="clientv2-infok">
                  {intl.formatMessage({ id: 'Reminder' })}
                  {reminder && reminder.days > 0 && (
                    <span className="clientv2-info-countdown" data-testid="drawer-reminder-countdown">
                      {intl.formatMessage({ id: 'clientv2-queue-in-days' }, { days: reminder.days })}
                    </span>
                  )}
                </div>
                {/* Split at the century, exactly as the birthday readout above
                    is: this cell is half the drawer wide and 「20 Aug 2026」 was
                    the value that wrapped mid-date first on a narrow phone. The
                    narrow-phone stylesheet drops 「20」 (2026 → 26), which is
                    still the year to anyone reading it. Which digits go is a
                    width decision, so it is made in CSS where the width is
                    known; the parts render in the order `D MMM YYYY` produced,
                    so a locale that orders the month differently is unaffected.
                    「今天」 carries no year to shorten. */}
                <div className="clientv2-infov" data-testid="drawer-reminder-date">
                  {!reminder && '—'}
                  {reminder && reminder.days === 0 && todayLabel}
                  {reminder && reminder.days !== 0 && (
                    // One inline span, for the same reason the birthday readout
                    // above wears one: `.clientv2-infov` is a flex row with a
                    // 6px gap, so left as loose children the date and its year
                    // would each become a flex item and be pushed 6px apart —
                    // on top of the single space the year already carries.
                    <span>
                      {reminder.date.format('D MMM')}
                      <span className="clientv2-year">
                        {' '}
                        <span className="clientv2-century">{reminder.date.format('YYYY').slice(0, 2)}</span>
                        {reminder.date.format('YYYY').slice(2)}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </button>
          </div>

          {!recruitView && servicePrefsLine && (
            <div className="clientv2-service-prefs" data-testid="drawer-service-prefs">
              <span className="clientv2-service-prefs-k">
                {intl.formatMessage({ id: 'clientv2-service-prefs-label' })}
              </span>
              <span className="clientv2-service-prefs-v">{servicePrefsLine}</span>
            </div>
          )}

          {/* 標籤 and the coverage radar share a row: the radar at full drawer
              width was far larger than it needed to be, and the tag chips wrap
              narrow anyway, so the space the radar gives back is exactly what
              the tags want. Family stacks above tags in the same left column.
              The radar has no heading of its own — at this size the chart reads
              as what it is, and a section header would cost the column height
              the chart needs. */}
          {/* Keyed for the same reason as the grid above — it is the other
              unconditional block in this column, so it was the other one left
              standing while its neighbours animated across a view switch. It
              also genuinely changes shape between the two: the radar is
              withheld in 招募 (see below), so the tags take the full width.

              Its own prefix, not the same string the grid above uses: these two
              are SIBLINGS, and React requires keys to be unique among siblings.
              Handing both the bare view name made the reconciler match the
              wrong slots and render the follow-up grid twice — caught by the
              drawer suite as 「Found multiple elements by
              data-testid="drawer-last-meeting"」. */}
          <div className="clientv2-drawer-tagradar" key={`tags-${recruitView ? 'recruit' : 'business'}`}>
            <div className="clientv2-drawer-tagradar-tags">
              <div className="clientv2-subsect">
                <House size={15} aria-hidden="true" />
                {intl.formatMessage({ id: 'clientv2-family-tree' })}
              </div>
              <div className="clientv2-chiprow" data-testid="drawer-family">
                {families.length > 0 ? (
                  families.map(family => (
                    <button
                      key={family}
                      type="button"
                      className="clientv2-famchip"
                      aria-disabled={!canEditProfile || undefined}
                      title={lockTitle('edit-profile')}
                      onClick={guard('edit-profile', () => onEditFamily?.(activeClient))}
                    >
                      <House size={12} aria-hidden="true" />
                      {family}
                    </button>
                  ))
                ) : (
                  <button
                    type="button"
                    className="clientv2-famchip"
                    aria-disabled={!canEditProfile || undefined}
                    title={lockTitle('edit-profile')}
                    onClick={guard('edit-profile', () => onEditFamily?.(activeClient))}
                  >
                    <Plus size={12} aria-hidden="true" />
                    {intl.formatMessage({ id: 'clientv2-add-family' })}
                  </button>
                )}
              </div>

              <div className="clientv2-subsect">
                <Tags size={15} aria-hidden="true" />
                {intl.formatMessage({ id: 'clientv2-tags' })}
              </div>
              <div className="clientv2-chiprow" data-testid="drawer-tags">
                {/* Each tag opens the tag editor, exactly as each 家庭 chip
                    above opens the family editor. They were spans: the only way
                    into the editor was 「+ 新增標籤」, so an adviser wanting to
                    fix or remove a tag had to tap a control captioned 「add」 and
                    trust that it also edits. Tapping the thing you want to
                    change is the shorter sentence, and it is the one this
                    section was already speaking one row up. */}
                {tags.map(tag => (
                  <button
                    key={tag.text}
                    type="button"
                    className="clientv2-tag"
                    data-testid="drawer-tag"
                    style={tag.color ? { color: tag.color, borderColor: tag.color } : undefined}
                    aria-disabled={!canEditProfile || undefined}
                    title={lockTitle('edit-profile')}
                    onClick={guard('edit-profile', () => onEditTags?.(activeClient))}
                  >
                    {tag.text}
                  </button>
                ))}
                <button
                  type="button"
                  className="clientv2-addtag"
                  data-testid="drawer-add-tag"
                  disabled={!onEditTags}
                  aria-disabled={!canEditProfile || undefined}
                  title={lockTitle('edit-profile')}
                  onClick={guard('edit-profile', () => onEditTags?.(activeClient))}
                >
                  <Plus size={12} aria-hidden="true" />
                  {intl.formatMessage({ id: 'mda-add-custom-tag-title' })}
                </button>
              </div>
            </div>

            {/* The whole column belongs to the needs analysis, so a status that
                withholds the analysis gets no column at all — not an empty
                pentagon, and not a prompt towards a control it cannot open. The
                chart is drawn from the CLIENT's policy data, which 已中斷 has
                revoked; there is nothing here the adviser is entitled to see, so
                the tags take the full width and the panel says nothing it cannot
                back up. The 已中斷 notice above already explains the absence.
                Withheld in the 招募 view for the same kind of reason: the
                pentagon reports how well this person's FAMILY is covered, which
                is an answer to the other conversation. Nothing is lost — 業務 is
                one tap up. */}
            {/* 招募's own pentagon: five angles on the candidate, scored by the
                recruiter. Adviser-owned judgement, so no status withholds it. */}
            {recruitView && clientId && (
              <div className="clientv2-drawer-tagradar-chart" data-testid="drawer-recruit-radar">
                <RecruitRadar clientId={clientId} intl={intl} editRequest={recruitScoreRequest} />
              </div>
            )}
            {!recruitView && can('analysis') && (
              <div className="clientv2-drawer-tagradar-chart">
                {coverageKnown ? (
                  <CoverageRadar scores={activeScores} intl={intl} />
                ) : (
                  <button
                    type="button"
                    data-testid="drawer-radar-empty"
                    className="clientv2-radar-empty"
                    disabled={!onAction}
                    onClick={() => onAction?.('analysis', activeClient)}
                  >
                    {intl.formatMessage({ id: 'clientv2-radar-empty' })}
                    <ChevronRight size={13} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 補資料 — the questions that fill the factsheet. Each tap writes
          immediately, so the caption at the top counts down as the adviser
          answers and the tile behind the dialog disappears the moment the last
          field lands.

          A portalled, viewport-centred MUI dialog, like every other popup this
          page opens (the C.P.I. explainer, 智能搜尋, the move-policy pair) and
          like the legacy dialogs the tiles above open. It used to be a
          hand-rolled card living inside the panel, dimming it with a scrim of
          its own, and that had two problems worth more than the novelty:

          - It declared aria-modal="true" while the page behind it stayed in the
            accessibility tree, untrapped and tabbable, and focus never entered
            the card at all. That is exactly the lie this file refuses to tell
            about the drawer itself (see the docblock above, which is why the
            drawer is role="dialog" WITHOUT aria-modal). MUI inerts the
            background for real, traps focus, restores it on close, and owns
            Escape.
          - Its width was the panel's, not the content's: 300px for nine
            questions of tap-sized answers, which is why DISC folds into 2x2 and
            the child-count buttons are as narrow as they are. Centred on the
            viewport the same card gets a phone's full width, and a desktop's. */}
      {/* A sheet on a phone, the same centred dialog above 600px —
          `BottomSheetOrDialog` is the component that already makes exactly that
          split, and 會面筆記, 智能 IDEA and 編輯家庭 all go through it. This card
          is the same KIND of thing they are: a stack of fields about the client
          in the drawer behind it, opened from that drawer, on a phone. It was
          the odd one out.
          Not for width — measured at 375px, the MUI dialog was already
          `margin: 0` and edge to edge, and the sheet's own Box padding is wider
          than this card's. The reason is that two adjacent surfaces doing the
          same job should not be two different kinds of surface.
          `blocking: true`, which this file previously argued against on a
          measurement that was taken too early. The claim was that it "buys
          nothing" because focus stayed on a button behind the sheet through the
          next tick. It does not stay: react-spring-bottom-sheet gates a real
          `focus-trap` on exactly this prop (dist/index.js — `createFocusTrap`,
          `enabled: <ready> && <blocking>`), and it activates through a promise
          chain AFTER the open transition. Re-measured at 375x812: focus is
          still on `drawer-close` at t=0 and has moved to `drawer-facts-close`
          by t=200ms, then holds through 12 Tabs and 20 Shift+Tabs without once
          leaving the sheet. Without it, 371 focusable elements sit behind an
          open sheet and the adviser's focus is parked on one of them.
          This matters because the surface REGRESSED: as a MUI Dialog it had
          Modal's focus trap, and swapping to a sheet below 600px silently
          dropped it. VoiceOver, Switch Control and any external keyboard could
          reach and fire the panel underneath.
          Set here rather than on BottomSheetOrDialog's own default, which stays
          `false`. Flipping it there would change the backdrop, the scroll lock
          and the focus behaviour of ~19 other sheets at once — a bigger call
          than this branch should make.
          One coupling to know about: `blocking` makes rsbs draw its own
          `[data-rsbs-backdrop]`, and that element matches nothing in
          OVERLAY_OF_ITS_OWN above — it carries only `data-rsbs-backdrop`, no
          role and no MUI class. A tap landing on it would therefore read as
          「outside」 and close the panel behind. It cannot happen HERE, because
          this sheet's overlay covers the viewport whole (measured top 0,
          bottom 812, height 812 on an 812px screen), so no part of that
          backdrop is ever exposed to a finger. A future caller that opens to a
          partial snap point would need OVERLAY_OF_ITS_OWN taught about it. */}
      <FactsSurface
        open={factsOpen}
        onClose={closeFacts}
        className="clientv2-facts-modal"
        // `BoxProps.className` is how the sheet's own scrolling element gets
        // `.clientv2-facts-dialog` — the same class `DialogContentProps` puts on
        // MUI's scroller below. One rule (overscroll containment, and the phone
        // gutters) then lands on whichever of the two is doing the scrolling.
        //
        // The `height` is what makes that Box the scroller, and it is why this
        // sheet could not be scrolled by dragging its content.
        // BottomSheetOrDialog defaults the Box to `max-content`, so the Box
        // never overflows and the scrolling falls to `[data-rsbs-scroll]` —
        // the very element react-spring-bottom-sheet binds its own drag handling
        // to (`expandOnContentDrag`), which is what was eating the gesture.
        // Measured at 375px: 1042px of content in a 1042px box, nothing to
        // scroll, against 1042 in 758 once the Box has a height of its own.
        //
        // A REAL length, not `100%`. Percentages resolve against a parent rsbs
        // has not sized yet when it measures the content to pick a snap point,
        // so `100%` collapsed the whole sheet to 32px on open — it looked right
        // only when applied to a sheet that had already been measured. Every
        // working sheet in the app passes arithmetic off the viewport for this
        // reason; MeetingDialog's is the same line minus a footer this card does
        // not have. 54px is the sheet's own header.
        BottomSheetProps={{
          blocking: true,
          // `false` is react-spring-bottom-sheet's supported way to disable
          // only its focus trap. Keep blocking, aria hiding and body scroll
          // locking active; the portalled picker owns focus until it closes.
          initialFocusRef: dobPickerOpen ? false : undefined,
          BoxProps: {
            className: 'clientv2-facts-dialog',
            height: `calc(${windowHeight}px - 54px - var(--top-padding, 0px))`
          }
        }}
        DialogProps={{
          fullWidth: true,
          maxWidth: 'sm',
          'data-testid': 'drawer-facts-modal',
          'aria-labelledby': 'clientv2-facts-title'
        }}
        DialogTitleProps={{ id: 'clientv2-facts-title', className: 'clientv2-facts-head' }}
        DialogContentProps={{ className: 'clientv2-facts-dialog' }}
        BottomSheetParams={{ hideCloseButton: true }}
        header={
          <span className="clientv2-facts-head-title">
            {intl.formatMessage({ id: recruitView ? 'clientv2-recruit-facts-title' : 'clientv2-facts-title' })}
          </span>
        }
        headerCTAs={[
          // The only way out, and enough of one. There was a second 關閉 in the
          // footer as well — the pair made sense when this was a centred dialog
          // whose actions bar came free, but as a sheet that bar is a permanent
          // strip across the bottom of the phone, and it was spending it on a
          // button already sitting at the top. 智能 IDEA, the sheet this one now
          // sits beside, closes from its header alone for the same reason.
          //
          // Supplied here rather than left to the component's own ✕
          // (`hideCloseButton` above) so both surfaces get the same button, with
          // the same test id and the same accessible name.
          <button
            key="close"
            type="button"
            className="clientv2-facts-x"
            data-testid="drawer-facts-close"
            aria-label={intl.formatMessage({ id: 'Close' })}
            onClick={closeFacts}
          >
            <X size={16} aria-hidden="true" />
          </button>
        ]}
        content={
          <>
            {/* Why this is worth a minute, and it moves as answers land.
                `role="status"` so the countdown is spoken too — it is the only
                feedback a tap gives, since nothing else on the card changes
                shape.

                It promises INSIGHT, not a ranking, because that is both the
                better motivation and the more accurate one: these exact fields
                are the payload three AI features send about the client —
                `client_personality`, `client_marriage_status`, `client_child`
                and `client_monthly_income` in ChatGPTPersonalAnalysis (吸客懶人包)
                and ChatGPTIceBreakingDrawerV2 (破冰話題), and `personality` in
                SmartIdeaDialog. An empty factsheet does not make those features
                unavailable; it makes them describe a stranger, which is worse,
                because the adviser reads the output and cannot tell.

                The C.P.I. band follows as a footnote. It is a real consequence
                and worth stating, but nobody enters data to move a number. */}
            {/* The questions scroll, the title and 關閉 do not — nine of them do
                not fit a phone, and a 關閉 that has scrolled off the bottom is a
                dialog with no visible way out. Both surfaces give that for free:
                MUI's DialogContent is the scrolling element above 600px, and the
                sheet's own Box is below it, with the header and footer pinned
                either side. */}

            {/* The 招募 card cannot borrow the sentence above it. 「AI 就是靠這幾項
              認識客戶」 is true of 認識客戶 — those five fields are the literal
              payload three AI features send — and it is FALSE of these four: no
              recruitment feature reads the factsheet today. So this card sells
              what it can actually deliver, which is that the adviser walks into
              a career conversation knowing the answers. When 招募 IDEA lands and
              does read them, this is the line to change. */}
            <div className="clientv2-facts-why" data-testid="drawer-facts-why" role="status">
              {recruitView ? (
                <>
                  <p className="clientv2-facts-why-lead">
                    {recruitUnanswered.length === 0
                      ? intl.formatMessage({ id: 'clientv2-recruit-facts-done' })
                      : intl.formatMessage(
                          { id: 'clientv2-recruit-facts-remaining' },
                          { count: recruitUnanswered.length }
                        )}
                  </p>
                  <p className="clientv2-facts-why-rank">{intl.formatMessage({ id: 'clientv2-recruit-facts-why' })}</p>
                </>
              ) : (
                <>
                  <p className="clientv2-facts-why-lead">
                    {unansweredKnow.length === 0
                      ? intl.formatMessage({ id: 'clientv2-facts-done' })
                      : intl.formatMessage({ id: 'clientv2-facts-remaining' }, { count: unansweredKnow.length })}
                  </p>
                  {cpiGap.plan.length > 0 && (
                    <p className="clientv2-facts-why-rank">{intl.formatMessage({ id: 'clientv2-facts-rank' })}</p>
                  )}
                </>
              )}
            </div>

            {/* 分類客戶 — where this client sits in the book rather than who they
                are. These drive the filters, the sort, the 招募 tab and the row
                badges; none of them reaches the AI.
                Never counted and never chased: 重要性「普通」 and 潛在招募「否」 are
                legitimate resting states for most of a book, so counting them
                would make the tile permanent — the standing complaint the tile
                is built to avoid. They are here because the adviser is already
                holding the card, not because anything is missing. */}
            <p className="clientv2-facts-section">
              {intl.formatMessage({
                id: recruitView ? 'clientv2-recruit-facts-section-sort' : 'clientv2-facts-section-sort'
              })}
            </p>
            {/* 招募路線 and 階段 below save to the factsheet's adviser notes; with no
                factsheet there is nowhere to save them, so they are disabled and
                this says why. */}
            {recruitView && clientId && !canSaveNotes && (
              <p className="clientv2-recruit-readonly-note" data-testid="recruit-notes-readonly">
                {intl.formatMessage({ id: 'clientv2-notes-no-factsheet' })}
              </p>
            )}

            {/* 現有客戶 and 重要性 keep their own write path (`onEditExisting` /
                `onEditTier`) rather than going through `commitFact`: their echo
                also feeds the two cells above and the badge beside the client's
                name, so re-routing them would mean teaching three more readouts
                about a second source of truth. One spec, `statusPicks`, still
                drives the cell and this row alike.
                Both are absent from the 招募 card: 現有客戶 asks whether the
                policy closed and 重要性 asks who to chase first, and neither
                says anything about whether this person would join. */}
            {(recruitView ? [] : statusPicks).map(pick => (
              <div
                key={pick.key}
                className={`clientv2-statusgroup${isFactFocused(pick.key) ? ' is-focus' : ''}`}
                data-testid="drawer-status-group"
                data-section="sort"
                data-field={pick.key}
                data-value={pick.value || ''}
              >
                {factIcon(pick.key)}
                <div className="clientv2-statusgroup-body">
                  <div className="clientv2-statusgroup-k">
                    {pick.label}
                    <span className="clientv2-facts-hint">
                      {intl.formatMessage({ id: `clientv2-facts-hint-${pick.key}` })}
                    </span>
                    {pick.value && (
                      <button
                        type="button"
                        className="clientv2-statusgroup-clear"
                        data-testid={`drawer-clear-${pick.key}`}
                        onClick={() => pick.commit(null)}
                      >
                        {intl.formatMessage({ id: 'Clear' })}
                      </button>
                    )}
                  </div>
                  {/* 現有客戶's 是 / 否 come back as ✓ / ✕; 重要性's three ranks
                    stay words, since no glyph means 普通 to anybody. */}
                  <FactToggle
                    optionTestId={pick.optionTestId}
                    // `pick.label`, not `formatMessage({ id: pick.label })`.
                    // `statusPicks` resolves its label when it builds the spec
                    // (`intl.formatMessage({ id: 'clientv2-existing' })`), so what
                    // arrives here is already 「現有客戶」 — translating it again
                    // asked react-intl for a message whose id is Chinese text.
                    // It fell back to the id, which happens to BE the right
                    // wording, so nothing was visibly wrong; it just logged two
                    // errors per render and would have started returning the id
                    // for real the first time either message value changed.
                    // The heading three lines up already renders `pick.label`
                    // straight, as do both readouts in the 2×2 grid.
                    ariaLabel={pick.label}
                    value={pick.value ?? null}
                    options={pick.choices.map(choice => factChoiceOption(choice, choice))}
                    onChange={next => pick.commit(next)}
                  />
                </div>
              </div>
            ))}

            {recruitView && (
              <div
                className="clientv2-statusgroup"
                data-testid="drawer-facts-group"
                data-section="sort"
                data-field="recruitRoute"
                data-value={recruitRoute || ''}
              >
                {factIcon('recruitRoute')}
                <div className="clientv2-statusgroup-body">
                  <div className="clientv2-statusgroup-k">
                    {intl.formatMessage({ id: 'clientv2-recruit-route' })}
                    <span className="clientv2-facts-hint">
                      {intl.formatMessage({ id: 'clientv2-facts-hint-recruitRoute' })}
                    </span>
                    {recruitRoute && (
                      <button
                        type="button"
                        className="clientv2-statusgroup-clear"
                        data-testid="drawer-facts-clear-recruitRoute"
                        disabled={!canSaveNotes}
                        onClick={() => writeRecruitIdeaPath(clientId, null)}
                      >
                        {intl.formatMessage({ id: 'Clear' })}
                      </button>
                    )}
                  </div>
                  <FactToggle
                    optionTestId="drawer-facts-option"
                    dataField="recruitRoute"
                    ariaLabel={intl.formatMessage({ id: 'clientv2-recruit-route' })}
                    value={recruitRoute}
                    options={(['graduate', 'experienced'] as RecruitIdeaPath[]).map(option => ({
                      value: option,
                      label: intl.formatMessage({ id: `clientv2-recruit-route-${option}` })
                    }))}
                    disabled={!canSaveNotes}
                    onChange={next => writeRecruitIdeaPath(clientId, next as RecruitIdeaPath | null)}
                  />
                </div>
              </div>
            )}

            {/* 階段 — the same question the header pill asks, in the place an
                adviser fills the rest of the card in. Two entry points rather
                than one because they are used at different moments: the pill
                mid-conversation, this one while going through the card before
                a meeting. `writeRecruitStage` broadcasts, so whichever is
                touched, the other follows without a reload.

                Chips rather than a FactToggle: six options on a 434px dialog
                give each segment ~60px, which breaks four-character stages
                over two lines. Wrapped chips also let each stage carry its
                dot, and the dots ARE the information — the same six colours
                the pill, its menu and the client list use. */}
            {recruitView && (
              <div
                className="clientv2-statusgroup"
                data-testid="drawer-facts-group"
                data-section="sort"
                data-field="recruitStage"
                data-value={recruitStage || ''}
              >
                {factIcon('recruitStage')}
                <div className="clientv2-statusgroup-body">
                  <div className="clientv2-statusgroup-k">
                    {intl.formatMessage({ id: 'clientv2-recruit-marker-stage' })}
                    <span className="clientv2-facts-hint">
                      {intl.formatMessage({ id: 'clientv2-facts-hint-recruitStage' })}
                    </span>
                    {recruitStage && (
                      <button
                        type="button"
                        className="clientv2-statusgroup-clear"
                        data-testid="drawer-facts-clear-recruitStage"
                        disabled={!canSaveNotes}
                        onClick={() => writeRecruitStage(clientId, null)}
                      >
                        {intl.formatMessage({ id: 'Clear' })}
                      </button>
                    )}
                  </div>
                  <div
                    className="clientv2-stagechips"
                    role="group"
                    aria-label={intl.formatMessage({ id: 'clientv2-recruit-marker-stage' })}
                  >
                    {RECRUIT_STAGES.map(option => (
                      <button
                        key={option}
                        type="button"
                        className="clientv2-stagechip"
                        data-testid="drawer-facts-option"
                        data-field="recruitStage"
                        data-stage={option}
                        aria-pressed={option === recruitStage}
                        disabled={!canSaveNotes}
                        // Picking the current stage again clears it, exactly as
                        // the header menu does — one behaviour, two surfaces.
                        onClick={() =>
                          writeRecruitStage(clientId, option === recruitStage ? null : (option as RecruitStage))
                        }
                      >
                        <RecruitStageDot stage={option} />
                        {intl.formatMessage({ id: `clientv2-recruit-stage-${option}` })}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {(recruitView ? RECRUIT_SEGMENT_CHOICES : SEGMENT_CHOICES).map(field => renderFactGroup(field, 'sort'))}

            {/* 認識客戶 — the half the AI reads. Named, because the card's two
                halves buy different things and an unlabelled run of eight
                questions would read as one long form. */}
            <p className="clientv2-facts-section">
              {intl.formatMessage({
                id: recruitView ? 'clientv2-recruit-facts-section-know' : 'clientv2-facts-section-know'
              })}
            </p>

            {/* DISC — two questions, not four letters. Leads the card because
                it is the cheapest thing on it: the adviser answers about a
                person they have met rather than classifying them.

                Withheld when the adviser has no letter of their own: the C.P.I.
                scores this axis only when BOTH are set (server utils/client.js),
                so offering it would promise a ranking for a tap that cannot pay
                — the one thing this card must never do.

                That withholding does NOT carry over to the 招募 card. It exists
                because a C.P.I. ranking is what the business card is selling,
                and the DISC axis cannot score without both letters. The 招募
                card sells no ranking: it asks how to open the conversation, and
                a candidate's own letter answers that whether or not the adviser
                has taken the test. */}
            {(adviserPersonality || recruitView) && (
              <div
                className={`clientv2-statusgroup${isFactFocused('personality') ? ' is-focus' : ''}`}
                data-testid="drawer-facts-group"
                data-section="know"
                data-field="personality"
                data-value={discLetter || ''}
              >
                {factIcon('personality')}
                <div className="clientv2-statusgroup-body">
                  <div className="clientv2-statusgroup-k">
                    {/* `clientv2-unlock-personality`, not a bare 'DISC' — that
                      id exists in no locale file, so react-intl logged a
                      missing-message error on every render and printed the id
                      back. It happened to read right in all three languages,
                      which is exactly why it survived; the key it should have
                      been using was already there, unused. */}
                    {intl.formatMessage({ id: 'clientv2-unlock-personality' })}
                    {/* What this one answer tells the adviser. Every question on
                      the card carries one: a form that only labels its fields
                      is asking the adviser to take on faith that any of it
                      matters, and the honest answer to 「點解要填?」 is different
                      for each field. */}
                    <span className="clientv2-facts-hint">
                      {intl.formatMessage({ id: 'clientv2-facts-hint-personality' })}
                    </span>
                    {/* The letter, once both halves are answered — the conclusion
                      the two taps reach, shown rather than asked for. */}
                    {discLetter && <span className="clientv2-facts-disc">{discLabel(discLetter, intl.locale)}</span>}
                    {(discLetter || shownTraits.some(Boolean)) && (
                      <button
                        type="button"
                        className="clientv2-statusgroup-clear"
                        data-testid="drawer-facts-clear-personality"
                        onClick={() => {
                          setDiscTraits([null, null]);
                          if (discLetter) commitFact('personality', null);
                        }}
                      >
                        {intl.formatMessage({ id: 'Clear' })}
                      </button>
                    )}
                  </div>
                  {DISC_AXES.map((axis, index) => (
                    <FactToggle
                      key={axis.key}
                      className="clientv2-facts-axis"
                      optionTestId="drawer-facts-trait"
                      dataAxis={axis.key}
                      ariaLabel={intl.formatMessage({ id: 'clientv2-facts-hint-personality' })}
                      value={shownTraits[index] ?? null}
                      options={axis.choices.map(choice => factChoiceOption(choice, choice))}
                      onChange={next => commitDiscTrait(index, next)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 現時月入 and 現職 — the two the 招募 card asks that no row of
              buttons can. Both write straight to the factsheet fields V1's own
              form owns, so an answer given here shows up there and vice versa;
              that is what the 共用 badge on each row is telling the adviser. */}
            {recruitView && (
              <>
                <FactTextGroup
                  fieldKey="monthlyIncome"
                  label={intl.formatMessage({ id: 'clientv2-recruit-income' })}
                  hint={intl.formatMessage({ id: 'clientv2-facts-hint-monthlyIncome' })}
                  clearLabel={intl.formatMessage({ id: 'Clear' })}
                  icon={factIcon('monthlyIncome')}
                  value={
                    factValueOf('monthlyIncome') === null || factValueOf('monthlyIncome') === undefined
                      ? ''
                      : String(factValueOf('monthlyIncome'))
                  }
                  numeric
                  focused={isFactFocused('monthlyIncome')}
                  disabled={!canEditProfile}
                  onCommit={next => commitFact('monthlyIncome', next)}
                />
                <FactTextGroup
                  fieldKey="occupation"
                  label={intl.formatMessage({ id: 'Occupation' })}
                  hint={intl.formatMessage({ id: 'clientv2-facts-hint-occupation' })}
                  clearLabel={intl.formatMessage({ id: 'Clear' })}
                  icon={factIcon('occupation')}
                  value={
                    factValueOf('occupation') === null || factValueOf('occupation') === undefined
                      ? ''
                      : String(factValueOf('occupation'))
                  }
                  focused={isFactFocused('occupation')}
                  disabled={!canEditProfile}
                  onCommit={next => commitFact('occupation', next)}
                />
              </>
            )}

            {/* 家庭狀況 combines 婚姻狀況 and 子女 in the panel readout, so both
              questions stay editable here. 性別 stays behind: it feeds AI copy
              but no recruitment feature currently reads it. */}
            {(recruitView
              ? FACT_CHOICES.filter(field => field.key === 'marriageStatus' || field.key === 'child')
              : FACT_CHOICES
            ).map(field => renderFactGroup(field, 'know'))}

            {/* 生日, the one field that cannot be a tap — and the one worth most
                (1.8 of the 4.2, because the C.P.I.'s age axis is derived from
                it). The same picker V1's factsheet edits this field with
                (ClickableField, opened from factsheetDetailsFormTwo.js), on the
                same 年份優先 / 三十年前 settings, because a dob is reached by
                YEAR: a calendar opened on this month is thirty years of paging
                away from the answer.
                It replaced a native `<input type="date">`, which was the wrong
                control for a field that writes on every change. That input
                fires `change` per KEYSTROKE in the year segment, so typing 1981
                committed 0001, then 0019, then 0198, then 1981 — four writes,
                three of them nonsense, and the first one opened 智能 IDEA on a
                birthday in year 1 (reported from the app, 2026-08-27; the code
                here claimed the opposite in a comment). This field is read-only
                and answers on `onAccept`, so one chosen date is one write.

                Not on the 招募 card. What a birthday buys here is a greeting and
                a 智能 IDEA reading, both of which belong to the relationship the
                business card is about — and it is the most expensive answer on
                either card, which is a bad thing to put in front of an adviser
                who came to find out how to talk to somebody about a job. One
                tap up, on 業務, unchanged. */}
            {!recruitView && (
              <div
                // Outlined like every other row when the card is opened FOR it —
                // 重點跟進's 補資料 task asks for 生日 more than anything else.
                className={`clientv2-statusgroup${isFactFocused('dob') ? ' is-focus' : ''}`}
                data-testid="drawer-facts-group"
                data-section="know"
                data-field="dob"
                data-value={shownDob}
              >
                {factIcon('dob')}
                <div className="clientv2-statusgroup-body">
                  <div className="clientv2-statusgroup-k">
                    {intl.formatMessage({ id: 'clientv2-unlock-dob' })}
                    <span className="clientv2-facts-hint">{intl.formatMessage({ id: 'clientv2-facts-hint-dob' })}</span>
                    {shownDob && (
                      <button
                        type="button"
                        className="clientv2-statusgroup-clear"
                        data-testid="drawer-facts-clear-dob"
                        onClick={() => commitFact('dob', null)}
                      >
                        {intl.formatMessage({ id: 'Clear' })}
                      </button>
                    )}
                  </div>
                  <ClickableDatePicker
                    name="dob"
                    className="clientv2-facts-date"
                    // ISO on screen as well as on the wire, so the field shows the
                    // date in the shape it is stored and read in — the same string
                    // `shownDob` puts on `data-value` and `onAccept` sends. V1's
                    // editor of this field prints DD-MMM-YYYY and this followed it
                    // until 2026-09-02; the month abbreviation is English in all
                    // three locales, which is a third notation for an adviser who
                    // has just typed 1981 into a year list.
                    format="YYYY-MM-DD"
                    // UNCONTROLLED, keyed on the record — `defaultValue`, not
                    // `value`.
                    //
                    // It was `value` with no `onChange`, which is a controlled MUI
                    // picker whose owner never accepts a change: `usePickerValue`
                    // snapped its draft back to this prop on every render, so
                    // every year and every day the adviser picked was thrown away
                    // before the next one. `onAccept` then committed the date
                    // already on file. On a client with no dob that is `null` all
                    // the way through — pick a year, pick a day, press OK, and the
                    // field is still empty, which is exactly how it was reported
                    // from the Android app (2026-09-01). Reproduced on an emulated
                    // Android/touch device: the picker's own toolbar never left
                    // 「Thu, Sep 23」 while 1990 and the 15th were being tapped.
                    //
                    // Uncontrolled hands the draft to MUI, which is the only place
                    // it can live between views. The `key` is what keeps the field
                    // honest afterwards: the record changing — the write landing,
                    // an echo retracted after a failure, the adviser switching
                    // client — remounts the picker on the new date instead of
                    // leaving a stale draft standing.
                    //
                    // `parseZone`, for the reason `shownDob` uses it: the stored dob
                    // is UTC midnight, and re-reading it in the browser's zone puts
                    // the control a day behind the record west of UTC.
                    key={shownDob || 'dob-empty'}
                    defaultValue={shownDob ? moment.parseZone(shownDob) : null}
                    // Straight to the year list, on a reference thirty years back —
                    // V1's own dob settings (factsheetDetailsFormTwo.js). A picker
                    // that opens on a day grid for THIS month asks the adviser to
                    // page back three hundred and sixty times before the first
                    // useful screen.
                    openTo="year"
                    referenceDate={referenceNow.clone().subtract(30, 'years')}
                    // Nobody was born tomorrow, and a future dob is not merely
                    // wrong — `getAgeFromDob` floors the difference, so it stores a
                    // NEGATIVE age, which then scores as the youngest possible band
                    // on an axis meant to reward the 31–41 bracket. Cheap to prevent
                    // here; the picker simply will not offer those days. The floor
                    // is the picker's own 1900, which is what keeps a year-1
                    // birthday off the list at all.
                    maxDate={referenceNow}
                    onOpen={() => setDobPickerOpen(true)}
                    onClose={() => setDobPickerOpen(false)}
                    // `onAccept` is what WRITES, and still the only thing that
                    // does. Every field here writes the moment it is answered, and
                    // `onChange` fires once per VIEW — committing on it would send
                    // a half-chosen date to the server and spend the one-shot
                    // 生日解鎖 on it. `onAccept` fires once, on the finished date,
                    // and once more with null for the picker's own Clear, which
                    // means the same as the 清除 button above. The draft between
                    // those views is MUI's own now (see `defaultValue`), which is
                    // the part that used to be missing.
                    onAccept={(date: moment.Moment | null) =>
                      commitFact('dob', date && date.isValid() ? date.format('YYYY-MM-DD') : null)
                    }
                    // `field` rather than `textField`: ClickableField replaces the
                    // field slot outright (PlainFieldSlot), and that is what forwards
                    // `inputProps` onto the rendered input.
                    slotProps={{
                      // The picker is a MUI Dialog on mobile, nested inside this
                      // card's blocking react-spring-bottom-sheet. The sheet
                      // already owns body scroll locking. Letting MUI lock it a
                      // second time makes cleanup order observable: if the sheet
                      // closes first, MUI later restores the `overflow: hidden`
                      // value it saw on open and every body-scrolling page stays
                      // frozen. Keep one lock owner while retaining both focus
                      // traps and modal semantics.
                      dialog: { disableScrollLock: true },
                      field: { inputProps: { 'data-testid': 'drawer-facts-dob' } }
                    }}
                  />
                  {/* 生日解鎖, as an offer under the field it was earned by.
                      This is where the reward used to arrive by itself, closing
                      the card mid-errand; here it waits, and the adviser can
                      answer the other three questions first and take it on the
                      way out. Rendered only while there is a payout in hand, so
                      the row is its ordinary self on every later visit. */}
                  {birthdayUnlockOffer && (
                    <button
                      ref={unlockButtonRef}
                      type="button"
                      className="clientv2-facts-unlock"
                      data-testid="drawer-facts-dob-unlock"
                      onClick={() => openBirthdayUnlock(birthdayUnlockOffer)}
                    >
                      {/* 智能摘要's OWN glyph, the one on its tile in the action
                          row above (ACTION_TILES) — so the button names where it
                          goes rather than describing itself. The chevron is the
                          other half of that: without the pair, a tinted full-width
                          box directly under a white full-width input reads as a
                          second FIELD, which is what it looked like on first
                          inspection. */}
                      <Lightbulb size={14} aria-hidden="true" />
                      <span>{intl.formatMessage({ id: 'clientv2-facts-dob-unlocked' })}</span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 痛點・顧慮・優勢 只留在招募 IDEA,不在這裡重複一次。
                它們是對話用的查閱卡:選了什麼,是為了 IDEA 那幾句話怎麼講。放在
                快速資料裡,等於把三組共 29 顆晶片疊在一張本來只問幾個欄位的表
                後面 —— 捲很久、每一顆都要想,而想完之後要用它的地方還在別處。
                一件事一個家,這裡就是那個「別處」的相反。 */}
          </>
        }
      />

      {/* 招募對話：如何開口 — four lines chosen by the candidate's DISC letter.
          Mounted only while open: it is a lookup, not a panel, and the drawer
          should not carry a dialog for every client whether or not the adviser
          ever asks for it. */}
      <Dialog
        open={discPlaybookOpen}
        onClose={() => setDiscPlaybookOpen(false)}
        // The quick-facts bottom sheet already owns the mobile body lock. Keep
        // this nested playbook from capturing and later restoring `hidden`.
        disableScrollLock
        fullWidth
        maxWidth="xs"
        data-testid="drawer-disc-playbook"
        aria-labelledby="clientv2-recruit-disc-title"
      >
        <DialogTitle id="clientv2-recruit-disc-title" className="clientv2-facts-head">
          <span className="clientv2-facts-head-title">{intl.formatMessage({ id: 'clientv2-recruit-disc-title' })}</span>
          {discLetter && (
            <span className="clientv2-discplay-letter" style={{ backgroundColor: discColorOf(discLetter) }}>
              {discLabel(discLetter, intl.locale)}
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          {isDiscLetter(discLetter) ? (
            <DiscTips mode="recruit" letter={discLetter} intl={intl} showAsk testId="drawer-disc-playbook-rows" />
          ) : (
            <p className="clientv2-discplay-empty">{intl.formatMessage({ id: 'clientv2-recruit-disc-empty' })}</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" data-testid="drawer-disc-playbook-close" onClick={() => setDiscPlaybookOpen(false)}>
            {intl.formatMessage({ id: 'Close' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 招募 IDEA. Its 活動量計算 CTA is the one thing on the page that leaves
          it, and it goes through `onAction` like the tile of the same name — the
          dispatcher's status gate should not be skippable by reaching the
          calculator from inside a dialog. */}
      <RecruitIdeaDialog
        open={recruitIdeaOpen}
        onClose={() => setRecruitIdeaOpen(false)}
        intl={intl}
        clientId={clientId}
        candidateName={displayName(activeClient)}
        // The same `discLetter` the header readout and the inline playbook use,
        // so all three say one thing about one person.
        discLetter={discLetter}
        // The same birthday fact Smart IDEA reads for 星座 / 生日號數 / 靈魂數.
        dob={(shownDobValue as string | null | undefined) || null}
        // And the same salary the 現時月入 cell above prints, for the same
        // reason — the dialog opens on top of that cell.
        monthlyIncome={recruitIncomeValue}
        // The 溝通方式 picker writes through the existing DISC fact flow, so
        // the header readout follows the tap immediately.
        // The local half-answer is reset too: `discLetter` reads it first, so a
        // stale one would keep showing the old letter over the saved one.
        onChangePersonality={changePersonality}
        onSetBirthday={guard('edit-profile', () => {
          setRecruitIdeaOpen(false);
          chooseView('business');
          openFacts('dob');
        })}
        // Picked in place from the card's 「先設定生日」, through the same write as
        // the 生日 field above. Only offered when that write is allowed; otherwise
        // the hint keeps `onSetBirthday`, whose guard explains the restriction.
        onPickBirthday={canEditProfile && onEditFact ? value => commitFact('dob', value) : undefined}
        onOpenCalculator={
          onAction
            ? guard('mdrt-calculator', () => {
                setRecruitIdeaOpen(false);
                onAction('mdrt-calculator', activeClient);
              })
            : undefined
        }
        onOpenQuiz={() => {
          setRecruitQuizPart(1);
          setRecruitQuizOpen(true);
        }}
        onOpenIncomeSimulator={
          onAction
            ? guard('income-simulator', () => {
                setRecruitIdeaOpen(false);
                onAction('income-simulator', activeClient);
              })
            : undefined
        }
      />

      <QuizChooserDialog
        open={quizChooserOpen}
        onClose={() => setQuizChooserOpen(false)}
        onPick={part => {
          setQuizChooserOpen(false);
          if (part === 'career1' || part === 'career2') {
            setRecruitQuizPart(part === 'career2' ? 2 : 1);
            setRecruitQuizOpen(true);
            return;
          }
          setCoverQuizPart(part === 'cover2' ? 2 : 1);
          setCoverQuizOpen(true);
        }}
        clientId={clientId || ''}
        clientName={factsheet?.name || displayName(activeClient)}
        intl={intl}
        mode={recruitView ? 'recruit' : 'business'}
      />

      <RecruitQuizDialog
        open={recruitQuizOpen}
        part={recruitQuizPart}
        onClose={() => setRecruitQuizOpen(false)}
        clientId={clientId || ''}
        candidateName={factsheet?.name || displayName(activeClient)}
        intl={intl}
        currentDisc={factValueOf('personality') as string | null}
        onChangePersonality={changePersonality}
        onOpenRecruitIdea={() => setRecruitIdeaOpen(true)}
      />

      <CoverQuizDialog
        open={coverQuizOpen}
        part={coverQuizPart}
        onClose={() => setCoverQuizOpen(false)}
        intl={intl}
        clientId={clientId || ''}
        clientName={factsheet?.name || displayName(activeClient)}
        currentDisc={factValueOf('personality') as string | null}
        onApplyDisc={
          canEditProfile
            ? letter =>
                guard('edit-profile', () => {
                  setDiscTraits(TRAITS_FROM_DISC[letter] || [null, null]);
                  commitFact('personality', letter);
                })()
            : undefined
        }
      />
    </aside>
  );
};

export default injectIntl(ClientDetailDrawer);
